import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, Platform, Alert, ActivityIndicator, TextInput, Modal } from 'react-native';
import { useTheme } from '../context/ThemeContext';
import { useNavigation } from '../context/NavigationContext';
import { adminService } from '../services/adminService';
import { resetUserProgress } from '../services/resetUserProgress';
import { glossaryService } from '../services/glossaryService';
import { persistentWidgetService } from '../services/persistentWidgetService';
import { starterGlossaryTerms } from '../data/starterGlossary';
import { emulatorService } from '../services/emulatorService';
import { StrategicDecisionMatrix, StrategicActionMatrix, TigerTeamBreakoutsTracker } from '../components/PersistentWidgets';
import { v12DiagnosticService } from '../services/v12DiagnosticService';
import { doc, getDoc, deleteDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../config/firebase';
import ToggleSwitch from '../components/ToggleSwitch';
import FormInput from '../components/FormInput';
import AdminSidebar from '../components/AdminSidebar';
import AnimatedButton from '../components/AnimatedButton';
import AnimatedCard from '../components/AnimatedCard';
import CheckboxRow from '../components/CheckboxRow';
import WidgetRenderer from '../components/WidgetRenderer';
import MarkdownRenderer from '../components/MarkdownRenderer';

const AdminScreen = () => {
  const { palette, mode } = useTheme();
  const { comingSoonEnabled, bannerEnabled, bannerStartedAt } = useNavigation();
  
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(true);
  
  // Course state
  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(true);
  const [editingCourse, setEditingCourse] = useState(null);
  const [showCourseForm, setShowCourseForm] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(null); // Track which chapter is uploading
  const [uploadProgress, setUploadProgress] = useState({}); // Track upload progress percentages
  
  // Widget state
  const [widgets, setWidgets] = useState([]);
  const [widgetsLoading, setWidgetsLoading] = useState(true);
  const [editingWidget, setEditingWidget] = useState(null);
  const [showWidgetForm, setShowWidgetForm] = useState(false);
  const [selectedWidgetType, setSelectedWidgetType] = useState('all'); // Filter state

  // Course Pacing State
  const [selectedCourseForPacing, setSelectedCourseForPacing] = useState(null);
  const [expandedChapterIndex, setExpandedChapterIndex] = useState(null); // Which chapter is expanded for editing
  const [tempRules, setTempRules] = useState({ unlock: null, deadline: null }); // Temporary rules while editing
  
  // Course Editor UI State
  const [expandedEditorChapters, setExpandedEditorChapters] = useState({}); // Track which chapters are expanded in editor
  const [expandedModules, setExpandedModules] = useState({}); // Track which modules are expanded for detailed editing
  
  // Glossary state
  const [glossaryTerms, setGlossaryTerms] = useState([]);
  const [glossaryLoading, setGlossaryLoading] = useState(true);
  const [editingTerm, setEditingTerm] = useState(null);
  const [showTermForm, setShowTermForm] = useState(false);
  
  // Persistent Widgets state
  const [persistentWidgets, setPersistentWidgets] = useState([]);
  const [persistentWidgetsLoading, setPersistentWidgetsLoading] = useState(true);
  const [editingWidgetType, setEditingWidgetType] = useState(null); // Which widget type is being edited in modal
  const [tempSchedule, setTempSchedule] = useState(null); // Temporary schedule while editing in modal
  const [tempStatus, setTempStatus] = useState(null); // Temporary status while editing in modal
  
  // Add user form
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserFirstName, setNewUserFirstName] = useState('');
  const [newUserLastName, setNewUserLastName] = useState('');
  const [newUserType, setNewUserType] = useState('user');
  const [newUserPassword, setNewUserPassword] = useState(''); // Optional - if empty, temp password will be generated
  
  // Maintenance state
  const [comingSoonToggle, setComingSoonToggle] = useState(comingSoonEnabled);
  const [bannerToggle, setBannerToggle] = useState(bannerEnabled);
  const [updatingMaintenance, setUpdatingMaintenance] = useState(false);
  
  // Site Management sub-tab state
  const [siteManagementSubTab, setSiteManagementSubTab] = useState('maintenance');
  
  // Independent web/mobile maintenance states
  const [webComingSoon, setWebComingSoon] = useState(false);
  const [webMaintenance, setWebMaintenance] = useState(false);
  const [mobileComingSoon, setMobileComingSoon] = useState(false);
  const [mobileMaintenance, setMobileMaintenance] = useState(false);
  
  // Emulator state
  const [isEmulatorConnected, setIsEmulatorConnected] = useState(false);
  const [emulatorLoading, setEmulatorLoading] = useState(false);
  const [emulatorAvailable, setEmulatorAvailable] = useState(false);

  // Email testing state
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [testEmailName, setTestEmailName] = useState('');
  const [testEmailLoading, setTestEmailLoading] = useState(false);
  const [progressFrequency, setProgressFrequency] = useState('weekly');

  // V12 Diagnostic state
  const [v12Statuses, setV12Statuses] = useState({});
  const [v12Loading, setV12Loading] = useState(false);
  const [selectedUserForV12, setSelectedUserForV12] = useState(null);
  const [v12UserHistory, setV12UserHistory] = useState([]);

  // Load users on mount and when tab changes
  useEffect(() => {
    if (activeTab === 'users') {
      loadUsers();
    } else if (activeTab === 'v12') {
      loadV12Data();
    } else if (activeTab === 'courses') {
      loadCourses();
      loadWidgets(); // Load widgets so they're available for course module assignment
    } else if (activeTab === 'pacing') {
      loadCourses(); // Load courses for pacing configuration
    } else if (activeTab === 'widgets') {
      loadWidgets();
      loadCourses(); // Load courses so we can show widget usage
    } else if (activeTab === 'persistent_widgets') {
      loadPersistentWidgets();
    } else if (activeTab === 'glossary') {
      loadGlossaryTerms();
    } else if (activeTab === 'site_management') {
      checkEmulatorStatus(); // Refresh emulator status for development sub-tab
      loadMaintenanceSettings(); // Load current maintenance settings
    }
  }, [activeTab]);

  // Sync maintenance toggles with context
  useEffect(() => {
    setComingSoonToggle(comingSoonEnabled);
    setBannerToggle(bannerEnabled);
  }, [comingSoonEnabled, bannerEnabled]);

  // Check emulator status on mount
  useEffect(() => {
    checkEmulatorStatus();
  }, []);

  const checkEmulatorStatus = async () => {
    const status = emulatorService.getConnectionStatus();
    setIsEmulatorConnected(status.isConnected);
    
    const available = await emulatorService.checkEmulatorAvailability();
    setEmulatorAvailable(available);
  };

  // Load maintenance settings from Firestore
  const loadMaintenanceSettings = async () => {
    console.log('[Maintenance] Loading settings...');
    try {
      const maintenanceRef = doc(db, 'site_maintenance', 'global');
      const snap = await getDoc(maintenanceRef);
      
      if (snap.exists()) {
        const data = snap.data();
        console.log('[Maintenance] Loaded data:', data);
        // Load independent web/mobile settings
        setWebComingSoon(!!data.webComingSoonEnabled);
        setWebMaintenance(!!data.webMaintenanceEnabled);
        setMobileComingSoon(!!data.mobileComingSoonEnabled);
        setMobileMaintenance(!!data.mobileMaintenanceEnabled);
        // Legacy settings (for backwards compatibility)
        setComingSoonToggle(!!data.comingSoonEnabled);
        setBannerToggle(!!data.maintenanceBannerEnabled);
      } else {
        console.log('[Maintenance] No maintenance document exists');
      }
    } catch (error) {
      console.error('[Maintenance] Error loading settings:', error);
    }
  };

  const handleEmulatorToggle = async () => {
    setEmulatorLoading(true);
    try {
      if (isEmulatorConnected) {
        // Disconnect from emulator (will trigger page reload)
        const result = await emulatorService.disconnectFromEmulators();
        if (!result.success) {
          Alert.alert('Error', result.error);
          setEmulatorLoading(false);
        }
        // If successful, page will reload automatically
      } else {
        // Connect to emulator (will trigger page reload)
        const result = await emulatorService.connectToEmulators();
        
        if (result.success) {
          // Page will reload automatically, no need for manual reload
        } else {
          Alert.alert('Error', result.error);
          setEmulatorLoading(false);
        }
      }
    } catch (error) {
      Alert.alert('Error', `Failed to toggle emulator: ${error.message}`);
    }
    setEmulatorLoading(false);
  };

  // Email testing functions
  const handleSendTestEmail = async (emailType, frequency = 'weekly') => {
    if (!testEmailAddress) {
      Alert.alert('Error', 'Please enter an email address');
      return;
    }

    setTestEmailLoading(true);
    try {
      // For test emails, always use production to send real emails
      // Even if emulator is connected for other functions
      const baseUrl = 'https://us-central1-twelve-month-millionaire.cloudfunctions.net';
      
      console.log('Email test debug:', {
        isEmulatorConnected,
        baseUrl: 'Production (forced for real emails)',
        emailType,
        testEmailAddress,
        testEmailName
      });
      
      const endpoint = emailType === 'welcome' 
        ? `${baseUrl}/testEmailFunction`
        : `${baseUrl}/testProgressReport`;

      const payload = emailType === 'welcome'
        ? {
            type: 'welcome',
            email: testEmailAddress,
            firstName: testEmailName || 'Test User'
          }
        : {
            email: testEmailAddress,
            firstName: testEmailName || 'Test User',
            frequency: frequency
          };

      console.log('Making request to:', endpoint, 'with payload:', payload);
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      console.log('Response status:', response.status, 'Response headers:', response.headers);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('HTTP Error:', response.status, errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      
      const result = await response.json();
      console.log('Response result:', result);

      if (result.success) {
        const emailTypeLabel = emailType === 'welcome' ? 'Welcome' : `${frequency.charAt(0).toUpperCase() + frequency.slice(1)} Progress`;
        Alert.alert(
          'Success', 
          `${emailTypeLabel} email sent successfully to ${testEmailAddress}\nMessage ID: ${result.messageId || 'N/A'}`,
          [{ text: 'OK' }]
        );
      } else {
        console.error('Email send failed:', result);
        Alert.alert('Error', `Failed to send email: ${result.error || result.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Email test error:', error);
      console.error('Error stack:', error.stack);
      Alert.alert('Error', `Failed to send test email: ${error.message}\nCheck console for full details`);
    }
    setTestEmailLoading(false);
  };

  // Test function to verify network connectivity
  const testNetworkConnection = async () => {
    try {
      console.log('Testing network connection...');
      const response = await fetch('https://us-central1-twelve-month-millionaire.cloudfunctions.net/testEmailConfig', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const result = await response.json();
      console.log('Network test result:', result);
      Alert.alert('Network Test', `Connection successful!\nSandbox mode: ${result.config?.sandboxMode}`);
    } catch (error) {
      console.error('Network test error:', error);
      Alert.alert('Network Test Failed', error.message);
    }
  };

  const loadUsers = async () => {
    setUsersLoading(true);
    const { users: fetchedUsers, error } = await adminService.getAllUsers();
    if (error) {
      Alert.alert('Error', `Failed to load users: ${error}`);
    } else {
      setUsers(fetchedUsers);
      
      // Also load V12 statuses for all users
      const { statuses } = await v12DiagnosticService.getAllUsersV12Status();
      setV12Statuses(statuses);
    }
    setUsersLoading(false);
  };

  // V12 Diagnostic Admin Functions
  const loadV12Data = async () => {
    setV12Loading(true);
    try {
      // Load users if not already loaded
      if (users.length === 0) {
        const { users: fetchedUsers } = await adminService.getAllUsers();
        setUsers(fetchedUsers);
      }
      
      // Load all V12 statuses
      const { statuses } = await v12DiagnosticService.getAllUsersV12Status();
      setV12Statuses(statuses);
    } catch (error) {
      Alert.alert('Error', `Failed to load V12 data: ${error.message}`);
    }
    setV12Loading(false);
  };

  const handleViewV12History = async (userId) => {
    setSelectedUserForV12(userId);
    try {
      const { history } = await v12DiagnosticService.getDiagnosticHistory(userId, 20);
      setV12UserHistory(history);
    } catch (error) {
      console.error('Error loading V12 history:', error);
      setV12UserHistory([]);
    }
  };

  const handleForceV12Diagnostic = async (userId) => {
    const confirmed = Platform.OS === 'web'
      ? window.confirm('Force trigger V12 diagnostic for this user? This will make the diagnostic show as due.')
      : await new Promise(resolve => {
        Alert.alert('Force V12 Diagnostic', 'This will make the diagnostic show as due for this user.', [
          { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
          { text: 'Force Trigger', onPress: () => resolve(true) },
        ]);
      });

    if (!confirmed) return;

    setLoading(true);
    try {
      await v12DiagnosticService.forceTriggerDiagnostic(userId);
      Alert.alert('Success', 'V12 diagnostic has been triggered for this user.');
      await loadV12Data();
    } catch (error) {
      Alert.alert('Error', `Failed to trigger diagnostic: ${error.message}`);
    }
    setLoading(false);
  };

  const handleResetV12Progress = async (userId) => {
    const confirmed = Platform.OS === 'web'
      ? window.confirm('Reset V12 progress for this user? This will delete all their V12 diagnostic history.')
      : await new Promise(resolve => {
        Alert.alert('Reset V12 Progress', 'This will delete all V12 diagnostic history for this user. This cannot be undone.', [
          { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
          { text: 'Reset', style: 'destructive', onPress: () => resolve(true) },
        ]);
      });

    if (!confirmed) return;

    setLoading(true);
    try {
      // Delete the V12 status document
      await deleteDoc(doc(db, 'v12_diagnostics', userId));
      
      Alert.alert('Success', 'V12 progress has been reset for this user.');
      await loadV12Data();
      setSelectedUserForV12(null);
      setV12UserHistory([]);
    } catch (error) {
      Alert.alert('Error', `Failed to reset V12 progress: ${error.message}`);
    }
    setLoading(false);
  };

  const loadCourses = async () => {
    setCoursesLoading(true);
    const { courses: fetchedCourses, error } = await adminService.getAllCourses();
    if (error) {
      Alert.alert('Error', `Failed to load courses: ${error}`);
    } else {
      setCourses(fetchedCourses);
    }
    setCoursesLoading(false);
  };

  const loadWidgets = async () => {
    setWidgetsLoading(true);
    const { widgets: fetchedWidgets, error } = await adminService.getAllWidgets();
    if (error) {
      Alert.alert('Error', `Failed to load widgets: ${error}`);
    } else {
      setWidgets(fetchedWidgets);
    }
    setWidgetsLoading(false);
  };

  const loadGlossaryTerms = async () => {
    setGlossaryLoading(true);
    const { terms, error } = await glossaryService.getAllTerms();
    if (error) {
      Alert.alert('Error', `Failed to load glossary terms: ${error}`);
    } else {
      setGlossaryTerms(terms);
    }
    setGlossaryLoading(false);
  };

  const loadPersistentWidgets = async () => {
    setPersistentWidgetsLoading(true);
    const { widgets, error } = await persistentWidgetService.getAllPersistentWidgets();
    if (error) {
      Alert.alert('Error', `Failed to load persistent widgets: ${error}`);
    } else {
      setPersistentWidgets(widgets);
    }
    setPersistentWidgetsLoading(false);
  };

  const handleInitializePersistentWidgets = async () => {
    // Define the three matrix widget types with default settings
    const defaultWidgets = [
      {
        type: 'strategic_decision',
        title: 'Strategic Decision',
        description: 'Evaluate decisions based on criteria and importance',
        status: 'Inactive',
        schedule: { type: 'weekly', dayOfWeek: 1, dayOfMonth: 1 },
      },
      {
        type: 'strategic_action',
        title: 'Strategic Action',
        description: 'Prioritize actions based on impact and effort',
        status: 'Inactive',
        schedule: { type: 'weekly', dayOfWeek: 1, dayOfMonth: 1 },
      },

    ];

    setLoading(true);
    let createdCount = 0;
    let skippedCount = 0;
    const errors = [];

    try {
      // Check existing widgets
      const { widgets: existingWidgets } = await persistentWidgetService.getAllPersistentWidgets();
      const existingTypes = existingWidgets.map(w => w.type);

      // Create widgets that don't exist
      for (const widgetData of defaultWidgets) {
        if (existingTypes.includes(widgetData.type)) {
          skippedCount++;
          continue;
        }

        const result = await persistentWidgetService.createPersistentWidget(widgetData);
        if (result.success) {
          createdCount++;
        } else {
          errors.push(`${widgetData.title}: ${result.error}`);
        }
      }

      // Reload widgets
      await loadPersistentWidgets();

      // Show results
      if (errors.length > 0) {
        Alert.alert(
          'Initialization Complete with Errors',
          `Created: ${createdCount}\nSkipped: ${skippedCount}\nErrors:\n${errors.join('\n')}`
        );
      } else if (createdCount > 0) {
        Alert.alert('Success', `Successfully initialized ${createdCount} persistent widget(s). ${skippedCount > 0 ? `${skippedCount} widget(s) already existed.` : ''}`);
      } else {
        Alert.alert('Info', 'All persistent widgets already exist. No new widgets were created.');
      }
    } catch (error) {
      console.error('Error initializing persistent widgets:', error);
      Alert.alert('Error', `Failed to initialize persistent widgets: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTerm = () => {
    setEditingTerm({
      term: '',
      definition: '',
      category: '',
      relatedTerms: [],
    });
    setShowTermForm(true);
  };

  const handleEditTerm = (term) => {
    setEditingTerm(term);
    setShowTermForm(true);
  };

  const handleSaveTerm = async () => {
    if (!editingTerm.term || !editingTerm.definition) {
      Alert.alert('Error', 'Please fill in term and definition');
      return;
    }

    setLoading(true);
    let result;

    if (editingTerm.id) {
      // Update existing term
      const { id, ...updates } = editingTerm;
      result = await glossaryService.updateTerm(id, updates);
    } else {
      // Create new term
      result = await glossaryService.createTerm(editingTerm);
    }

    setLoading(false);

    if (result.success) {
      Alert.alert('Success', editingTerm.id ? 'Term updated' : 'Term created');
      setShowTermForm(false);
      setEditingTerm(null);
      await loadGlossaryTerms();
    } else {
      Alert.alert('Error', result.error || 'Failed to save term');
    }
  };

  const handleDeleteTerm = async (termId) => {
    const confirmed = Platform.OS === 'web'
      ? window.confirm('Delete this term? This cannot be undone.')
      : await new Promise(resolve => {
        Alert.alert('Confirm Delete', 'Delete this term? This cannot be undone.', [
          { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
          { text: 'Delete', style: 'destructive', onPress: () => resolve(true) },
        ]);
      });

    if (!confirmed) return;

    setLoading(true);
    const result = await glossaryService.deleteTerm(termId);
    setLoading(false);

    if (result.success) {
      Alert.alert('Success', 'Term deleted');
      await loadGlossaryTerms();
    } else {
      Alert.alert('Error', result.error || 'Failed to delete term');
    }
  };

  const handleSeedStarterTerms = async () => {
    const confirmed = Platform.OS === 'web'
      ? window.confirm(`This will add ${starterGlossaryTerms.length} business terms to your glossary. Continue?`)
      : await new Promise(resolve => {
        Alert.alert(
          'Seed Starter Terms',
          `This will add ${starterGlossaryTerms.length} business terms to your glossary. Continue?`,
          [
            { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
            { text: 'Add Terms', onPress: () => resolve(true) },
          ]
        );
      });

    if (!confirmed) return;

    setLoading(true);
    let successCount = 0;
    let errorCount = 0;

    for (const term of starterGlossaryTerms) {
      const result = await glossaryService.createTerm(term);
      if (result.success) {
        successCount++;
      } else {
        errorCount++;
      }
    }

    setLoading(false);

    if (errorCount === 0) {
      Alert.alert('Success', `Added ${successCount} business terms to your glossary!`);
    } else {
      Alert.alert(
        'Partially Complete',
        `Added ${successCount} terms successfully. ${errorCount} terms failed to add.`
      );
    }

    await loadGlossaryTerms();
  };

  const handleCreateWidget = () => {
    setEditingWidget({
      title: '',
      description: '',
      type: 'spreadsheet',
      config: {},
    });
    setShowWidgetForm(true);
  };

  const handleEditWidget = (widget) => {
    setEditingWidget({ ...widget });
    setShowWidgetForm(true);
  };

  const handleSaveWidget = async () => {
    if (!editingWidget.title || !editingWidget.type) {
      Alert.alert('Error', 'Please fill in widget title and select a type');
      return;
    }

    // Validate widget config based on type
    if (editingWidget.type === 'spreadsheet') {
      if (!editingWidget.config?.columns || editingWidget.config.columns.length === 0) {
        Alert.alert('Error', 'Please add at least one column to the spreadsheet');
        return;
      }
    } else if (editingWidget.type === 'quiz') {
      if (!editingWidget.config?.questions || editingWidget.config.questions.length === 0) {
        Alert.alert('Error', 'Please add at least one question to the quiz');
        return;
      }
    } else if (editingWidget.type === 'matching') {
      if (!editingWidget.config?.pairs || editingWidget.config.pairs.length === 0) {
        Alert.alert('Error', 'Please add at least one matching pair');
        return;
      }
      // Check if all pairs have both left and right items
      const incompletePairs = editingWidget.config.pairs.filter(p => !p.left || !p.right);
      if (incompletePairs.length > 0) {
        Alert.alert('Error', 'Please fill in both items for all matching pairs');
        return;
      }
    }

    setLoading(true);
    let result;

    if (editingWidget.id) {
      // Update existing widget
      const { id, ...updates } = editingWidget;
      result = await adminService.updateWidget(id, updates);
    } else {
      // Create new widget
      result = await adminService.createWidget(editingWidget);
    }

    setLoading(false);

    if (result.success) {
      Alert.alert('Success', editingWidget.id ? 'Widget updated' : 'Widget created');
      setShowWidgetForm(false);
      setEditingWidget(null);
      await loadWidgets();
    } else {
      Alert.alert('Error', result.error || 'Failed to save widget');
    }
  };

  const handleDeleteWidget = async (widgetId) => {
    const confirmed = Platform.OS === 'web'
      ? window.confirm('Delete this widget? This cannot be undone.')
      : await new Promise(resolve => {
        Alert.alert('Confirm Delete', 'Delete this widget? This cannot be undone.', [
          { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
          { text: 'Delete', style: 'destructive', onPress: () => resolve(true) },
        ]);
      });

    if (!confirmed) return;

    setLoading(true);
    const result = await adminService.deleteWidget(widgetId);
    setLoading(false);

    if (result.success) {
      Alert.alert('Success', 'Widget deleted');
      await loadWidgets();
    } else {
      Alert.alert('Error', result.error || 'Failed to delete widget');
    }
  };

  // Course Pacing Handlers
  const handleToggleChapterEdit = (chapter, chapterIndex) => {
    if (expandedChapterIndex === chapterIndex) {
      // Collapse
      setExpandedChapterIndex(null);
      setTempRules({ unlock: null, deadline: null });
    } else {
      // Expand and load rules
      const existingUnlock = chapter.unlockRule || { type: chapterIndex === 0 ? 'immediate' : 'days', days: chapterIndex * 7 };
      const existingDeadline = chapter.deadlineRule || { type: 'days', days: 14 };
      
      setExpandedChapterIndex(chapterIndex);
      setTempRules({
        unlock: existingUnlock,
        deadline: existingDeadline,
      });
    }
  };

  const handleSaveChapterRules = async (chapterIndex) => {
    if (!selectedCourseForPacing || !tempRules.unlock || !tempRules.deadline) return;

    setLoading(true);
    
    // Update the chapter with new rules
    const updatedCourse = { ...selectedCourseForPacing };
    updatedCourse.chapters[chapterIndex] = {
      ...updatedCourse.chapters[chapterIndex],
      unlockRule: tempRules.unlock,
      deadlineRule: tempRules.deadline,
    };

    // Save to Firestore
    const result = await adminService.updateCourse(selectedCourseForPacing.id, {
      chapters: updatedCourse.chapters,
    });

    setLoading(false);

    if (result.success) {
      Alert.alert('Success', 'Pacing rules updated');
      setExpandedChapterIndex(null);
      setTempRules({ unlock: null, deadline: null });
      // Refresh courses
      await loadCourses();
      // Update selected course
      const refreshedCourse = courses.find(c => c.id === selectedCourseForPacing.id);
      if (refreshedCourse) {
        setSelectedCourseForPacing(refreshedCourse);
      }
    } else {
      Alert.alert('Error', result.error || 'Failed to save rules');
    }
  };

  // Calculate where a widget is being used
  const getWidgetUsage = (widgetId) => {
    const usage = [];
    
    courses.forEach(course => {
      if (course.chapters && Array.isArray(course.chapters)) {
        course.chapters.forEach((chapter, chapterIndex) => {
          if (chapter.modules && Array.isArray(chapter.modules)) {
            chapter.modules.forEach((module, moduleIndex) => {
              if (module.widgets && Array.isArray(module.widgets)) {
                module.widgets.forEach(widget => {
                  if (widget.widgetId === widgetId) {
                    usage.push({
                      courseId: course.id,
                      courseTitle: course.title,
                      chapterTitle: chapter.title,
                      moduleTitle: module.title,
                      placement: widget.placement || 'sidebar',
                    });
                  }
                });
              }
            });
          }
        });
      }
    });

    return usage;
  };

  const handleCreateCourse = () => {
    setEditingCourse({
      title: '',
      description: '',
      status: 'Coming Soon',
      order: courses.length + 1,
      thumbnailUrl: '',
      chapters: [],
    });
    setShowCourseForm(true);
  };

  const handleEditCourse = (course) => {
    setEditingCourse({ ...course });
    setShowCourseForm(true);
  };

  const handleSaveCourse = async () => {
    if (!editingCourse.title || !editingCourse.description) {
      Alert.alert('Error', 'Please fill in all required fields');
      return;
    }

    setLoading(true);
    let result;
    
    if (editingCourse.id) {
      // Update existing course
      const { id, ...updates } = editingCourse;
      result = await adminService.updateCourse(id, updates);
    } else {
      // Create new course
      result = await adminService.createCourse(editingCourse);
    }

    setLoading(false);

    if (result.success) {
      Alert.alert('Success', editingCourse.id ? 'Course updated' : 'Course created');
      setShowCourseForm(false);
      setEditingCourse(null);
      await loadCourses();
    } else {
      Alert.alert('Error', result.error || 'Failed to save course');
    }
  };

  const handleDeleteCourse = async (courseId) => {
    const confirmed = Platform.OS === 'web'
      ? window.confirm('Delete this course? This cannot be undone.')
      : await new Promise(resolve => {
        Alert.alert('Confirm Delete', 'Delete this course? This cannot be undone.', [
          { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
          { text: 'Delete', style: 'destructive', onPress: () => resolve(true) },
        ]);
      });

    if (!confirmed) return;

    setLoading(true);
    const result = await adminService.deleteCourse(courseId);
    setLoading(false);

    if (result.success) {
      Alert.alert('Success', 'Course deleted');
      await loadCourses();
    } else {
      Alert.alert('Error', result.error || 'Failed to delete course');
    }
  };

  const handleAddChapter = () => {
    if (!editingCourse) return;
    
    const newChapter = {
      id: Date.now().toString(),
      title: '',
      description: '',
      order: editingCourse.chapters.length + 1,
      status: 'Available', // Available or Coming Soon
      modules: [], // Chapters now contain modules
    };

    setEditingCourse({
      ...editingCourse,
      chapters: [...editingCourse.chapters, newChapter],
    });
  };

  const handleAddModule = (chapterId) => {
    if (!editingCourse) return;
    
    const newModule = {
      id: Date.now().toString(),
      title: '',
      description: '',
      videoUrl: '',
      imageUrl: '',
      order: 0,
      required: false, // Whether module must be completed to progress
      widgets: [], // Array of widget configurations: { widgetId, placement: 'above'|'below' }
    };

    setEditingCourse({
      ...editingCourse,
      chapters: editingCourse.chapters.map(chapter => {
        if (chapter.id === chapterId) {
          const modules = chapter.modules || [];
          newModule.order = modules.length + 1;
          return { ...chapter, modules: [...modules, newModule] };
        }
        return chapter;
      }),
    });
  };

  const handleUpdateModule = (chapterId, moduleId, field, value) => {
    if (!editingCourse) return;

    setEditingCourse({
      ...editingCourse,
      chapters: editingCourse.chapters.map(chapter => {
        if (chapter.id === chapterId) {
          return {
            ...chapter,
            modules: (chapter.modules || []).map(module =>
              module.id === moduleId ? { ...module, [field]: value } : module
            ),
          };
        }
        return chapter;
      }),
    });
  };

  const handleDeleteModule = (chapterId, moduleId) => {
    if (!editingCourse) return;

    setEditingCourse({
      ...editingCourse,
      chapters: editingCourse.chapters.map(chapter => {
        if (chapter.id === chapterId) {
          return {
            ...chapter,
            modules: (chapter.modules || []).filter(module => module.id !== moduleId),
          };
        }
        return chapter;
      }),
    });
  };

  const handleUpdateChapter = (chapterId, field, value) => {
    if (!editingCourse) return;

    setEditingCourse({
      ...editingCourse,
      chapters: editingCourse.chapters.map(chapter =>
        chapter.id === chapterId ? { ...chapter, [field]: value } : chapter
      ),
    });
  };

  const handleDeleteChapter = (chapterId) => {
    if (!editingCourse) return;

    setEditingCourse({
      ...editingCourse,
      chapters: editingCourse.chapters.filter(chapter => chapter.id !== chapterId),
    });
  };

  // Reorder chapter up or down
  const handleReorderChapter = (chapterIndex, direction) => {
    if (!editingCourse) return;
    const newIndex = direction === 'up' ? chapterIndex - 1 : chapterIndex + 1;
    if (newIndex < 0 || newIndex >= editingCourse.chapters.length) return;
    
    const newChapters = [...editingCourse.chapters];
    const [moved] = newChapters.splice(chapterIndex, 1);
    newChapters.splice(newIndex, 0, moved);
    
    // Update order numbers
    newChapters.forEach((ch, idx) => { ch.order = idx + 1; });
    
    setEditingCourse({ ...editingCourse, chapters: newChapters });
  };

  // Reorder module within a chapter
  const handleReorderModule = (chapterId, moduleIndex, direction) => {
    if (!editingCourse) return;
    
    setEditingCourse({
      ...editingCourse,
      chapters: editingCourse.chapters.map(chapter => {
        if (chapter.id !== chapterId) return chapter;
        
        const modules = [...(chapter.modules || [])];
        const newIndex = direction === 'up' ? moduleIndex - 1 : moduleIndex + 1;
        if (newIndex < 0 || newIndex >= modules.length) return chapter;
        
        const [moved] = modules.splice(moduleIndex, 1);
        modules.splice(newIndex, 0, moved);
        
        // Update order numbers
        modules.forEach((m, idx) => { m.order = idx + 1; });
        
        return { ...chapter, modules };
      }),
    });
  };

  const handleImageUpload = async (event, type, chapterId = null, moduleId = null) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      Alert.alert('Error', 'Please select an image file');
      return;
    }

    setLoading(true);
    const fileName = `${Date.now()}_${file.name}`;
    const courseId = editingCourse?.id || 'temp';
    
    const result = await adminService.uploadCourseImage(file, courseId, fileName);
    setLoading(false);

    if (result.success) {
      if (type === 'thumbnail') {
        setEditingCourse({ ...editingCourse, thumbnailUrl: result.url });
      } else if (type === 'module' && chapterId && moduleId) {
        handleUpdateModule(chapterId, moduleId, 'imageUrl', result.url);
      }
    } else {
      Alert.alert('Error', result.error || 'Failed to upload image');
    }
  };

  const handleVideoUpload = async (event, chapterId, moduleId) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('video/')) {
      Alert.alert('Error', 'Please select a video file');
      return;
    }

    // Check file size (max 2GB - Firebase allows up to 5GB but 2GB is more reasonable)
    const maxSize = 2 * 1024 * 1024 * 1024; // 2GB in bytes
    const fileSizeGB = (file.size / (1024 * 1024 * 1024)).toFixed(2);
    
    if (file.size > maxSize) {
      Alert.alert('Error', `Video file is ${fileSizeGB}GB. Maximum allowed is 2GB.\n\nPlease compress your video or use a video hosting service (YouTube, Vimeo) and paste the URL instead.`);
      return;
    }

    const uploadKey = `${chapterId}-${moduleId}`;
    setUploadingVideo(uploadKey);
    setUploadProgress({ ...uploadProgress, [uploadKey]: 0 });
    
    const fileName = `${Date.now()}_${file.name}`;
    const courseId = editingCourse?.id || 'temp';
    
    // Progress callback
    const onProgress = (progress, bytesTransferred, totalBytes) => {
      const progressMB = (bytesTransferred / (1024 * 1024)).toFixed(1);
      const totalMB = (totalBytes / (1024 * 1024)).toFixed(1);
      console.log(`Upload progress: ${progress.toFixed(1)}% (${progressMB}MB / ${totalMB}MB)`);
      setUploadProgress({ ...uploadProgress, [uploadKey]: progress });
    };
    
    const result = await adminService.uploadCourseVideo(file, courseId, `${chapterId}-${moduleId}`, fileName, onProgress);
    
    setUploadingVideo(null);
    setUploadProgress({ ...uploadProgress, [uploadKey]: undefined });

    if (result.success) {
      console.log('Video uploaded! URL:', result.url);
      
      // Update module in state
      handleUpdateModule(chapterId, moduleId, 'videoUrl', result.url);
      
      // IMPORTANT: Save to Firestore immediately if course already exists
      if (editingCourse?.id) {
        console.log('Saving course to Firestore with new video URL...');
        const { id, ...updates } = editingCourse;
        
        // Find and update the specific module
        const updatedChapters = editingCourse.chapters.map(ch => {
          if (ch.id === chapterId) {
            return {
              ...ch,
              modules: ch.modules.map(mod => {
                if (mod.id === moduleId) {
                  return { ...mod, videoUrl: result.url };
                }
                return mod;
              })
            };
          }
          return ch;
        });
        
        const saveResult = await adminService.updateCourse(id, { ...updates, chapters: updatedChapters });
        
        if (saveResult.success) {
          Alert.alert('Success', 'Video uploaded and saved!');
        } else {
          Alert.alert('Warning', 'Video uploaded but failed to save to course. Please click "Save Course" button.');
        }
      } else {
        Alert.alert('Success', 'Video uploaded! Remember to save the course.');
      }
    } else {
      Alert.alert('Upload Failed', result.error || 'Failed to upload video');
    }
  };

  const handleUserAction = async (userId, action, email = '') => {
    if (loading) return;
    
    let confirmMessage = '';
    let actionFn = null;

    switch (action) {
            case 'resetProgress':
              confirmMessage = 'Reset all progress and intake status for this user? This will trigger the intake form to show again. This cannot be undone.';
              actionFn = async () => await resetUserProgress(userId);
              break;
      case 'grant':
        confirmMessage = 'Grant access to this user?';
        actionFn = () => adminService.grantAccess(userId);
        break;
      case 'suspend':
        confirmMessage = 'Suspend access for this user?';
        actionFn = () => adminService.suspendUser(userId);
        break;
      case 'admin':
        confirmMessage = 'Set this user as admin?';
        actionFn = () => adminService.setAsAdmin(userId);
        break;
      case 'user':
        confirmMessage = 'Set this user as regular user?';
        actionFn = () => adminService.setAsUser(userId);
        break;
      case 'delete':
        confirmMessage = 'Permanently delete this user account? This cannot be undone.';
        actionFn = () => adminService.deleteUser(userId);
        break;
      case 'resetPassword':
        confirmMessage = `Send password reset email to ${email}?`;
        actionFn = () => adminService.resetUserPassword(email);
        break;
      default:
        return;
    }

    const confirmed = Platform.OS === 'web'
      ? window.confirm(confirmMessage)
      : await new Promise(resolve => {
        Alert.alert('Confirm Action', confirmMessage, [
          { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
          { text: 'Confirm', style: 'destructive', onPress: () => resolve(true) },
        ]);
      });

    if (!confirmed) return;

    setLoading(true);
    const result = await actionFn();
    setLoading(false);

    if (result.success) {
      if (Platform.OS === 'web') {
        window.dispatchEvent(new CustomEvent('toast', {
          detail: { type: 'success', text: 'Action completed successfully' }
        }));
      }
      await loadUsers();
    } else {
      Alert.alert('Error', result.error || 'Failed to perform action');
    }
  };

  const handleAddUser = async () => {
    if (!newUserEmail || !newUserFirstName || !newUserLastName) {
      Alert.alert('Error', 'Please fill in all required fields (Email, First Name, Last Name)');
      return;
    }

    // Validate password if provided (minimum 6 characters for Firebase)
    if (newUserPassword && newUserPassword.length < 6) {
      Alert.alert('Error', 'Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    // Pass password only if provided, otherwise null (cloud function will generate temp password)
    const passwordToSend = newUserPassword.trim() || null;
    const result = await adminService.createUser(newUserEmail, newUserFirstName, newUserLastName, newUserType, passwordToSend);
    setLoading(false);

    if (result.success) {
      // Show appropriate success message
      if (result.requiresPasswordReset && result.tempPassword) {
        Alert.alert(
          'User Created Successfully',
          `A temporary password has been generated:\n\n${result.tempPassword}\n\nPlease share this with the user. They should reset their password upon first login.`,
          [{ text: 'OK', onPress: () => {} }]
        );
      } else {
        Alert.alert('Success', 'User created successfully with the specified password.');
      }
      // Clear form
      setNewUserEmail('');
      setNewUserFirstName('');
      setNewUserLastName('');
      setNewUserType('user');
      setNewUserPassword('');
      await loadUsers();
    } else {
      Alert.alert('Error', result.error || 'Failed to create user');
    }
  };

  // Get confirmation message for maintenance toggle
  const getMaintenanceConfirmMessage = (setting, value) => {
    const settingLabels = {
      webComingSoon: 'Web App Coming Soon page',
      webMaintenance: 'Web App Maintenance mode',
      mobileComingSoon: 'Mobile App Coming Soon page',
      mobileMaintenance: 'Mobile App Maintenance mode',
      comingSoon: 'Global Coming Soon (Web + Mobile)',
      globalMaintenance: 'Global Maintenance Mode (Web + Mobile)',
      banner: 'Maintenance Banner',
    };
    
    const label = settingLabels[setting] || setting;
    const isGlobal = setting === 'comingSoon' || setting === 'globalMaintenance';
    
    if (value) {
      if (isGlobal) {
        return `Are you sure you want to ENABLE ${label}?\n\n⚠️ This will immediately affect ALL non-admin users on BOTH Web and Mobile platforms.`;
      }
      return `Are you sure you want to ENABLE ${label}?\n\nThis will immediately affect all non-admin users on this platform.`;
    } else {
      if (isGlobal) {
        return `Are you sure you want to DISABLE ${label}?\n\nThis will immediately allow users to access BOTH platforms again.`;
      }
      return `Are you sure you want to DISABLE ${label}?\n\nThis will immediately allow users to access the platform again.`;
    }
  };

  const handleMaintenanceToggle = async (setting, value) => {
    // Show confirmation dialog
    const confirmMessage = getMaintenanceConfirmMessage(setting, value);
    
    if (Platform.OS === 'web') {
      if (!window.confirm(confirmMessage)) {
        return;
      }
    } else {
      // For native, use Alert.alert with callbacks
      return new Promise((resolve) => {
        Alert.alert(
          value ? 'Enable Maintenance' : 'Disable Maintenance',
          confirmMessage,
          [
            {
              text: 'Cancel',
              style: 'cancel',
              onPress: () => resolve(false),
            },
            {
              text: value ? 'Enable' : 'Disable',
              style: value ? 'destructive' : 'default',
              onPress: async () => {
                await executeMaintenanceToggle(setting, value);
                resolve(true);
              },
            },
          ]
        );
      });
    }
    
    // Execute the toggle (for web after confirmation)
    await executeMaintenanceToggle(setting, value);
  };

  const executeMaintenanceToggle = async (setting, value) => {
    console.log('[Maintenance Toggle] Executing:', setting, value);
    setUpdatingMaintenance(true);
    
    let updates = {};
    
    // Handle new independent web/mobile settings
    // Smart toggling: 
    // - Global toggles affect both web and mobile
    // - Turning OFF individual toggles also turns OFF global (so screens dismiss properly)
    switch (setting) {
      case 'webComingSoon':
        updates.webComingSoonEnabled = value;
        // When turning OFF, also turn off global legacy toggle
        if (!value) {
          updates.comingSoonEnabled = false;
        }
        break;
      case 'webMaintenance':
        updates.webMaintenanceEnabled = value;
        if (value) updates.webMaintenanceStartedAt = new Date();
        else updates.webMaintenanceStartedAt = null;
        break;
      case 'mobileComingSoon':
        updates.mobileComingSoonEnabled = value;
        // When turning OFF, also turn off global legacy toggle
        if (!value) {
          updates.comingSoonEnabled = false;
        }
        break;
      case 'mobileMaintenance':
        updates.mobileMaintenanceEnabled = value;
        if (value) updates.mobileMaintenanceStartedAt = new Date();
        else updates.mobileMaintenanceStartedAt = null;
        break;
      case 'comingSoon':
        // SMART TOGGLE: Global Coming Soon also enables/disables both web and mobile
        updates.comingSoonEnabled = value;
        updates.webComingSoonEnabled = value;
        updates.mobileComingSoonEnabled = value;
        break;
      case 'globalMaintenance':
        // SMART TOGGLE: Global Maintenance enables/disables both web and mobile
        updates.webMaintenanceEnabled = value;
        updates.mobileMaintenanceEnabled = value;
        if (value) {
          updates.webMaintenanceStartedAt = new Date();
          updates.mobileMaintenanceStartedAt = new Date();
        } else {
          updates.webMaintenanceStartedAt = null;
          updates.mobileMaintenanceStartedAt = null;
        }
        break;
      case 'banner':
        updates.maintenanceBannerEnabled = value;
        if (value && !bannerStartedAt) updates.maintenanceBannerStartedAt = new Date();
        else if (!value) updates.maintenanceBannerStartedAt = null;
        break;
      default:
        break;
    }

    console.log('[Maintenance Toggle] Updates to save:', updates);
    const result = await adminService.updateMaintenanceSettings(updates);
    console.log('[Maintenance Toggle] Result:', result);
    setUpdatingMaintenance(false);

    if (result.success) {
      // Update local state based on setting
      switch (setting) {
        case 'webComingSoon':
          setWebComingSoon(value);
          // Also update global toggle UI when turning off
          if (!value) setComingSoonToggle(false);
          break;
        case 'webMaintenance':
          setWebMaintenance(value);
          break;
        case 'mobileComingSoon':
          setMobileComingSoon(value);
          // Also update global toggle UI when turning off
          if (!value) setComingSoonToggle(false);
          break;
        case 'mobileMaintenance':
          setMobileMaintenance(value);
          break;
        case 'comingSoon':
          // Update all three states for global toggle
          setComingSoonToggle(value);
          setWebComingSoon(value);
          setMobileComingSoon(value);
          break;
        case 'globalMaintenance':
          setWebMaintenance(value);
          setMobileMaintenance(value);
          break;
        case 'banner':
          setBannerToggle(value);
          break;
        default:
          break;
      }
      
      // Show success feedback
      const action = value ? 'enabled' : 'disabled';
      if (Platform.OS === 'web') {
        // Brief visual feedback - the toggle state updates automatically
        console.log(`Maintenance setting ${setting} ${action} successfully`);
      } else {
        Alert.alert('Success', `Setting ${action} successfully. Changes are now live.`);
      }
    } else {
      Alert.alert('Error', result.error || 'Failed to update maintenance settings');
    }
  };

  const renderUsersTab = () => (
    <View style={styles.tabContent}>
      <Text style={[styles.tabTitle, { color: palette.text || 'white' }]}>User Management</Text>
      
      {/* Add User Section */}
      <View style={[styles.section, { borderTopColor: palette.border || 'rgba(255,255,255,0.16)' }]}>
        <Text style={[styles.sectionTitle, { color: palette.text || 'white' }]}>Add User</Text>
        <View style={styles.addUserForm}>
          <View style={styles.formRow}>
            <View style={styles.formCol}>
              <Text style={[styles.label, { color: palette.text || 'white' }]}>Email</Text>
              <View style={[styles.input, { borderColor: palette.border || 'white' }]}>
                <FormInput
                  placeholder="user@example.com"
                  value={newUserEmail}
                  onChangeText={setNewUserEmail}
                  keyboardType="email-address"
                  autoCapitalize="none"
                  style={{ backgroundColor: 'transparent', borderWidth: 0, color: palette.text || 'white' }}
                />
              </View>
            </View>
            <View style={styles.formCol}>
              <Text style={[styles.label, { color: palette.text || 'white' }]}>First Name</Text>
              <View style={[styles.input, { borderColor: palette.border || 'white' }]}>
                <FormInput
                  placeholder="First Name"
                  value={newUserFirstName}
                  onChangeText={setNewUserFirstName}
                  style={{ backgroundColor: 'transparent', borderWidth: 0, color: palette.text || 'white' }}
                />
              </View>
            </View>
            <View style={styles.formCol}>
              <Text style={[styles.label, { color: palette.text || 'white' }]}>Last Name</Text>
              <View style={[styles.input, { borderColor: palette.border || 'white' }]}>
                <FormInput
                  placeholder="Last Name"
                  value={newUserLastName}
                  onChangeText={setNewUserLastName}
                  style={{ backgroundColor: 'transparent', borderWidth: 0, color: palette.text || 'white' }}
                />
              </View>
            </View>
            <View style={styles.formCol}>
              <Text style={[styles.label, { color: palette.text || 'white' }]}>Role</Text>
              <View style={[styles.selectContainer, { borderColor: palette.border || 'white' }]}>
                <TouchableOpacity
                  style={[styles.selectOption, newUserType === 'user' && { backgroundColor: palette.primary || '#FF0600' }]}
                  onPress={() => setNewUserType('user')}
                >
                  <Text style={[styles.selectText, { color: newUserType === 'user' ? '#fff' : (palette.text || 'white') }]}>User</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.selectOption, newUserType === 'admin' && { backgroundColor: palette.primary || '#FF0600' }]}
                  onPress={() => setNewUserType('admin')}
                >
                  <Text style={[styles.selectText, { color: newUserType === 'admin' ? '#fff' : (palette.text || 'white') }]}>Admin</Text>
                </TouchableOpacity>
              </View>
            </View>
            <View style={styles.formCol}>
              <Text style={[styles.label, { color: palette.text || 'white' }]}>Password (Optional)</Text>
              <View style={[styles.input, { borderColor: palette.border || 'white' }]}>
                <FormInput
                  placeholder="Leave blank for temp password"
                  value={newUserPassword}
                  onChangeText={setNewUserPassword}
                  secureTextEntry
                  autoCapitalize="none"
                  style={{ backgroundColor: 'transparent', borderWidth: 0, color: palette.text || 'white' }}
                />
              </View>
              <Text style={[styles.helperText, { color: palette.mutedText || 'rgba(255,255,255,0.5)' }]}>
                If left empty, a temporary password will be generated
              </Text>
            </View>
          </View>
          <TouchableOpacity
            style={[styles.addButton, { backgroundColor: palette.primary || '#FF0600' }]}
            onPress={handleAddUser}
            disabled={loading}
          >
            <Text style={[styles.addButtonText, { color: '#fff' }]}>
              {loading ? 'Adding...' : 'Add User'}
            </Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Users List */}
      <View style={[styles.section, { borderTopColor: palette.border || 'rgba(255,255,255,0.16)' }]}>
        <Text style={[styles.sectionTitle, { color: palette.text || 'white' }]}>All Users ({users.length})</Text>
        {usersLoading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={palette.primary || '#FF0600'} />
            <Text style={[styles.loadingText, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>Loading users...</Text>
          </View>
        ) : users.length === 0 ? (
          <Text style={[styles.emptyText, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>No users found</Text>
        ) : (
          <ScrollView style={styles.usersList}>
            {users.map((user) => (
              <View key={user.id || user.uid} style={[styles.userCard, { borderColor: palette.border || 'rgba(255,255,255,0.16)', backgroundColor: palette.surface || 'transparent' }]}>
                <View style={styles.userInfo}>
                  <Text style={[styles.userName, { color: palette.text || 'white' }]}>
                    {user.firstName} {user.lastName} {user.userType === 'admin' && '👑'}
                  </Text>
                  <Text style={[styles.userEmail, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>{user.email}</Text>
                  <View style={styles.userMeta}>
                    <Text style={[styles.userMetaText, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>
                      Role: {user.userType || 'user'} | Status: {user.suspended ? '❌ Suspended' : '✅ Active'}
                    </Text>
                  </View>
                  
                  {/* Commitment Level & V12 Progress */}
                  <View style={styles.userExtendedMeta}>
                    {/* Commitment Level */}
                    <View style={[styles.commitmentBadge, { 
                      backgroundColor: user.commitmentLevel === 'all-in' 
                        ? 'rgba(16, 185, 129, 0.15)' 
                        : user.commitmentLevel === 'committed'
                          ? 'rgba(59, 130, 246, 0.15)'
                          : user.commitmentLevel === 'sort-of-committed'
                            ? 'rgba(245, 158, 11, 0.15)'
                            : 'rgba(255,255,255,0.05)',
                      borderColor: user.commitmentLevel === 'all-in'
                        ? '#10B981'
                        : user.commitmentLevel === 'committed'
                          ? '#3B82F6'
                          : user.commitmentLevel === 'sort-of-committed'
                            ? '#F59E0B'
                            : palette.border || 'rgba(255,255,255,0.2)',
                    }]}>
                      <Text style={[styles.commitmentText, { 
                        color: user.commitmentLevel === 'all-in'
                          ? '#10B981'
                          : user.commitmentLevel === 'committed'
                            ? '#3B82F6'
                            : user.commitmentLevel === 'sort-of-committed'
                              ? '#F59E0B'
                              : palette.mutedText || 'rgba(255,255,255,0.5)',
                      }]}>
                        {user.commitmentLevel === 'all-in' && '🎯 All In'}
                        {user.commitmentLevel === 'committed' && '💪 Committed'}
                        {user.commitmentLevel === 'sort-of-committed' && '⚠️ Sort of Committed'}
                        {!user.commitmentLevel && '❓ No Commitment Set'}
                      </Text>
                    </View>
                    
                    {/* V12 Progress */}
                    {v12Statuses[user.id || user.uid] && (
                      <View style={[styles.v12StatusBadge, { backgroundColor: 'rgba(212, 175, 55, 0.15)', borderColor: '#D4AF37' }]}>
                        <Text style={[styles.v12StatusText, { color: '#D4AF37' }]}>
                          V12: {v12DiagnosticService.getProgressSummary(v12Statuses[user.id || user.uid])}
                        </Text>
                      </View>
                    )}
                    {!v12Statuses[user.id || user.uid] && (
                      <View style={[styles.v12StatusBadge, { backgroundColor: 'rgba(255,255,255,0.05)', borderColor: palette.border || 'rgba(255,255,255,0.2)' }]}>
                        <Text style={[styles.v12StatusText, { color: palette.mutedText || 'rgba(255,255,255,0.5)' }]}>
                          V12: Not Started
                        </Text>
                      </View>
                    )}
                  </View>
                </View>
                <View style={styles.userActions}>
                  {user.suspended ? (
                    <TouchableOpacity
                      style={[styles.actionButton, styles.grantButton]}
                      onPress={() => handleUserAction(user.id || user.uid, 'grant')}
                      disabled={loading}
                    >
                      <Text style={[styles.actionButtonText, { color: palette.text || 'white' }]}>Grant Access</Text>
                    </TouchableOpacity>
                  ) : (
                    <TouchableOpacity
                      style={[styles.actionButton, styles.suspendButton]}
                      onPress={() => handleUserAction(user.id || user.uid, 'suspend')}
                      disabled={loading}
                    >
                      <Text style={[styles.actionButtonText, { color: palette.text || 'white' }]}>Suspend</Text>
                    </TouchableOpacity>
                  )}
                  {user.userType === 'admin' ? (
                    <TouchableOpacity
                      style={[styles.actionButton, styles.userButton]}
                      onPress={() => handleUserAction(user.id || user.uid, 'user')}
                      disabled={loading}
                    >
                      <Text style={[styles.actionButtonText, { color: palette.text || 'white' }]}>Set as User</Text>
                    </TouchableOpacity>
                  ) : (
                    <TouchableOpacity
                      style={[styles.actionButton, styles.adminButton]}
                      onPress={() => handleUserAction(user.id || user.uid, 'admin')}
                      disabled={loading}
                    >
                      <Text style={[styles.actionButtonText, { color: palette.text || 'white' }]}>Set as Admin</Text>
                    </TouchableOpacity>
                  )}
                  <TouchableOpacity
                    style={[styles.actionButton, styles.resetButton]}
                    onPress={() => handleUserAction(user.id || user.uid, 'resetPassword', user.email)}
                    disabled={loading}
                  >
                    <Text style={[styles.actionButtonText, { color: palette.text || 'white' }]}>Reset Password</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.actionButton, styles.resetButton]}
                    onPress={() => handleUserAction(user.id || user.uid, 'resetProgress')}
                    disabled={loading}
                  >
                    <Text style={[styles.actionButtonText, { color: palette.text || 'white' }]}>Reset & Test Intake</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.actionButton, styles.deleteButton]}
                    onPress={() => handleUserAction(user.id || user.uid, 'delete')}
                    disabled={loading}
                  >
                    <Text style={[styles.actionButtonText, styles.deleteButtonText]}>Delete</Text>
                  </TouchableOpacity>
                </View>
              </View>
            ))}
          </ScrollView>
        )}
      </View>
    </View>
  );

  // Art Deco gold color for V12
  const artDecoGold = '#D4AF37';

  const renderV12Tab = () => {
    const getUserById = (userId) => users.find(u => (u.id || u.uid) === userId);
    
    return (
      <View style={styles.tabContent}>
        <View style={styles.v12TabHeader}>
          <View style={[styles.v12HeaderBadge, { backgroundColor: artDecoGold }]}>
            <Text style={[styles.v12HeaderBadgeText, { color: '#1A1A1A' }]}>V12</Text>
          </View>
          <View>
            <Text style={[styles.tabTitle, { color: palette.text || 'white' }]}>Business Diagnostic Admin</Text>
            <Text style={[styles.subtitle, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>
              Monitor user progress through the V12 System
            </Text>
          </View>
        </View>

        {/* Overview Stats */}
        <View style={[styles.section, { borderTopColor: artDecoGold + '40' }]}>
          <Text style={[styles.sectionTitle, { color: artDecoGold }]}>Overview</Text>
          <View style={styles.v12StatsGrid}>
            <View style={[styles.v12StatCard, { borderColor: artDecoGold, backgroundColor: artDecoGold + '10' }]}>
              <Text style={[styles.v12StatNumber, { color: artDecoGold }]}>
                {Object.keys(v12Statuses).length}
              </Text>
              <Text style={[styles.v12StatLabel, { color: palette.text || 'white' }]}>
                Users Started
              </Text>
            </View>
            <View style={[styles.v12StatCard, { borderColor: artDecoGold, backgroundColor: artDecoGold + '10' }]}>
              <Text style={[styles.v12StatNumber, { color: artDecoGold }]}>
                {Object.values(v12Statuses).filter(s => s.latestCylindersComplete === 12).length}
              </Text>
              <Text style={[styles.v12StatLabel, { color: palette.text || 'white' }]}>
                All 12 Firing
              </Text>
            </View>
            <View style={[styles.v12StatCard, { borderColor: artDecoGold, backgroundColor: artDecoGold + '10' }]}>
              <Text style={[styles.v12StatNumber, { color: artDecoGold }]}>
                {Object.values(v12Statuses).reduce((sum, s) => sum + (s.totalDiagnosticsCompleted || 0), 0)}
              </Text>
              <Text style={[styles.v12StatLabel, { color: palette.text || 'white' }]}>
                Total Diagnostics
              </Text>
            </View>
          </View>
        </View>

        {/* User Progress List */}
        <View style={[styles.section, { borderTopColor: artDecoGold + '40' }]}>
          <Text style={[styles.sectionTitle, { color: artDecoGold }]}>User V12 Progress</Text>
          
          {v12Loading ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color={artDecoGold} />
              <Text style={[styles.loadingText, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>
                Loading V12 data...
              </Text>
            </View>
          ) : users.length === 0 ? (
            <Text style={[styles.emptyText, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>
              No users found
            </Text>
          ) : (
            <ScrollView style={styles.v12UserList}>
              {users.map((user) => {
                const userId = user.id || user.uid;
                const v12Status = v12Statuses[userId];
                const isSelected = selectedUserForV12 === userId;
                
                return (
                  <View key={userId}>
                    <TouchableOpacity
                      style={[
                        styles.v12UserCard,
                        { 
                          borderColor: isSelected ? artDecoGold : palette.border || 'rgba(255,255,255,0.16)',
                          backgroundColor: isSelected ? artDecoGold + '15' : palette.surface || 'transparent',
                        }
                      ]}
                      onPress={() => handleViewV12History(userId)}
                    >
                      <View style={styles.v12UserInfo}>
                        <Text style={[styles.v12UserName, { color: palette.text || 'white' }]}>
                          {user.firstName} {user.lastName}
                        </Text>
                        <Text style={[styles.v12UserEmail, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>
                          {user.email}
                        </Text>
                        
                        {/* Commitment Level */}
                        <View style={[styles.v12UserMeta]}>
                          <Text style={[styles.v12MetaLabel, { color: palette.mutedText }]}>
                            Commitment: {' '}
                            <Text style={{ 
                              color: user.commitmentLevel === 'all-in' ? '#10B981' 
                                : user.commitmentLevel === 'committed' ? '#3B82F6'
                                : user.commitmentLevel === 'sort-of-committed' ? '#F59E0B'
                                : palette.mutedText 
                            }}>
                              {user.commitmentLevel === 'all-in' && '🎯 All In'}
                              {user.commitmentLevel === 'committed' && '💪 Committed'}
                              {user.commitmentLevel === 'sort-of-committed' && '⚠️ Sort of'}
                              {!user.commitmentLevel && '❓ Not Set'}
                            </Text>
                          </Text>
                        </View>
                      </View>
                      
                      <View style={styles.v12UserProgress}>
                        {v12Status ? (
                          <>
                            <View style={[styles.v12CylinderCircle, { borderColor: artDecoGold }]}>
                              <Text style={[styles.v12CylinderCount, { color: artDecoGold }]}>
                                {v12Status.latestCylindersComplete || 0}
                              </Text>
                              <Text style={[styles.v12CylinderLabel, { color: palette.mutedText }]}>
                                /12
                              </Text>
                            </View>
                            <Text style={[styles.v12PhaseText, { color: palette.text || 'white' }]}>
                              {v12Status.currentMonth >= 3 
                                ? `Month ${v12Status.currentMonth}` 
                                : v12Status.currentWeek > 0 
                                  ? `Week ${v12Status.currentWeek}`
                                  : 'Started'
                              }
                            </Text>
                            <Text style={[styles.v12DiagnosticsCount, { color: palette.mutedText }]}>
                              {v12Status.totalDiagnosticsCompleted || 0} diagnostics
                            </Text>
                          </>
                        ) : (
                          <Text style={[styles.v12NotStarted, { color: palette.mutedText }]}>
                            Not Started
                          </Text>
                        )}
                      </View>
                    </TouchableOpacity>
                    
                    {/* Expanded User Details */}
                    {isSelected && (
                      <View style={[styles.v12UserDetails, { backgroundColor: artDecoGold + '08', borderColor: artDecoGold + '30' }]}>
                        {/* Action Buttons */}
                        <View style={styles.v12ActionButtons}>
                          <TouchableOpacity
                            style={[styles.v12ActionBtn, { backgroundColor: artDecoGold }]}
                            onPress={() => handleForceV12Diagnostic(userId)}
                            disabled={loading}
                          >
                            <Text style={[styles.v12ActionBtnText, { color: '#1A1A1A' }]}>
                              ⚡ Force Trigger Diagnostic
                            </Text>
                          </TouchableOpacity>
                          
                          <TouchableOpacity
                            style={[styles.v12ActionBtn, { backgroundColor: '#EA806B' }]}
                            onPress={() => handleResetV12Progress(userId)}
                            disabled={loading}
                          >
                            <Text style={[styles.v12ActionBtnText, { color: '#fff' }]}>
                              🗑️ Reset V12 Progress
                            </Text>
                          </TouchableOpacity>
                        </View>
                        
                        {/* Latest Cylinder Data */}
                        {v12Status?.latestCylinderData && (
                          <View style={styles.v12CylinderGrid}>
                            <Text style={[styles.v12DetailsLabel, { color: artDecoGold }]}>
                              Latest Cylinder Status
                            </Text>
                            <View style={styles.v12CylinderMiniGrid}>
                              {Object.entries(v12Status.latestCylinderData).map(([key, data]) => (
                                <View 
                                  key={key}
                                  style={[
                                    styles.v12CylinderMini,
                                    { 
                                      backgroundColor: data.completed === 3 
                                        ? artDecoGold 
                                        : data.completed > 0 
                                          ? artDecoGold + '50'
                                          : palette.border || 'rgba(255,255,255,0.2)',
                                    }
                                  ]}
                                >
                                  <Text style={[
                                    styles.v12CylinderMiniText, 
                                    { color: data.completed === 3 ? '#1A1A1A' : palette.text }
                                  ]}>
                                    {data.completed}/3
                                  </Text>
                                </View>
                              ))}
                            </View>
                          </View>
                        )}
                        
                        {/* Diagnostic History */}
                        {v12UserHistory.length > 0 && (
                          <View style={styles.v12HistorySection}>
                            <Text style={[styles.v12DetailsLabel, { color: artDecoGold }]}>
                              Diagnostic History
                            </Text>
                            {v12UserHistory.slice(0, 5).map((entry, idx) => {
                              const date = entry.completedAt?.toDate 
                                ? entry.completedAt.toDate() 
                                : new Date(entry.completedAt);
                              
                              return (
                                <View key={entry.id || idx} style={[styles.v12HistoryItem, { borderColor: artDecoGold + '30' }]}>
                                  <View style={styles.v12HistoryLeft}>
                                    <Text style={[styles.v12HistoryDate, { color: palette.text }]}>
                                      {date.toLocaleDateString()}
                                    </Text>
                                    <Text style={[styles.v12HistoryPhase, { color: palette.mutedText }]}>
                                      {entry.monthNumber 
                                        ? `Month ${entry.monthNumber}` 
                                        : entry.weekNumber !== null 
                                          ? `Week ${entry.weekNumber}`
                                          : 'Initial'
                                      }
                                    </Text>
                                  </View>
                                  <View style={styles.v12HistoryRight}>
                                    <Text style={[styles.v12HistoryScore, { color: artDecoGold }]}>
                                      {entry.cylindersComplete}/12 cylinders
                                    </Text>
                                    <Text style={[styles.v12HistoryItems, { color: palette.mutedText }]}>
                                      {entry.totalCompleted}/36 items
                                    </Text>
                                  </View>
                                </View>
                              );
                            })}
                          </View>
                        )}
                        
                        {v12UserHistory.length === 0 && v12Status && (
                          <Text style={[styles.v12NoHistory, { color: palette.mutedText }]}>
                            Loading history...
                          </Text>
                        )}
                      </View>
                    )}
                  </View>
                );
              })}
            </ScrollView>
          )}
        </View>
      </View>
    );
  };

  const renderMetricsTab = () => (
    <View style={styles.tabContent}>
      <Text style={[styles.tabTitle, { color: palette.text || 'white' }]}>Site Metrics</Text>
      <View style={[styles.section, { borderTopColor: palette.border || 'rgba(255,255,255,0.16)' }]}>
        <Text style={[styles.sectionTitle, { color: palette.text || 'white' }]}>Analytics Dashboard</Text>
        <Text style={[styles.placeholderText, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>
          Metrics will be displayed here once analytics are integrated.
        </Text>
        <View style={styles.metricsGrid}>
          <View style={[styles.metricCard, { borderColor: palette.border || 'rgba(255,255,255,0.16)', backgroundColor: palette.surface || 'transparent' }]}>
            <Text style={[styles.metricNumber, { color: palette.primary || '#FF0600' }]}>-</Text>
            <Text style={[styles.metricLabel, { color: palette.text || 'white' }]}>Unique Visits</Text>
          </View>
          <View style={[styles.metricCard, { borderColor: palette.border || 'rgba(255,255,255,0.16)', backgroundColor: palette.surface || 'transparent' }]}>
            <Text style={[styles.metricNumber, { color: palette.primary || '#FF0600' }]}>-</Text>
            <Text style={[styles.metricLabel, { color: palette.text || 'white' }]}>Average Time Spent</Text>
          </View>
          <View style={[styles.metricCard, { borderColor: palette.border || 'rgba(255,255,255,0.16)', backgroundColor: palette.surface || 'transparent' }]}>
            <Text style={[styles.metricNumber, { color: palette.primary || '#FF0600' }]}>-</Text>
            <Text style={[styles.metricLabel, { color: palette.text || 'white' }]}>Content Views</Text>
          </View>
        </View>
      </View>
    </View>
  );

  const renderPacingTab = () => {
    return (
      <View style={styles.tabContent}>
        <View style={styles.formHeader}>
          <View>
            <Text style={[styles.tabTitle, { color: palette.text || 'white' }]}>Course Pacing & Deadlines</Text>
            <Text style={[styles.subtitle, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>
              Set dynamic unlock rules and deadlines that adapt to each student's start date
            </Text>
          </View>
        </View>

        {/* Course Selection */}
        <View style={[styles.section, { borderTopColor: palette.border || 'rgba(255,255,255,0.16)' }]}>
          <Text style={[styles.sectionTitle, { color: palette.text || 'white' }]}>Select Course</Text>
          
          {coursesLoading ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color={palette.primary || '#FF0600'} />
              <Text style={[styles.loadingText, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>Loading courses...</Text>
            </View>
          ) : courses.length === 0 ? (
            <Text style={[styles.emptyText, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>
              No courses available. Create a course first to configure pacing rules.
            </Text>
          ) : (
            <View style={styles.courseSelectionGrid}>
              {courses.map((course) => {
                const isSelected = selectedCourseForPacing?.id === course.id;
                const chaptersCount = course.chapters?.length || 0;
                const modulesCount = course.chapters?.reduce((sum, ch) => sum + (ch.modules?.length || 0), 0) || 0;
                
                return (
                  <TouchableOpacity
                    key={course.id}
                    style={[
                      styles.courseSelectionCard,
                      { borderColor: palette.border || 'rgba(255,255,255,0.2)' },
                      isSelected && {
                        borderColor: palette.primary || '#FF0600',
                        backgroundColor: (palette.primary || '#FF0600') + '10',
                        borderWidth: 3,
                      }
                    ]}
                    onPress={() => setSelectedCourseForPacing(course)}
                  >
                    <View style={styles.courseSelectionHeader}>
                      <Text style={[styles.courseSelectionTitle, { color: palette.text || 'white' }]} numberOfLines={2}>
                        {course.title}
                      </Text>
                      {isSelected && (
                        <View style={[styles.selectedBadge, { backgroundColor: palette.primary || '#FF0600' }]}>
                          <Text style={styles.selectedBadgeText}>✓</Text>
                        </View>
                      )}
                    </View>
                    <View style={styles.courseSelectionMeta}>
                      <Text style={[styles.courseSelectionMetaText, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>
                        📚 {chaptersCount} {chaptersCount === 1 ? 'chapter' : 'chapters'}
                      </Text>
                      <Text style={[styles.courseSelectionMetaText, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>
                        📝 {modulesCount} {modulesCount === 1 ? 'module' : 'modules'}
                      </Text>
                    </View>
                  </TouchableOpacity>
                );
              })}
            </View>
          )}
        </View>

        {/* Timeline Editor */}
        {selectedCourseForPacing && (
          <View style={[styles.section, { borderTopColor: palette.border || 'rgba(255,255,255,0.16)' }]}>
            <View style={styles.sectionHeader}>
              <Text style={[styles.sectionTitle, { color: palette.text || 'white' }]}>
                Course Timeline & Rules
              </Text>
              <TouchableOpacity
                style={[styles.previewButton, { borderColor: palette.primary || '#FF0600' }]}
                onPress={() => {}}
              >
                <Text style={[styles.previewButtonText, { color: palette.primary || '#FF0600' }]}>
                  👁️ Preview Timeline
                </Text>
              </TouchableOpacity>
            </View>

            {/* Visual Timeline */}
            <View style={[styles.timelineContainer, { backgroundColor: palette.surface, borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
              <View style={styles.timelineHeader}>
                <Text style={[styles.timelineHeaderText, { color: palette.text || 'white' }]}>
                  🎯 Course Start: Day 0
                </Text>
                <Text style={[styles.timelineSubtext, { color: palette.mutedText || 'rgba(255,255,255,0.50)' }]}>
                  Timeline adjusts automatically based on each student's enrollment date
                </Text>
              </View>

              <View style={styles.timelineLine}>
                {/* Day 0 Marker */}
                <View style={styles.timelineMarker}>
                  <View style={[styles.timelineDot, { backgroundColor: '#10B981' }]} />
                  <View style={[styles.timelineCard, { borderColor: palette.border || 'rgba(255,255,255,0.2)', backgroundColor: palette.surface }]}>
                    <View style={styles.timelineCardHeader}>
                      <Text style={[styles.timelineCardDay, { color: '#10B981' }]}>Day 0</Text>
                      <View style={[styles.timelineCardBadge, { backgroundColor: '#10B981' + '20' }]}>
                        <Text style={[styles.timelineCardBadgeText, { color: '#10B981' }]}>START</Text>
                      </View>
                    </View>
                    <Text style={[styles.timelineCardTitle, { color: palette.text || 'white' }]}>
                      Course Available
                    </Text>
                    <Text style={[styles.timelineCardDescription, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>
                      Student enrolls and gains immediate access to unlocked content
                    </Text>
                  </View>
                </View>

                {/* Chapter Timeline Items */}
                {selectedCourseForPacing.chapters?.map((chapter, chapterIndex) => {
                  // Get rules or use defaults  
                  const unlockRule = chapter.unlockRule || { type: chapterIndex === 0 ? 'immediate' : 'days', days: chapterIndex * 7 };
                  const deadlineRule = chapter.deadlineRule || { type: 'days', days: 14 };
                  
                  const unlockDay = unlockRule.type === 'immediate' ? 0 : unlockRule.days || 0;
                  const deadlineDay = deadlineRule.type === 'none' ? null : (unlockDay + (deadlineRule.days || 0));
                  
                  const isExpanded = expandedChapterIndex === chapterIndex;
                  const editingUnlock = isExpanded ? tempRules.unlock : unlockRule;
                  const editingDeadline = isExpanded ? tempRules.deadline : deadlineRule;
                  
                  return (
                    <View key={chapter.id || chapterIndex} style={styles.timelineMarker}>
                      <View style={[styles.timelineDot, { backgroundColor: palette.primary || '#FF0600' }]} />
                      <View style={[
                        styles.timelineCard, 
                        { 
                          borderColor: isExpanded ? (palette.primary || '#FF0600') : (palette.border || 'rgba(255,255,255,0.2)'), 
                          backgroundColor: palette.surface,
                          borderWidth: isExpanded ? 3 : 2,
                        }
                      ]}>
                        <View style={styles.timelineCardHeader}>
                          <Text style={[styles.timelineCardDay, { color: palette.primary || '#FF0600' }]}>
                            Day {unlockDay}
                          </Text>
                          <TouchableOpacity
                            style={[
                              styles.editRulesButton, 
                              { 
                                borderColor: isExpanded ? (palette.primary || '#FF0600') : (palette.border || 'rgba(255,255,255,0.3)'),
                                backgroundColor: isExpanded ? ((palette.primary || '#FF0600') + '15') : 'transparent',
                              }
                            ]}
                            onPress={() => handleToggleChapterEdit(chapter, chapterIndex)}
                          >
                            <Text style={[styles.editRulesButtonText, { color: isExpanded ? (palette.primary || '#FF0600') : (palette.text || 'white') }]}>
                              {isExpanded ? '✕ Close' : '⚙️ Edit Rules'}
                            </Text>
                          </TouchableOpacity>
                        </View>
                        
                        <Text style={[styles.timelineCardTitle, { color: palette.text || 'white' }]}>
                          {chapter.title}
                        </Text>

                        {!isExpanded ? (
                          // Display Mode
                          <>
                            <View style={styles.timelineRules}>
                              <View style={styles.timelineRule}>
                                <Text style={styles.timelineRuleIcon}>🔓</Text>
                                <View>
                                  <Text style={[styles.timelineRuleLabel, { color: palette.mutedText || 'rgba(255,255,255,0.50)' }]}>
                                    Unlock Rule
                                  </Text>
                                  <Text style={[styles.timelineRuleValue, { color: palette.text || 'white' }]}>
                                    {unlockRule.type === 'immediate' 
                                      ? 'Available immediately' 
                                      : unlockRule.type === 'completion'
                                      ? 'After previous chapter complete'
                                      : `Available ${unlockRule.days} days after start`}
                                  </Text>
                                </View>
                              </View>
                              
                              <View style={styles.timelineRule}>
                                <Text style={styles.timelineRuleIcon}>⏰</Text>
                                <View>
                                  <Text style={[styles.timelineRuleLabel, { color: palette.mutedText || 'rgba(255,255,255,0.50)' }]}>
                                    Deadline
                                  </Text>
                                  <Text style={[styles.timelineRuleValue, { color: palette.text || 'white' }]}>
                                    {deadlineRule.type === 'none' 
                                      ? 'No deadline (self-paced)' 
                                      : `Due by Day ${deadlineDay}`}
                                  </Text>
                                </View>
                              </View>
                            </View>

                            <View style={styles.modulesList}>
                              <Text style={[styles.modulesLabel, { color: palette.mutedText || 'rgba(255,255,255,0.50)' }]}>
                                {chapter.modules?.length || 0} {chapter.modules?.length === 1 ? 'module' : 'modules'}
                              </Text>
                            </View>
                          </>
                        ) : (
                          // Edit Mode
                          <View style={styles.inlineEditContainer}>
                            {/* Unlock Rule Editor */}
                            <View style={styles.inlineEditSection}>
                              <Text style={[styles.inlineEditLabel, { color: palette.text || 'white' }]}>
                                🔓 Unlock Rule
                              </Text>
                              <View style={styles.inlineRuleOptions}>
                                <TouchableOpacity
                                  style={[
                                    styles.inlineRuleOption,
                                    { borderColor: palette.border || 'rgba(255,255,255,0.2)' },
                                    editingUnlock?.type === 'immediate' && {
                                      backgroundColor: (palette.primary || '#FF0600') + '20',
                                      borderColor: palette.primary || '#FF0600',
                                    }
                                  ]}
                                  onPress={() => setTempRules({ ...tempRules, unlock: { type: 'immediate', days: 0 } })}
                                >
                                  <Text style={[styles.inlineRuleOptionText, { color: palette.text || 'white' }]}>
                                    ⚡ Immediate
                                  </Text>
                                </TouchableOpacity>

                                <TouchableOpacity
                                  style={[
                                    styles.inlineRuleOption,
                                    { borderColor: palette.border || 'rgba(255,255,255,0.2)' },
                                    editingUnlock?.type === 'days' && {
                                      backgroundColor: (palette.primary || '#FF0600') + '20',
                                      borderColor: palette.primary || '#FF0600',
                                    }
                                  ]}
                                  onPress={() => setTempRules({ ...tempRules, unlock: { type: 'days', days: editingUnlock?.days || 7 } })}
                                >
                                  <Text style={[styles.inlineRuleOptionText, { color: palette.text || 'white' }]}>
                                    📅 After Days
                                  </Text>
                                </TouchableOpacity>

                                <TouchableOpacity
                                  style={[
                                    styles.inlineRuleOption,
                                    { borderColor: palette.border || 'rgba(255,255,255,0.2)' },
                                    editingUnlock?.type === 'completion' && {
                                      backgroundColor: (palette.primary || '#FF0600') + '20',
                                      borderColor: palette.primary || '#FF0600',
                                    }
                                  ]}
                                  onPress={() => setTempRules({ ...tempRules, unlock: { type: 'completion', days: 0 } })}
                                >
                                  <Text style={[styles.inlineRuleOptionText, { color: palette.text || 'white' }]}>
                                    ✅ On Complete
                                  </Text>
                                </TouchableOpacity>
                              </View>

                              {editingUnlock?.type === 'days' && (
                                <View style={styles.inlineDaysInput}>
                                  <TextInput
                                    style={[styles.daysInput, { borderColor: palette.border || 'rgba(255,255,255,0.2)', color: palette.text || 'white' }]}
                                    value={String(editingUnlock?.days || 0)}
                                    onChangeText={(text) => setTempRules({ 
                                      ...tempRules, 
                                      unlock: { type: 'days', days: parseInt(text) || 0 } 
                                    })}
                                    keyboardType="number-pad"
                                    placeholder="7"
                                    placeholderTextColor={palette.mutedText || 'rgba(255,255,255,0.4)'}
                                  />
                                  <Text style={[styles.daysInputLabel, { color: palette.text || 'white' }]}>
                                    days after enrollment
                                  </Text>
                                </View>
                              )}
                            </View>

                            {/* Deadline Rule Editor */}
                            <View style={styles.inlineEditSection}>
                              <Text style={[styles.inlineEditLabel, { color: palette.text || 'white' }]}>
                                ⏰ Deadline Rule
                              </Text>
                              <View style={styles.inlineRuleOptions}>
                                <TouchableOpacity
                                  style={[
                                    styles.inlineRuleOption,
                                    { borderColor: palette.border || 'rgba(255,255,255,0.2)' },
                                    editingDeadline?.type === 'none' && {
                                      backgroundColor: (palette.primary || '#FF0600') + '20',
                                      borderColor: palette.primary || '#FF0600',
                                    }
                                  ]}
                                  onPress={() => setTempRules({ ...tempRules, deadline: { type: 'none', days: 0 } })}
                                >
                                  <Text style={[styles.inlineRuleOptionText, { color: palette.text || 'white' }]}>
                                    ♾️ None
                                  </Text>
                                </TouchableOpacity>

                                <TouchableOpacity
                                  style={[
                                    styles.inlineRuleOption,
                                    { borderColor: palette.border || 'rgba(255,255,255,0.2)' },
                                    editingDeadline?.type === 'days' && {
                                      backgroundColor: (palette.primary || '#FF0600') + '20',
                                      borderColor: palette.primary || '#FF0600',
                                    }
                                  ]}
                                  onPress={() => setTempRules({ ...tempRules, deadline: { type: 'days', days: editingDeadline?.days || 14 } })}
                                >
                                  <Text style={[styles.inlineRuleOptionText, { color: palette.text || 'white' }]}>
                                    ⏱️ Due in Days
                                  </Text>
                                </TouchableOpacity>
                              </View>

                              {editingDeadline?.type === 'days' && (
                                <View style={styles.inlineDaysInput}>
                                  <TextInput
                                    style={[styles.daysInput, { borderColor: palette.border || 'rgba(255,255,255,0.2)', color: palette.text || 'white' }]}
                                    value={String(editingDeadline?.days || 0)}
                                    onChangeText={(text) => setTempRules({ 
                                      ...tempRules, 
                                      deadline: { type: 'days', days: parseInt(text) || 0 } 
                                    })}
                                    keyboardType="number-pad"
                                    placeholder="14"
                                    placeholderTextColor={palette.mutedText || 'rgba(255,255,255,0.4)'}
                                  />
                                  <Text style={[styles.daysInputLabel, { color: palette.text || 'white' }]}>
                                    days after unlock
                                  </Text>
                                </View>
                              )}
                            </View>

                            {/* Save Button */}
                            <AnimatedButton
                              title={loading ? 'Saving...' : 'Save Rules'}
                              onPress={() => handleSaveChapterRules(chapterIndex)}
                              disabled={loading}
                              loading={loading}
                              variant="primary"
                              style={{ marginTop: 16 }}
                            />
                          </View>
                        )}
                      </View>
                    </View>
                  );
                })}
              </View>
            </View>

            {/* Info Box */}
            <View style={[styles.infoBox, { backgroundColor: (palette.primary || '#FF0600') + '10', borderColor: palette.primary || '#FF0600' }]}>
              <Text style={styles.infoBoxIcon}>💡</Text>
              <View style={styles.infoBoxContent}>
                <Text style={[styles.infoBoxTitle, { color: palette.text || 'white' }]}>
                  Dynamic Timeline System
                </Text>
                <Text style={[styles.infoBoxText, { color: palette.mutedText || 'rgba(255,255,255,0.70)' }]}>
                  These rules apply to all students automatically. If Student A starts January 1st, Chapter 2 unlocks January 8th. 
                  If Student B starts March 15th, Chapter 2 unlocks March 22nd.
                </Text>
              </View>
            </View>
          </View>
        )}
      </View>
    );
  };

  const renderGlossaryTab = () => {
    if (showTermForm && editingTerm) {
      return (
        <View style={styles.tabContent}>
          <View style={styles.formHeader}>
            <Text style={[styles.tabTitle, { color: palette.text || 'white' }]}>
              {editingTerm.id ? 'Edit Glossary Term' : 'Create New Term'}
            </Text>
            <TouchableOpacity
              onPress={() => {
                setShowTermForm(false);
                setEditingTerm(null);
              }}
              style={[styles.closeButton, { borderColor: palette.border || 'rgba(255,255,255,0.2)' }]}
            >
              <Text style={[styles.closeButtonText, { color: palette.text || 'white' }]}>✕ Cancel</Text>
            </TouchableOpacity>
          </View>

          <AnimatedCard style={[styles.section, { borderTopColor: palette.border || 'rgba(255,255,255,0.16)', backgroundColor: palette.surface }]} delay={0}>
            <Text style={[styles.sectionTitle, { color: palette.text || 'white' }]}>Term Details</Text>
            
            <View style={styles.formGroup}>
              <Text style={[styles.label, { color: palette.text || 'white' }]}>Term *</Text>
              <TextInput
                style={[styles.textInput, { borderColor: palette.border || 'rgba(255,255,255,0.2)', color: palette.text || 'white' }]}
                value={editingTerm.term}
                onChangeText={(value) => setEditingTerm({ ...editingTerm, term: value })}
                placeholder="e.g., ROI, Revenue, Margin"
                placeholderTextColor={palette.mutedText || 'rgba(255,255,255,0.4)'}
              />
            </View>

            <View style={styles.formGroup}>
              <Text style={[styles.label, { color: palette.text || 'white' }]}>Definition *</Text>
              <TextInput
                style={[styles.textInput, styles.textArea, { borderColor: palette.border || 'rgba(255,255,255,0.2)', color: palette.text || 'white' }]}
                value={editingTerm.definition}
                onChangeText={(value) => setEditingTerm({ ...editingTerm, definition: value })}
                placeholder="Clear, concise definition of the term"
                placeholderTextColor={palette.mutedText || 'rgba(255,255,255,0.4)'}
                multiline
              />
            </View>

            <View style={styles.formGroup}>
              <Text style={[styles.label, { color: palette.text || 'white' }]}>Category (Optional)</Text>
              <TextInput
                style={[styles.textInput, { borderColor: palette.border || 'rgba(255,255,255,0.2)', color: palette.text || 'white' }]}
                value={editingTerm.category || ''}
                onChangeText={(value) => setEditingTerm({ ...editingTerm, category: value })}
                placeholder="e.g., Finance, Marketing, Operations"
                placeholderTextColor={palette.mutedText || 'rgba(255,255,255,0.4)'}
              />
              <Text style={[styles.helperText, { color: palette.mutedText || 'rgba(255,255,255,0.6)', fontSize: 12, marginTop: 4 }]}>
                Group related terms together by category
              </Text>
            </View>

            <View style={styles.formGroup}>
              <Text style={[styles.label, { color: palette.text || 'white' }]}>Example Usage (Optional)</Text>
              <TextInput
                style={[styles.textInput, styles.textArea, { borderColor: palette.border || 'rgba(255,255,255,0.2)', color: palette.text || 'white' }]}
                value={editingTerm.example || ''}
                onChangeText={(value) => setEditingTerm({ ...editingTerm, example: value })}
                placeholder="e.g., 'Our ROI increased by 25% this quarter'"
                placeholderTextColor={palette.mutedText || 'rgba(255,255,255,0.4)'}
                multiline
              />
            </View>
          </AnimatedCard>

          <View style={styles.formActions}>
            <AnimatedButton
              title={loading ? 'Saving...' : 'Save Term'}
              onPress={handleSaveTerm}
              disabled={loading}
              loading={loading}
              style={styles.saveButton}
            />
          </View>
        </View>
      );
    }

    // Term List View
    return (
      <View style={styles.tabContent}>
        <View style={styles.formHeader}>
          <Text style={[styles.tabTitle, { color: palette.text || 'white' }]}>Business Glossary</Text>
          <AnimatedButton
            title="+ Add Term"
            onPress={handleCreateTerm}
            variant="primary"
            style={styles.createButton}
          />
        </View>

        <View style={[styles.section, { borderTopColor: palette.border || 'rgba(255,255,255,0.16)' }]}>
          <View style={styles.labelRow}>
            <View>
              <Text style={[styles.sectionTitle, { color: palette.text || 'white' }]}>
                All Terms ({glossaryTerms.length})
              </Text>
              <Text style={[styles.helperText, { color: palette.mutedText || 'rgba(255,255,255,0.6)', marginTop: 4 }]}>
                Define business terms that users can reference while learning
              </Text>
            </View>
          </View>
          
          {glossaryLoading ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color={palette.primary || '#FF0600'} />
              <Text style={[styles.loadingText, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>Loading terms...</Text>
            </View>
          ) : glossaryTerms.length === 0 ? (
            <Text style={[styles.emptyText, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>
              No terms yet. Click "+ Add Term" to create your first glossary entry!
            </Text>
          ) : (
            <View style={styles.glossaryList}>
              {glossaryTerms.map((term, index) => (
                <AnimatedCard
                  key={term.id}
                  delay={index * 50}
                  style={[styles.glossaryCard, { borderColor: palette.border || 'rgba(255,255,255,0.16)', backgroundColor: palette.surface }]}
                >
                  <View style={styles.glossaryCardHeader}>
                    <View style={styles.glossaryCardInfo}>
                      <Text style={[styles.glossaryTerm, { color: palette.text || 'white' }]}>
                        {term.term}
                      </Text>
                      {term.category && (
                        <View style={[styles.categoryBadge, { backgroundColor: 'rgba(255, 6, 0, 0.15)', borderColor: palette.primary || '#FF0600' }]}>
                          <Text style={[styles.categoryBadgeText, { color: palette.primary || '#FF0600' }]}>{term.category}</Text>
                        </View>
                      )}
                    </View>
                    <View style={styles.glossaryCardActions}>
                      <TouchableOpacity
                        onPress={() => handleEditTerm(term)}
                        style={[styles.iconButton, { borderColor: palette.border || 'rgba(255,255,255,0.2)' }]}
                      >
                        <Text style={[styles.iconButtonText, { color: palette.text || 'white' }]}>✎</Text>
                      </TouchableOpacity>
                      <TouchableOpacity
                        onPress={() => handleDeleteTerm(term.id)}
                        style={[styles.iconButton, { borderColor: '#EA806B' }]}
                      >
                        <Text style={[styles.iconButtonText, { color: '#EA806B' }]}>✕</Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                  
                  <Text style={[styles.glossaryDefinition, { color: palette.mutedText || 'rgba(255,255,255,0.8)' }]}>
                    {term.definition}
                  </Text>
                  
                  {term.example && (
                    <View style={[styles.exampleBox, { backgroundColor: 'rgba(255,255,255,0.03)', borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                      <Text style={[styles.exampleLabel, { color: palette.mutedText || 'rgba(255,255,255,0.6)' }]}>Example:</Text>
                      <Text style={[styles.exampleText, { color: palette.mutedText || 'rgba(255,255,255,0.8)' }]}>{term.example}</Text>
                    </View>
                  )}
                </AnimatedCard>
              ))}
            </View>
          )}
        </View>
      </View>
    );
  };

  // Sub-tab rendering for Site Management - Maintenance
  const renderMaintenanceSubTab = () => (
    <>
      {/* Web App Section */}
      <View style={[styles.section, { borderTopColor: palette.border || 'rgba(255,255,255,0.16)' }]}>
        <Text style={[styles.sectionTitle, { color: palette.text || 'white', marginBottom: 16 }]}>
          🌐 Web App Controls
        </Text>
        
        {/* Web Coming Soon */}
        <View style={styles.maintenanceRow}>
          <View style={styles.maintenanceInfo}>
            <Text style={[styles.maintenanceTitle, { color: palette.text || 'rgba(255, 255, 255, 0.80)' }]}>Coming Soon Page</Text>
            <Text style={[styles.maintenanceDesc, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>
              Display the coming soon page to all web app users. Admins can still access all pages.
            </Text>
          </View>
          <ToggleSwitch
            isOn={webComingSoon}
            onToggle={() => handleMaintenanceToggle('webComingSoon', !webComingSoon)}
            disabled={updatingMaintenance}
          />
        </View>
        
        {/* Web Maintenance */}
        <View style={[styles.maintenanceRow, { marginTop: 12 }]}>
          <View style={styles.maintenanceInfo}>
            <Text style={[styles.maintenanceTitle, { color: palette.text || 'rgba(255, 255, 255, 0.80)' }]}>Maintenance Mode</Text>
            <Text style={[styles.maintenanceDesc, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>
              Display maintenance page to all web app users. Admins can bypass with login.
            </Text>
          </View>
          <ToggleSwitch
            isOn={webMaintenance}
            onToggle={() => handleMaintenanceToggle('webMaintenance', !webMaintenance)}
            disabled={updatingMaintenance}
          />
        </View>
      </View>

      {/* Mobile App Section */}
      <View style={[styles.section, { borderTopColor: palette.border || 'rgba(255,255,255,0.16)' }]}>
        <Text style={[styles.sectionTitle, { color: palette.text || 'white', marginBottom: 16 }]}>
          📱 Mobile App Controls
        </Text>
        
        {/* Mobile Coming Soon */}
        <View style={styles.maintenanceRow}>
          <View style={styles.maintenanceInfo}>
            <Text style={[styles.maintenanceTitle, { color: palette.text || 'rgba(255, 255, 255, 0.80)' }]}>Coming Soon Page</Text>
            <Text style={[styles.maintenanceDesc, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>
              Display the coming soon page to all mobile app users. Admins can still access all pages.
            </Text>
          </View>
          <ToggleSwitch
            isOn={mobileComingSoon}
            onToggle={() => handleMaintenanceToggle('mobileComingSoon', !mobileComingSoon)}
            disabled={updatingMaintenance}
          />
        </View>
        
        {/* Mobile Maintenance */}
        <View style={[styles.maintenanceRow, { marginTop: 12 }]}>
          <View style={styles.maintenanceInfo}>
            <Text style={[styles.maintenanceTitle, { color: palette.text || 'rgba(255, 255, 255, 0.80)' }]}>Maintenance Mode</Text>
            <Text style={[styles.maintenanceDesc, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>
              Display maintenance page to all mobile app users. Admins can bypass with login.
            </Text>
          </View>
          <ToggleSwitch
            isOn={mobileMaintenance}
            onToggle={() => handleMaintenanceToggle('mobileMaintenance', !mobileMaintenance)}
            disabled={updatingMaintenance}
          />
        </View>
      </View>

      {/* Quick Actions - Global Controls */}
      <View style={[styles.section, { borderTopColor: palette.border || 'rgba(255,255,255,0.16)' }]}>
        <Text style={[styles.sectionTitle, { color: palette.text || 'white', marginBottom: 16 }]}>
          ⚡ Quick Actions (All Platforms)
        </Text>
        
        {/* Global Coming Soon */}
        <View style={styles.maintenanceRow}>
          <View style={styles.maintenanceInfo}>
            <Text style={[styles.maintenanceTitle, { color: palette.text || 'rgba(255, 255, 255, 0.80)' }]}>
              🚀 Coming Soon (All Platforms)
            </Text>
            <Text style={[styles.maintenanceDesc, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>
              Enable Coming Soon page for BOTH Web and Mobile apps simultaneously. This is the fastest way to lock down all platforms.
            </Text>
            {(webComingSoon && mobileComingSoon) && (
              <Text style={[styles.bannerStatus, { color: '#4CAF50' }]}>
                ✓ Both platforms showing Coming Soon
              </Text>
            )}
          </View>
          <ToggleSwitch
            isOn={webComingSoon && mobileComingSoon}
            onToggle={() => handleMaintenanceToggle('comingSoon', !(webComingSoon && mobileComingSoon))}
            disabled={updatingMaintenance}
          />
        </View>

        {/* Global Maintenance */}
        <View style={[styles.maintenanceRow, { marginTop: 12 }]}>
          <View style={styles.maintenanceInfo}>
            <Text style={[styles.maintenanceTitle, { color: palette.text || 'rgba(255, 255, 255, 0.80)' }]}>
              🔧 Maintenance Mode (All Platforms)
            </Text>
            <Text style={[styles.maintenanceDesc, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>
              Enable Maintenance page for BOTH Web and Mobile apps simultaneously. Users will see a maintenance message.
            </Text>
            {(webMaintenance && mobileMaintenance) && (
              <Text style={[styles.bannerStatus, { color: '#EA806B' }]}>
                ⚠️ Both platforms in Maintenance Mode
              </Text>
            )}
          </View>
          <ToggleSwitch
            isOn={webMaintenance && mobileMaintenance}
            onToggle={() => handleMaintenanceToggle('globalMaintenance', !(webMaintenance && mobileMaintenance))}
            disabled={updatingMaintenance}
          />
        </View>

        {/* Maintenance Banner */}
        <View style={[styles.maintenanceRow, { marginTop: 12 }]}>
          <View style={styles.maintenanceInfo}>
            <Text style={[styles.maintenanceTitle, { color: palette.text || 'rgba(255, 255, 255, 0.80)' }]}>
              📢 Maintenance Banner (Web Only)
            </Text>
            <Text style={[styles.maintenanceDesc, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>
              Display a persistent banner at the top of the web app showing maintenance status and duration. Stays fixed while scrolling.
            </Text>
            {bannerToggle && bannerStartedAt && (
              <Text style={[styles.bannerStatus, { color: palette.primary || 'rgba(255, 6, 0, 0.80)' }]}>
                Banner started: {bannerStartedAt.toDate ? bannerStartedAt.toDate().toLocaleString() : new Date(bannerStartedAt).toLocaleString()}
              </Text>
            )}
          </View>
          <ToggleSwitch
            isOn={bannerToggle}
            onToggle={() => handleMaintenanceToggle('banner', !bannerToggle)}
            disabled={updatingMaintenance}
          />
        </View>
      </View>

      {/* Status Overview */}
      <View style={[styles.section, { borderTopColor: palette.border || 'rgba(255,255,255,0.16)' }]}>
        <Text style={[styles.sectionTitle, { color: palette.text || 'white', marginBottom: 16 }]}>
          📊 Current Status
        </Text>
        <View style={styles.statusGrid}>
          <View style={[styles.statusCard, { backgroundColor: webComingSoon || webMaintenance ? '#EA806B20' : '#4CAF5020', borderColor: webComingSoon || webMaintenance ? '#EA806B' : '#4CAF50' }]}>
            <Text style={styles.statusCardEmoji}>🌐</Text>
            <Text style={[styles.statusCardTitle, { color: palette.text }]}>Web App</Text>
            <Text style={[styles.statusCardStatus, { color: webComingSoon || webMaintenance ? '#EA806B' : '#4CAF50' }]}>
              {webMaintenance ? '🔧 Maintenance' : webComingSoon ? '⏳ Coming Soon' : '✅ Active'}
            </Text>
          </View>
          <View style={[styles.statusCard, { backgroundColor: mobileComingSoon || mobileMaintenance ? '#EA806B20' : '#4CAF5020', borderColor: mobileComingSoon || mobileMaintenance ? '#EA806B' : '#4CAF50' }]}>
            <Text style={styles.statusCardEmoji}>📱</Text>
            <Text style={[styles.statusCardTitle, { color: palette.text }]}>Mobile App</Text>
            <Text style={[styles.statusCardStatus, { color: mobileComingSoon || mobileMaintenance ? '#EA806B' : '#4CAF50' }]}>
              {mobileMaintenance ? '🔧 Maintenance' : mobileComingSoon ? '⏳ Coming Soon' : '✅ Active'}
            </Text>
          </View>
        </View>
      </View>
    </>
  );

  // Legacy renderMaintenanceTab for backwards compatibility
  const renderMaintenanceTab = () => (
    <View style={styles.tabContent}>
      <Text style={[styles.tabTitle, { color: palette.text || 'white' }]}>Maintenance Settings</Text>
      {renderMaintenanceSubTab()}
    </View>
  );

  const renderDevelopmentTab = () => (
    <View style={styles.tabContent}>
      <Text style={[styles.tabTitle, { color: palette.text || 'white' }]}>Development Settings</Text>
      
      {/* Emulator Connection Status */}
      <View style={[styles.section, { borderTopColor: palette.border || 'rgba(255,255,255,0.16)' }]}>
        <View style={styles.maintenanceRow}>
          <View style={styles.maintenanceInfo}>
            <Text style={[styles.maintenanceTitle, { color: palette.text || 'rgba(255, 255, 255, 0.80)' }]}>Firebase Emulator Connection</Text>
            <Text style={[styles.maintenanceDesc, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>
              Connect to local Firebase emulators for testing. This allows you to test notifications, user creation, and other Firebase functions without affecting production data.
            </Text>
            <Text style={[
              styles.statusText, 
              { color: isEmulatorConnected ? '#4CAF50' : (emulatorAvailable ? palette.text : '#ff6b6b') || 'rgba(255, 255, 255, 0.80)' }
            ]}>
              Status: {isEmulatorConnected ? '✅ Connected to Emulators' : 
                       emulatorAvailable ? '🔥 Production Mode (Emulators Available)' : 
                       '❌ Production Mode (Emulators Not Running)'}
            </Text>
            {!emulatorAvailable && !isEmulatorConnected && (
              <Text style={[styles.emulatorInstructions, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>
                To use emulators, run: firebase emulators:start
              </Text>
            )}
          </View>
          <ToggleSwitch
            isOn={isEmulatorConnected}
            onToggle={handleEmulatorToggle}
            disabled={emulatorLoading || (!emulatorAvailable && !isEmulatorConnected)}
          />
        </View>
      </View>

      {/* Emergency Production Mode */}
      <View style={[styles.section, { borderTopColor: palette.border || 'rgba(255,255,255,0.16)' }]}>
        <Text style={[styles.sectionTitle, { color: palette.text || 'white' }]}>Emergency Controls</Text>
        <Text style={[styles.instructionText, { color: palette.mutedText || 'rgba(255,255,255,0.80)' }]}>
          If you're stuck in emulator mode and can't access production data or login:
        </Text>
        
        <TouchableOpacity
          style={[styles.emergencyButton, { backgroundColor: '#ff6b6b', borderColor: '#ff5252' }]}
          onPress={() => {
            if (window.confirm('⚠️ This will immediately switch to PRODUCTION mode and reload the page. You will lose any unsaved emulator data. Continue?')) {
              emulatorService.forceProductionMode();
            }
          }}
        >
          <Text style={[styles.emergencyButtonText, { color: 'white' }]}>
            FORCE PRODUCTION MODE
          </Text>
        </TouchableOpacity>
        
        <Text style={[styles.emergencyNote, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>
          ⚠️ Use this if emulator toggle is not working or you need immediate access to production Firebase.
        </Text>
      </View>

      {/* Emulator Information */}
      {emulatorAvailable && (
        <View style={[styles.section, { borderTopColor: palette.border || 'rgba(255,255,255,0.16)' }]}>
          <Text style={[styles.sectionTitle, { color: palette.text || 'white' }]}>Emulator Endpoints</Text>
          <View style={styles.endpointsList}>
            <Text style={[styles.endpointItem, { color: palette.mutedText || 'rgba(255,255,255,0.80)' }]}>
              • Firestore: localhost:8080
            </Text>
            <Text style={[styles.endpointItem, { color: palette.mutedText || 'rgba(255,255,255,0.80)' }]}>
              • Auth: localhost:9099
            </Text>
            <Text style={[styles.endpointItem, { color: palette.mutedText || 'rgba(255,255,255,0.80)' }]}>
              • Functions: localhost:5002
            </Text>
            <Text style={[styles.endpointItem, { color: palette.mutedText || 'rgba(255,255,255,0.80)' }]}>
              • Emulator UI: localhost:4000
            </Text>
          </View>
          
          {isEmulatorConnected && (
            <TouchableOpacity
              style={[styles.emulatorButton, { backgroundColor: palette.primary || '#667eea' }]}
              onPress={() => window.open('http://localhost:4000', '_blank')}
            >
              <Text style={[styles.emulatorButtonText, { color: 'white' }]}>
                Open Emulator UI
              </Text>
            </TouchableOpacity>
          )}
        </View>
      )}

      {/* Testing Instructions */}
      <View style={[styles.section, { borderTopColor: palette.border || 'rgba(255,255,255,0.16)' }]}>
        <Text style={[styles.sectionTitle, { color: palette.text || 'white' }]}>Testing Notifications</Text>
        <Text style={[styles.instructionText, { color: palette.mutedText || 'rgba(255,255,255,0.80)' }]}>
          When connected to emulators:
        </Text>
        <Text style={[styles.instructionStep, { color: palette.mutedText || 'rgba(255,255,255,0.70)' }]}>
          1. Register a new user to trigger welcome email function
        </Text>
        <Text style={[styles.instructionStep, { color: palette.mutedText || 'rgba(255,255,255,0.70)' }]}>
          2. Check Functions tab in Emulator UI for logs
        </Text>
        <Text style={[styles.instructionStep, { color: palette.mutedText || 'rgba(255,255,255,0.70)' }]}>
          3. View notifications collection in Firestore tab
        </Text>
      </View>

      {/* Email Testing */}
      <View style={[styles.section, { borderTopColor: palette.border || 'rgba(255,255,255,0.16)' }]}>
        <Text style={[styles.sectionTitle, { color: palette.text || 'white' }]}>Test Email System</Text>
        <Text style={[styles.instructionText, { color: palette.mutedText || 'rgba(255,255,255,0.80)' }]}>
          Send test emails to verify SendGrid integration and email templates.
        </Text>
        <Text style={[styles.connectionStatus, { color: palette.primary || '#FF0600' }]}>
          Production Mode (Live Emails) - Test emails will be sent to real recipients
        </Text>
        
        <View style={styles.testEmailForm}>
          <FormInput
            label="Email Address"
            value={testEmailAddress}
            onChangeText={setTestEmailAddress}
            placeholder="test@example.com"
            autoCapitalize="none"
            keyboardType="email-address"
            style={{ marginBottom: 16 }}
          />
          
          <FormInput
            label="First Name (Optional)"
            value={testEmailName}
            onChangeText={setTestEmailName}
            placeholder="Test User"
            style={{ marginBottom: 20 }}
          />
          
          {/* Welcome Email Test */}
          <View style={styles.emailTestSection}>
            <Text style={[styles.emailTestTitle, { color: palette.text || 'white' }]}>Welcome Email</Text>
            <TouchableOpacity
              style={[styles.testEmailButton, { backgroundColor: palette.primary || '#667eea', opacity: testEmailLoading ? 0.6 : 1 }]}
              onPress={() => handleSendTestEmail('welcome')}
              disabled={testEmailLoading}
            >
              {testEmailLoading ? (
                <ActivityIndicator size="small" color="white" />
              ) : (
                <Text style={[styles.testEmailButtonText, { color: 'white' }]}>
                  Send Welcome Email
                </Text>
              )}
            </TouchableOpacity>
          </View>

          {/* Progress Report Tests */}
          <View style={styles.emailTestSection}>
            <Text style={[styles.emailTestTitle, { color: palette.text || 'white' }]}>Progress Report Email</Text>
            <Text style={[styles.emailTestSubtitle, { color: palette.mutedText || 'rgba(255,255,255,0.70)' }]}>
              Select frequency to test different progress report formats:
            </Text>
            
            <View style={styles.frequencySelector}>
              {['daily', 'weekly', 'monthly'].map((freq) => (
                <TouchableOpacity
                  key={freq}
                  style={[
                    styles.frequencyButton,
                    { 
                      borderColor: palette.border || 'rgba(255,255,255,0.3)',
                      backgroundColor: progressFrequency === freq ? (palette.primary || '#667eea') + '20' : 'transparent'
                    },
                    progressFrequency === freq && { borderColor: palette.primary || '#667eea' }
                  ]}
                  onPress={() => setProgressFrequency(freq)}
                >
                  <Text style={[
                    styles.frequencyButtonText, 
                    { color: progressFrequency === freq ? (palette.primary || '#667eea') : (palette.text || 'white') }
                  ]}>
                    {freq.charAt(0).toUpperCase() + freq.slice(1)}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
            
            <TouchableOpacity
              style={[styles.testEmailButton, { backgroundColor: '#f093fb', opacity: testEmailLoading ? 0.6 : 1 }]}
              onPress={() => handleSendTestEmail('progress', progressFrequency)}
              disabled={testEmailLoading}
            >
              {testEmailLoading ? (
                <ActivityIndicator size="small" color="white" />
              ) : (
                <Text style={[styles.testEmailButtonText, { color: 'white' }]}>
                  Send {progressFrequency.charAt(0).toUpperCase() + progressFrequency.slice(1)} Report
                </Text>
              )}
            </TouchableOpacity>
          </View>
          
          {/* Network Test Button */}
          <TouchableOpacity
            style={[styles.testEmailButton, { backgroundColor: '#28a745', marginBottom: 10 }]}
            onPress={testNetworkConnection}
          >
            <Text style={[styles.testEmailButtonText, { color: 'white' }]}>
              Test Network Connection
            </Text>
          </TouchableOpacity>

          <Text style={[styles.testEmailNote, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>
            Production mode: Emails will be sent to real addresses. Use your own email for testing.
          </Text>
        </View>
      </View>
    </View>
  );

  // Combined Site Management Tab with sub-tabs
  const renderSiteManagementTab = () => (
    <View style={styles.tabContent}>
      <Text style={[styles.tabTitle, { color: palette.text || 'white' }]}>Site Management</Text>
      
      {/* Sub-tab Navigation */}
      <View style={styles.subTabNav}>
        <TouchableOpacity
          style={[
            styles.subTabButton,
            siteManagementSubTab === 'maintenance' && styles.subTabButtonActive,
            { borderColor: palette.border || 'rgba(255,255,255,0.2)' },
            siteManagementSubTab === 'maintenance' && { 
              backgroundColor: '#EA806B', 
              borderColor: '#EA806B' 
            }
          ]}
          onPress={() => setSiteManagementSubTab('maintenance')}
        >
          <Text style={[
            styles.subTabButtonText,
            { color: siteManagementSubTab === 'maintenance' ? 'white' : palette.text || 'white' }
          ]}>
            🔧 Maintenance
          </Text>
        </TouchableOpacity>
        
        <TouchableOpacity
          style={[
            styles.subTabButton,
            siteManagementSubTab === 'development' && styles.subTabButtonActive,
            { borderColor: palette.border || 'rgba(255,255,255,0.2)' },
            siteManagementSubTab === 'development' && { 
              backgroundColor: palette.primary || '#667eea', 
              borderColor: palette.primary || '#667eea' 
            }
          ]}
          onPress={() => setSiteManagementSubTab('development')}
        >
          <Text style={[
            styles.subTabButtonText,
            { color: siteManagementSubTab === 'development' ? 'white' : palette.text || 'white' }
          ]}>
            ⚙️ Development
          </Text>
        </TouchableOpacity>
      </View>

      {/* Sub-tab Content */}
      {siteManagementSubTab === 'maintenance' && renderMaintenanceSubTab()}
      
      {siteManagementSubTab === 'development' && (
        <>
          {/* Emulator Connection Status */}
          <View style={[styles.section, { borderTopColor: palette.border || 'rgba(255,255,255,0.16)' }]}>
            <View style={styles.maintenanceRow}>
              <View style={styles.maintenanceInfo}>
                <Text style={[styles.maintenanceTitle, { color: palette.text || 'rgba(255, 255, 255, 0.80)' }]}>Firebase Emulator Connection</Text>
                <Text style={[styles.maintenanceDesc, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>
                  Connect to local Firebase emulators for testing. This allows you to test notifications, user creation, and other Firebase functions without affecting production data.
                </Text>
                <Text style={[
                  styles.statusText, 
                  { color: isEmulatorConnected ? '#4CAF50' : (emulatorAvailable ? palette.text : '#ff6b6b') || 'rgba(255, 255, 255, 0.80)' }
                ]}>
                  Status: {isEmulatorConnected ? '✅ Connected to Emulators' : 
                           emulatorAvailable ? '🔥 Production Mode (Emulators Available)' : 
                           '❌ Production Mode (Emulators Not Running)'}
                </Text>
                {!emulatorAvailable && !isEmulatorConnected && (
                  <Text style={[styles.emulatorInstructions, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>
                    To use emulators, run: firebase emulators:start
                  </Text>
                )}
              </View>
              <ToggleSwitch
                isOn={isEmulatorConnected}
                onToggle={handleEmulatorToggle}
                disabled={emulatorLoading || (!emulatorAvailable && !isEmulatorConnected)}
              />
            </View>
          </View>

          {/* Emergency Production Mode */}
          <View style={[styles.section, { borderTopColor: palette.border || 'rgba(255,255,255,0.16)' }]}>
            <Text style={[styles.sectionTitle, { color: palette.text || 'white' }]}>Emergency Controls</Text>
            <Text style={[styles.instructionText, { color: palette.mutedText || 'rgba(255,255,255,0.80)' }]}>
              If you're stuck in emulator mode and can't access production data or login:
            </Text>
            
            <TouchableOpacity
              style={[styles.emergencyButton, { backgroundColor: '#ff6b6b', borderColor: '#ff5252' }]}
              onPress={() => {
                if (window.confirm('⚠️ This will immediately switch to PRODUCTION mode and reload the page. You will lose any unsaved emulator data. Continue?')) {
                  emulatorService.forceProductionMode();
                }
              }}
            >
              <Text style={[styles.emergencyButtonText, { color: 'white' }]}>
                FORCE PRODUCTION MODE
              </Text>
            </TouchableOpacity>
            
            <Text style={[styles.emergencyNote, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>
              ⚠️ Use this if emulator toggle is not working or you need immediate access to production Firebase.
            </Text>
          </View>

          {/* Emulator Information */}
          {emulatorAvailable && (
            <View style={[styles.section, { borderTopColor: palette.border || 'rgba(255,255,255,0.16)' }]}>
              <Text style={[styles.sectionTitle, { color: palette.text || 'white' }]}>Emulator Endpoints</Text>
              <View style={styles.endpointsList}>
                <Text style={[styles.endpointItem, { color: palette.mutedText || 'rgba(255,255,255,0.80)' }]}>
                  • Firestore: localhost:8080
                </Text>
                <Text style={[styles.endpointItem, { color: palette.mutedText || 'rgba(255,255,255,0.80)' }]}>
                  • Auth: localhost:9099
                </Text>
                <Text style={[styles.endpointItem, { color: palette.mutedText || 'rgba(255,255,255,0.80)' }]}>
                  • Functions: localhost:5002
                </Text>
                <Text style={[styles.endpointItem, { color: palette.mutedText || 'rgba(255,255,255,0.80)' }]}>
                  • Emulator UI: localhost:4000
                </Text>
              </View>
              
              {isEmulatorConnected && (
                <TouchableOpacity
                  style={[styles.emulatorButton, { backgroundColor: palette.primary || '#667eea' }]}
                  onPress={() => window.open('http://localhost:4000', '_blank')}
                >
                  <Text style={[styles.emulatorButtonText, { color: 'white' }]}>
                    Open Emulator UI
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          )}

          {/* Testing Instructions */}
          <View style={[styles.section, { borderTopColor: palette.border || 'rgba(255,255,255,0.16)' }]}>
            <Text style={[styles.sectionTitle, { color: palette.text || 'white' }]}>Testing Notifications</Text>
            <Text style={[styles.instructionText, { color: palette.mutedText || 'rgba(255,255,255,0.80)' }]}>
              When connected to emulators:
            </Text>
            <Text style={[styles.instructionStep, { color: palette.mutedText || 'rgba(255,255,255,0.70)' }]}>
              1. Register a new user to trigger welcome email function
            </Text>
            <Text style={[styles.instructionStep, { color: palette.mutedText || 'rgba(255,255,255,0.70)' }]}>
              2. Check Functions tab in Emulator UI for logs
            </Text>
            <Text style={[styles.instructionStep, { color: palette.mutedText || 'rgba(255,255,255,0.70)' }]}>
              3. View notifications collection in Firestore tab
            </Text>
          </View>

          {/* Email Testing */}
          <View style={[styles.section, { borderTopColor: palette.border || 'rgba(255,255,255,0.16)' }]}>
            <Text style={[styles.sectionTitle, { color: palette.text || 'white' }]}>Test Email System</Text>
            <Text style={[styles.instructionText, { color: palette.mutedText || 'rgba(255,255,255,0.80)' }]}>
              Send test emails to verify SendGrid integration and email templates.
            </Text>
            <Text style={[styles.connectionStatus, { color: palette.primary || '#FF0600' }]}>
              Production Mode (Live Emails) - Test emails will be sent to real recipients
            </Text>
            
            <View style={styles.testEmailForm}>
              <FormInput
                label="Email Address"
                value={testEmailAddress}
                onChangeText={setTestEmailAddress}
                placeholder="test@example.com"
                autoCapitalize="none"
                keyboardType="email-address"
                style={{ marginBottom: 16 }}
              />
              
              <FormInput
                label="First Name (Optional)"
                value={testEmailName}
                onChangeText={setTestEmailName}
                placeholder="Test User"
                style={{ marginBottom: 20 }}
              />
              
              {/* Welcome Email Test */}
              <View style={styles.emailTestSection}>
                <Text style={[styles.emailTestTitle, { color: palette.text || 'white' }]}>Welcome Email</Text>
                <TouchableOpacity
                  style={[styles.testEmailButton, { backgroundColor: palette.primary || '#667eea', opacity: testEmailLoading ? 0.6 : 1 }]}
                  onPress={() => handleSendTestEmail('welcome')}
                  disabled={testEmailLoading}
                >
                  {testEmailLoading ? (
                    <ActivityIndicator size="small" color="white" />
                  ) : (
                    <Text style={[styles.testEmailButtonText, { color: 'white' }]}>
                      Send Welcome Email
                    </Text>
                  )}
                </TouchableOpacity>
              </View>

              {/* Progress Report Tests */}
              <View style={styles.emailTestSection}>
                <Text style={[styles.emailTestTitle, { color: palette.text || 'white' }]}>Progress Report Email</Text>
                <Text style={[styles.emailTestSubtitle, { color: palette.mutedText || 'rgba(255,255,255,0.70)' }]}>
                  Select frequency to test different progress report formats:
                </Text>
                
                <View style={styles.frequencySelector}>
                  {['daily', 'weekly', 'monthly'].map((freq) => (
                    <TouchableOpacity
                      key={freq}
                      style={[
                        styles.frequencyButton,
                        { 
                          borderColor: palette.border || 'rgba(255,255,255,0.3)',
                          backgroundColor: progressFrequency === freq ? (palette.primary || '#667eea') + '20' : 'transparent'
                        },
                        progressFrequency === freq && { borderColor: palette.primary || '#667eea' }
                      ]}
                      onPress={() => setProgressFrequency(freq)}
                    >
                      <Text style={[
                        styles.frequencyButtonText, 
                        { color: progressFrequency === freq ? (palette.primary || '#667eea') : (palette.text || 'white') }
                      ]}>
                        {freq.charAt(0).toUpperCase() + freq.slice(1)}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
                
                <TouchableOpacity
                  style={[styles.testEmailButton, { backgroundColor: '#f093fb', opacity: testEmailLoading ? 0.6 : 1 }]}
                  onPress={() => handleSendTestEmail('progress', progressFrequency)}
                  disabled={testEmailLoading}
                >
                  {testEmailLoading ? (
                    <ActivityIndicator size="small" color="white" />
                  ) : (
                    <Text style={[styles.testEmailButtonText, { color: 'white' }]}>
                      Send {progressFrequency.charAt(0).toUpperCase() + progressFrequency.slice(1)} Report
                    </Text>
                  )}
                </TouchableOpacity>
              </View>
              
              {/* Network Test Button */}
              <TouchableOpacity
                style={[styles.testEmailButton, { backgroundColor: '#28a745', marginBottom: 10 }]}
                onPress={testNetworkConnection}
              >
                <Text style={[styles.testEmailButtonText, { color: 'white' }]}>
                  Test Network Connection
                </Text>
              </TouchableOpacity>

              <Text style={[styles.testEmailNote, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>
                Production mode: Emails will be sent to real addresses. Use your own email for testing.
              </Text>
            </View>
          </View>
        </>
      )}
    </View>
  );

  const renderWidgetsTab = () => {
    if (showWidgetForm && editingWidget) {
      return (
        <View style={styles.tabContent}>
          <View style={styles.formHeader}>
            <Text style={[styles.tabTitle, { color: palette.text || 'white' }]}>
              {editingWidget.id ? 'Edit Widget' : 'Create New Widget'}
            </Text>
            <TouchableOpacity
              onPress={() => {
                setShowWidgetForm(false);
                setEditingWidget(null);
              }}
              style={[styles.closeButton, { borderColor: palette.border || 'rgba(255,255,255,0.2)' }]}
            >
              <Text style={[styles.closeButtonText, { color: palette.text || 'white' }]}>✕ Cancel</Text>
            </TouchableOpacity>
          </View>

          <AnimatedCard style={[styles.section, { borderTopColor: palette.border || 'rgba(255,255,255,0.16)', backgroundColor: palette.surface }]} delay={0}>
            <Text style={[styles.sectionTitle, { color: palette.text || 'white' }]}>Widget Details</Text>
            
            <View style={styles.formGroup}>
              <Text style={[styles.label, { color: palette.text || 'white' }]}>Widget Type *</Text>
              <Text style={[styles.helperText, { color: palette.mutedText || 'rgba(255,255,255,0.50)' }]}>
                Select the type of interactive widget you want to create
              </Text>
              <View style={styles.widgetTypeGrid}>
                <TouchableOpacity
                  style={[
                    styles.widgetTypeCard,
                    { borderColor: palette.border || 'rgba(255,255,255,0.2)' },
                    editingWidget.type === 'spreadsheet' && { 
                      borderColor: palette.primary || '#FF0600',
                      backgroundColor: (palette.primary || '#FF0600') + '15',
                      borderWidth: 3,
                    }
                  ]}
                  onPress={() => setEditingWidget({ ...editingWidget, type: 'spreadsheet' })}
                >
                  <Text style={styles.widgetTypeEmoji}>📊</Text>
                  <Text style={[styles.widgetTypeName, { color: palette.text || 'white' }]}>Spreadsheet</Text>
                  <Text style={[styles.widgetTypeDescription, { color: palette.mutedText || 'rgba(255,255,255,0.50)' }]}>
                    Editable tables with custom columns
                  </Text>
                </TouchableOpacity>
                
                <TouchableOpacity
                  style={[
                    styles.widgetTypeCard,
                    { borderColor: palette.border || 'rgba(255,255,255,0.2)' },
                    editingWidget.type === 'calculator' && { 
                      borderColor: palette.primary || '#FF0600',
                      backgroundColor: (palette.primary || '#FF0600') + '15',
                      borderWidth: 3,
                    }
                  ]}
                  onPress={() => setEditingWidget({ ...editingWidget, type: 'calculator' })}
                >
                  <Text style={styles.widgetTypeEmoji}>🧮</Text>
                  <Text style={[styles.widgetTypeName, { color: palette.text || 'white' }]}>Calculator</Text>
                  <Text style={[styles.widgetTypeDescription, { color: palette.mutedText || 'rgba(255,255,255,0.50)' }]}>
                    Custom formulas & calculations
                  </Text>
                </TouchableOpacity>
                
                <TouchableOpacity
                  style={[
                    styles.widgetTypeCard,
                    { borderColor: palette.border || 'rgba(255,255,255,0.2)' },
                    editingWidget.type === 'quiz' && { 
                      borderColor: palette.primary || '#FF0600',
                      backgroundColor: (palette.primary || '#FF0600') + '15',
                      borderWidth: 3,
                    }
                  ]}
                  onPress={() => setEditingWidget({ ...editingWidget, type: 'quiz' })}
                >
                  <Text style={styles.widgetTypeEmoji}>❓</Text>
                  <Text style={[styles.widgetTypeName, { color: palette.text || 'white' }]}>Quiz</Text>
                  <Text style={[styles.widgetTypeDescription, { color: palette.mutedText || 'rgba(255,255,255,0.50)' }]}>
                    Multiple choice questions with AI feedback
                  </Text>
                </TouchableOpacity>
                
                <TouchableOpacity
                  style={[
                    styles.widgetTypeCard,
                    { borderColor: palette.border || 'rgba(255,255,255,0.2)' },
                    editingWidget.type === 'sequential_quiz' && { 
                      borderColor: palette.primary || '#FF0600',
                      backgroundColor: (palette.primary || '#FF0600') + '15',
                      borderWidth: 3,
                    }
                  ]}
                  onPress={() => setEditingWidget({ ...editingWidget, type: 'sequential_quiz' })}
                >
                  <Text style={styles.widgetTypeEmoji}>🎯</Text>
                  <Text style={[styles.widgetTypeName, { color: palette.text || 'white' }]}>Sequential Quiz</Text>
                  <Text style={[styles.widgetTypeDescription, { color: palette.mutedText || 'rgba(255,255,255,0.50)' }]}>
                    One question at a time with instant feedback
                  </Text>
                </TouchableOpacity>
                
                <TouchableOpacity
                  style={[
                    styles.widgetTypeCard,
                    { borderColor: palette.border || 'rgba(255,255,255,0.2)' },
                    editingWidget.type === 'scorecard' && { 
                      borderColor: palette.primary || '#FF0600',
                      backgroundColor: (palette.primary || '#FF0600') + '15',
                      borderWidth: 3,
                    }
                  ]}
                  onPress={() => setEditingWidget({ ...editingWidget, type: 'scorecard' })}
                >
                  <Text style={styles.widgetTypeEmoji}>📈</Text>
                  <Text style={[styles.widgetTypeName, { color: palette.text || 'white' }]}>Scorecard</Text>
                  <Text style={[styles.widgetTypeDescription, { color: palette.mutedText || 'rgba(255,255,255,0.50)' }]}>
                    Self-assessment with rating scales
                  </Text>
                </TouchableOpacity>
                
                <TouchableOpacity
                  style={[
                    styles.widgetTypeCard,
                    { borderColor: palette.border || 'rgba(255,255,255,0.2)' },
                    editingWidget.type === 'checklist' && { 
                      borderColor: palette.primary || '#FF0600',
                      backgroundColor: (palette.primary || '#FF0600') + '15',
                      borderWidth: 3,
                    }
                  ]}
                  onPress={() => setEditingWidget({ ...editingWidget, type: 'checklist' })}
                >
                  <Text style={styles.widgetTypeEmoji}>✅</Text>
                  <Text style={[styles.widgetTypeName, { color: palette.text || 'white' }]}>Checklist</Text>
                  <Text style={[styles.widgetTypeDescription, { color: palette.mutedText || 'rgba(255,255,255,0.50)' }]}>
                    Track action items & completion
                  </Text>
                </TouchableOpacity>
                
                <TouchableOpacity
                  style={[
                    styles.widgetTypeCard,
                    { borderColor: palette.border || 'rgba(255,255,255,0.2)' },
                    editingWidget.type === 'decision_tree' && { 
                      borderColor: palette.primary || '#FF0600',
                      backgroundColor: (palette.primary || '#FF0600') + '15',
                      borderWidth: 3,
                    }
                  ]}
                  onPress={() => setEditingWidget({ ...editingWidget, type: 'decision_tree' })}
                >
                  <Text style={styles.widgetTypeEmoji}>🌳</Text>
                  <Text style={[styles.widgetTypeName, { color: palette.text || 'white' }]}>Decision Tree</Text>
                  <Text style={[styles.widgetTypeDescription, { color: palette.mutedText || 'rgba(255,255,255,0.50)' }]}>
                    Yes/no flow with guided outcomes
                  </Text>
                </TouchableOpacity>
                
                <TouchableOpacity
                  style={[
                    styles.widgetTypeCard,
                    { borderColor: palette.border || 'rgba(255,255,255,0.2)' },
                    editingWidget.type === 'timeline' && { 
                      borderColor: palette.primary || '#FF0600',
                      backgroundColor: (palette.primary || '#FF0600') + '15',
                      borderWidth: 3,
                    }
                  ]}
                  onPress={() => setEditingWidget({ ...editingWidget, type: 'timeline' })}
                >
                  <Text style={styles.widgetTypeEmoji}>⏱️</Text>
                  <Text style={[styles.widgetTypeName, { color: palette.text || 'white' }]}>Timeline</Text>
                  <Text style={[styles.widgetTypeDescription, { color: palette.mutedText || 'rgba(255,255,255,0.50)' }]}>
                    Chronological milestones & events
                  </Text>
                </TouchableOpacity>
                
                <TouchableOpacity
                  style={[
                    styles.widgetTypeCard,
                    { borderColor: palette.border || 'rgba(255,255,255,0.2)' },
                    editingWidget.type === 'comparison' && { 
                      borderColor: palette.primary || '#FF0600',
                      backgroundColor: (palette.primary || '#FF0600') + '15',
                      borderWidth: 3,
                    }
                  ]}
                  onPress={() => setEditingWidget({ ...editingWidget, type: 'comparison' })}
                >
                  <Text style={styles.widgetTypeEmoji}>⚖️</Text>
                  <Text style={[styles.widgetTypeName, { color: palette.text || 'white' }]}>Comparison</Text>
                  <Text style={[styles.widgetTypeDescription, { color: palette.mutedText || 'rgba(255,255,255,0.50)' }]}>
                    Side-by-side option evaluation
                  </Text>
                </TouchableOpacity>
                
                <TouchableOpacity
                  style={[
                    styles.widgetTypeCard,
                    { borderColor: palette.border || 'rgba(255,255,255,0.2)' },
                    editingWidget.type === 'matching' && { 
                      borderColor: palette.primary || '#FF0600',
                      backgroundColor: (palette.primary || '#FF0600') + '15',
                      borderWidth: 3,
                    }
                  ]}
                  onPress={() => setEditingWidget({ ...editingWidget, type: 'matching' })}
                >
                  <Text style={styles.widgetTypeEmoji}>🎮</Text>
                  <Text style={[styles.widgetTypeName, { color: palette.text || 'white' }]}>Matching Game</Text>
                  <Text style={[styles.widgetTypeDescription, { color: palette.mutedText || 'rgba(255,255,255,0.50)' }]}>
                    Interactive pairs matching activity
                  </Text>
                </TouchableOpacity>
                
                <TouchableOpacity
                  style={[
                    styles.widgetTypeCard,
                    { borderColor: palette.border || 'rgba(255,255,255,0.2)' },
                    editingWidget.type === 'file' && { 
                      borderColor: palette.primary || '#FF0600',
                      backgroundColor: (palette.primary || '#FF0600') + '15',
                      borderWidth: 3,
                    }
                  ]}
                  onPress={() => setEditingWidget({ ...editingWidget, type: 'file' })}
                >
                  <Text style={styles.widgetTypeEmoji}>📁</Text>
                  <Text style={[styles.widgetTypeName, { color: palette.text || 'white' }]}>File Upload</Text>
                  <Text style={[styles.widgetTypeDescription, { color: palette.mutedText || 'rgba(255,255,255,0.50)' }]}>
                    Student submissions & downloads
                  </Text>
                </TouchableOpacity>
              </View>
            </View>

            <View style={styles.formGroup}>
              <Text style={[styles.label, { color: palette.text || 'white' }]}>Widget Title *</Text>
              <TextInput
                style={[styles.textInput, { borderColor: palette.border || 'rgba(255,255,255,0.2)', color: palette.text || 'white' }]}
                value={editingWidget.title}
                onChangeText={(value) => setEditingWidget({ ...editingWidget, title: value })}
                placeholder="e.g., Investment Calculator"
                placeholderTextColor={palette.mutedText || 'rgba(255,255,255,0.4)'}
              />
            </View>

            <View style={styles.formGroup}>
              <Text style={[styles.label, { color: palette.text || 'white' }]}>Description</Text>
              <TextInput
                style={[styles.textInput, styles.textArea, { borderColor: palette.border || 'rgba(255,255,255,0.2)', color: palette.text || 'white' }]}
                value={editingWidget.description}
                onChangeText={(value) => setEditingWidget({ ...editingWidget, description: value })}
                placeholder="Brief description of this widget"
                placeholderTextColor={palette.mutedText || 'rgba(255,255,255,0.4)'}
                multiline
              />
            </View>

            {editingWidget.type === 'spreadsheet' && (
              <>
              <View style={styles.formGroup}>
                  <View style={styles.labelRow}>
                    <Text style={[styles.label, { color: palette.text || 'white' }]}>Spreadsheet Columns</Text>
                    <TouchableOpacity
                      onPress={() => {
                        const columns = editingWidget.config?.columns || [];
                        setEditingWidget({
                          ...editingWidget,
                          config: {
                            ...editingWidget.config,
                            columns: [...columns, { name: '', type: 'text' }]
                          }
                        });
                      }}
                      style={[styles.addItemButton, { backgroundColor: palette.primary || '#FF0600' }]}
                    >
                      <Text style={[styles.addItemButtonText, { color: '#fff' }]}>+ Add Column</Text>
                    </TouchableOpacity>
                  </View>
                  <Text style={[styles.helperText, { color: palette.mutedText || 'rgba(255,255,255,0.6)', fontSize: 12, marginBottom: 12 }]}>
                    Define the columns that will appear in the spreadsheet
                  </Text>
                  
                  {(editingWidget.config?.columns || []).map((column, index) => (
                    <View key={index} style={[styles.columnRow, { backgroundColor: 'rgba(255,255,255,0.03)', borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                      <View style={{ flex: 1 }}>
                <TextInput
                          style={[styles.textInput, { borderColor: palette.border || 'rgba(255,255,255,0.2)', color: palette.text || 'white', marginBottom: 8 }]}
                          value={column.name || ''}
                  onChangeText={(value) => {
                            const columns = [...(editingWidget.config?.columns || [])];
                            columns[index] = { ...columns[index], name: value };
                            setEditingWidget({
                              ...editingWidget,
                              config: { ...editingWidget.config, columns }
                            });
                          }}
                          placeholder="Column Name (e.g., Revenue, Cost, Profit)"
                  placeholderTextColor={palette.mutedText || 'rgba(255,255,255,0.4)'}
                        />
                        <View style={[styles.selectContainer, { borderColor: palette.border || 'white', marginTop: 0 }]}>
                          <TouchableOpacity
                            style={[styles.selectOption, (!column.type || column.type === 'text') && { backgroundColor: 'rgba(255,255,255,0.1)' }]}
                            onPress={() => {
                              const columns = [...(editingWidget.config?.columns || [])];
                              columns[index] = { ...columns[index], type: 'text' };
                              setEditingWidget({
                                ...editingWidget,
                                config: { ...editingWidget.config, columns }
                              });
                            }}
                          >
                            <Text style={[styles.selectText, { color: palette.text || 'white', fontSize: 12 }]}>Text</Text>
                          </TouchableOpacity>
                          <TouchableOpacity
                            style={[styles.selectOption, column.type === 'number' && { backgroundColor: 'rgba(255,255,255,0.1)' }]}
                            onPress={() => {
                              const columns = [...(editingWidget.config?.columns || [])];
                              columns[index] = { ...columns[index], type: 'number' };
                              setEditingWidget({
                                ...editingWidget,
                                config: { ...editingWidget.config, columns }
                              });
                            }}
                          >
                            <Text style={[styles.selectText, { color: palette.text || 'white', fontSize: 12 }]}>Number</Text>
                          </TouchableOpacity>
                          <TouchableOpacity
                            style={[styles.selectOption, column.type === 'currency' && { backgroundColor: 'rgba(255,255,255,0.1)' }]}
                            onPress={() => {
                              const columns = [...(editingWidget.config?.columns || [])];
                              columns[index] = { ...columns[index], type: 'currency' };
                              setEditingWidget({
                                ...editingWidget,
                                config: { ...editingWidget.config, columns }
                              });
                            }}
                          >
                            <Text style={[styles.selectText, { color: palette.text || 'white', fontSize: 12 }]}>Currency</Text>
                          </TouchableOpacity>
                        </View>
                      </View>
                      <TouchableOpacity
                        onPress={() => {
                          const columns = [...(editingWidget.config?.columns || [])];
                          columns.splice(index, 1);
                          setEditingWidget({
                            ...editingWidget,
                            config: { ...editingWidget.config, columns }
                          });
                        }}
                        style={[styles.deleteButton, { backgroundColor: '#EA806B' }]}
                      >
                        <Text style={[styles.deleteButtonText, { color: '#fff' }]}>✕</Text>
                      </TouchableOpacity>
                    </View>
                  ))}
                  
                  {(!editingWidget.config?.columns || editingWidget.config.columns.length === 0) && (
                    <View style={[styles.emptyState, { backgroundColor: 'rgba(255,255,255,0.03)', borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                      <Text style={[styles.emptyStateText, { color: palette.mutedText }]}>No columns added yet. Click "+ Add Column" to get started.</Text>
                    </View>
                  )}
                </View>

                <View style={styles.formGroup}>
                  <Text style={[styles.label, { color: palette.text || 'white' }]}>Number of Rows</Text>
                  <TextInput
                    style={[styles.textInput, { borderColor: palette.border || 'rgba(255,255,255,0.2)', color: palette.text || 'white' }]}
                    value={editingWidget.config?.rows?.toString() || '5'}
                    onChangeText={(value) => {
                      const numValue = parseInt(value) || 5;
                      setEditingWidget({
                        ...editingWidget,
                        config: { ...editingWidget.config, rows: numValue }
                      });
                    }}
                    placeholder="5"
                    keyboardType="numeric"
                    placeholderTextColor={palette.mutedText || 'rgba(255,255,255,0.4)'}
                />
                <Text style={[styles.helperText, { color: palette.mutedText || 'rgba(255,255,255,0.6)', fontSize: 12, marginTop: 4 }]}>
                    How many rows should the spreadsheet have? (Users can add more)
                </Text>
              </View>
              </>
            )}

            {editingWidget.type === 'quiz' && (
              <View style={styles.formGroup}>
                <View style={styles.labelRow}>
                  <Text style={[styles.label, { color: palette.text || 'white' }]}>Quiz Questions</Text>
                  <TouchableOpacity
                    onPress={() => {
                      const questions = editingWidget.config?.questions || [];
                      setEditingWidget({
                        ...editingWidget,
                        config: {
                          ...editingWidget.config,
                          questions: [...questions, { question: '', answers: ['', '', ''], correctAnswer: 0 }]
                        }
                      });
                    }}
                    style={[styles.addItemButton, { backgroundColor: palette.primary || '#FF0600' }]}
                  >
                    <Text style={[styles.addItemButtonText, { color: '#fff' }]}>+ Add Question</Text>
                  </TouchableOpacity>
                </View>
                <Text style={[styles.helperText, { color: palette.mutedText || 'rgba(255,255,255,0.6)', fontSize: 12, marginBottom: 16 }]}>
                  Create multiple-choice questions for the quiz
                </Text>
                
                {(editingWidget.config?.questions || []).map((q, qIndex) => (
                  <View key={qIndex} style={[styles.questionCard, { backgroundColor: 'rgba(255,255,255,0.03)', borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                    <View style={styles.questionHeader}>
                      <Text style={[styles.questionNumber, { color: palette.text || 'white' }]}>Question {qIndex + 1}</Text>
                      <TouchableOpacity
                        onPress={() => {
                          const questions = [...(editingWidget.config?.questions || [])];
                          questions.splice(qIndex, 1);
                          setEditingWidget({
                            ...editingWidget,
                            config: { ...editingWidget.config, questions }
                          });
                        }}
                        style={[styles.deleteButton, { backgroundColor: '#EA806B' }]}
                      >
                        <Text style={[styles.deleteButtonText, { color: '#fff' }]}>✕</Text>
                      </TouchableOpacity>
                    </View>
                    
                <TextInput
                      style={[styles.textInput, { borderColor: palette.border || 'rgba(255,255,255,0.2)', color: palette.text || 'white', marginBottom: 12 }]}
                      value={q.question || ''}
                  onChangeText={(value) => {
                        const questions = [...(editingWidget.config?.questions || [])];
                        questions[qIndex] = { ...questions[qIndex], question: value };
                        setEditingWidget({
                          ...editingWidget,
                          config: { ...editingWidget.config, questions }
                        });
                      }}
                      placeholder="Enter your question"
                      placeholderTextColor={palette.mutedText || 'rgba(255,255,255,0.4)'}
                      multiline
                    />
                    
                    <Text style={[styles.answersLabel, { color: palette.mutedText || 'rgba(255,255,255,0.8)' }]}>Answer Options:</Text>
                    
                    {(q.answers || []).map((answer, aIndex) => (
                      <View key={aIndex} style={styles.answerRow}>
                        <TouchableOpacity
                          style={[
                            styles.radioButton,
                            { borderColor: palette.border || 'rgba(255,255,255,0.3)' },
                            q.correctAnswer === aIndex && { backgroundColor: '#0DC44A', borderColor: '#0DC44A' }
                          ]}
                          onPress={() => {
                            const questions = [...(editingWidget.config?.questions || [])];
                            questions[qIndex] = { ...questions[qIndex], correctAnswer: aIndex };
                            setEditingWidget({
                              ...editingWidget,
                              config: { ...editingWidget.config, questions }
                            });
                          }}
                        >
                          {q.correctAnswer === aIndex && (
                            <Text style={styles.radioCheck}>✓</Text>
                          )}
                        </TouchableOpacity>
                        
                        <TextInput
                          style={[styles.textInput, { borderColor: palette.border || 'rgba(255,255,255,0.2)', color: palette.text || 'white', flex: 1 }]}
                          value={answer || ''}
                          onChangeText={(value) => {
                            const questions = [...(editingWidget.config?.questions || [])];
                            const answers = [...(questions[qIndex].answers || [])];
                            answers[aIndex] = value;
                            questions[qIndex] = { ...questions[qIndex], answers };
                            setEditingWidget({
                              ...editingWidget,
                              config: { ...editingWidget.config, questions }
                            });
                          }}
                          placeholder={`Answer ${aIndex + 1}`}
                          placeholderTextColor={palette.mutedText || 'rgba(255,255,255,0.4)'}
                        />
                        
                        {aIndex >= 3 && (
                          <TouchableOpacity
                            onPress={() => {
                              const questions = [...(editingWidget.config?.questions || [])];
                              const answers = [...(questions[qIndex].answers || [])];
                              answers.splice(aIndex, 1);
                              // Adjust correctAnswer if needed
                              let correctAnswer = questions[qIndex].correctAnswer;
                              if (correctAnswer >= aIndex) {
                                correctAnswer = Math.max(0, correctAnswer - 1);
                              }
                              questions[qIndex] = { ...questions[qIndex], answers, correctAnswer };
                              setEditingWidget({
                                ...editingWidget,
                                config: { ...editingWidget.config, questions }
                              });
                            }}
                            style={[styles.deleteButton, { backgroundColor: '#EA806B', marginLeft: 8 }]}
                          >
                            <Text style={[styles.deleteButtonText, { color: '#fff' }]}>✕</Text>
                          </TouchableOpacity>
                        )}
                      </View>
                    ))}
                    
                    <TouchableOpacity
                      onPress={() => {
                        const questions = [...(editingWidget.config?.questions || [])];
                        const answers = [...(questions[qIndex].answers || []), ''];
                        questions[qIndex] = { ...questions[qIndex], answers };
                        setEditingWidget({
                          ...editingWidget,
                          config: { ...editingWidget.config, questions }
                        });
                      }}
                      style={[styles.addAnswerButton, { borderColor: palette.border || 'rgba(255,255,255,0.2)' }]}
                    >
                      <Text style={[styles.addAnswerButtonText, { color: palette.text || 'white' }]}>+ Add Answer Option</Text>
                    </TouchableOpacity>
                    
                    <Text style={[styles.helperText, { color: palette.mutedText || 'rgba(255,255,255,0.6)', fontSize: 11, marginTop: 8 }]}>
                      Click the circle to mark the correct answer
                    </Text>
                  </View>
                ))}
                
                {(!editingWidget.config?.questions || editingWidget.config.questions.length === 0) && (
                  <View style={[styles.emptyState, { backgroundColor: 'rgba(255,255,255,0.03)', borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                    <Text style={[styles.emptyStateText, { color: palette.mutedText }]}>No questions added yet. Click "+ Add Question" to get started.</Text>
                  </View>
                )}
              </View>
            )}

             {/* Matching Game Configuration */}
             {editingWidget.type === 'matching' && (
               <View style={styles.formGroup}>
                 <View style={styles.labelRow}>
                   <Text style={[styles.label, { color: palette.text || 'white' }]}>Matching Pairs</Text>
                   <TouchableOpacity
                     onPress={() => {
                       const pairs = editingWidget.config?.pairs || [];
                       setEditingWidget({
                         ...editingWidget,
                         config: {
                           ...editingWidget.config,
                           pairs: [...pairs, { left: '', right: '' }]
                         }
                       });
                     }}
                     style={[styles.addItemButton, { backgroundColor: palette.primary || '#FF0600' }]}
                   >
                     <Text style={[styles.addItemButtonText, { color: '#fff' }]}>+ Add Pair</Text>
                   </TouchableOpacity>
                 </View>
                 <Text style={[styles.helperText, { color: palette.mutedText || 'rgba(255,255,255,0.6)', fontSize: 12, marginBottom: 16 }]}>
                   Create pairs of items that students will match together
                 </Text>
                 
                 {(editingWidget.config?.pairs || []).map((pair, pairIndex) => (
                   <View key={pairIndex} style={[styles.questionCard, { backgroundColor: 'rgba(255,255,255,0.03)', borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                     <View style={styles.questionHeader}>
                       <Text style={[styles.questionNumber, { color: palette.text || 'white' }]}>Pair {pairIndex + 1}</Text>
                       <TouchableOpacity
                         onPress={() => {
                           const pairs = [...(editingWidget.config?.pairs || [])];
                           pairs.splice(pairIndex, 1);
                           setEditingWidget({
                             ...editingWidget,
                             config: { ...editingWidget.config, pairs }
                           });
                         }}
                         style={[styles.deleteButton, { backgroundColor: '#EA806B' }]}
                       >
                         <Text style={[styles.deleteButtonText, { color: '#fff' }]}>✕</Text>
                       </TouchableOpacity>
                     </View>
                     
                     <View style={{ flexDirection: 'row', gap: 12, marginTop: 8 }}>
                       <View style={{ flex: 1 }}>
                         <Text style={[styles.label, { color: palette.mutedText || 'rgba(255,255,255,0.8)', fontSize: 12, marginBottom: 6 }]}>Left Item</Text>
                         <TextInput
                           style={[styles.textInput, { borderColor: palette.border || 'rgba(255,255,255,0.2)', color: palette.text || 'white' }]}
                           value={pair.left || ''}
                           onChangeText={(value) => {
                             const pairs = [...(editingWidget.config?.pairs || [])];
                             pairs[pairIndex] = { ...pairs[pairIndex], left: value };
                             setEditingWidget({
                               ...editingWidget,
                               config: { ...editingWidget.config, pairs }
                             });
                           }}
                           placeholder="e.g., Revenue"
                           placeholderTextColor={palette.mutedText || 'rgba(255,255,255,0.4)'}
                         />
                       </View>
                       
                       <View style={{ alignItems: 'center', justifyContent: 'center', paddingTop: 20 }}>
                         <Text style={{ color: palette.mutedText, fontSize: 20 }}>↔</Text>
                       </View>
                       
                       <View style={{ flex: 1 }}>
                         <Text style={[styles.label, { color: palette.mutedText || 'rgba(255,255,255,0.8)', fontSize: 12, marginBottom: 6 }]}>Right Item</Text>
                         <TextInput
                           style={[styles.textInput, { borderColor: palette.border || 'rgba(255,255,255,0.2)', color: palette.text || 'white' }]}
                           value={pair.right || ''}
                           onChangeText={(value) => {
                             const pairs = [...(editingWidget.config?.pairs || [])];
                             pairs[pairIndex] = { ...pairs[pairIndex], right: value };
                             setEditingWidget({
                               ...editingWidget,
                               config: { ...editingWidget.config, pairs }
                             });
                           }}
                           placeholder="e.g., Total income generated"
                           placeholderTextColor={palette.mutedText || 'rgba(255,255,255,0.4)'}
                         />
                       </View>
                     </View>
                   </View>
                 ))}
                 
                 {(!editingWidget.config?.pairs || editingWidget.config.pairs.length === 0) && (
                   <View style={[styles.emptyState, { backgroundColor: 'rgba(255,255,255,0.03)', borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                     <Text style={[styles.emptyStateText, { color: palette.mutedText }]}>No pairs added yet. Click "+ Add Pair" to get started.</Text>
                   </View>
                 )}
               </View>
             )}

             {/* File Widget Configuration */}
             {editingWidget.type === 'file' && (
              <View style={styles.formGroup}>
                <View style={styles.labelRow}>
                  <Text style={[styles.label, { color: palette.text || 'white' }]}>Downloadable Files</Text>
                  {Platform.OS === 'web' && (
                    <label htmlFor="file-widget-upload">
                      <input
                        id="file-widget-upload"
                        type="file"
                        multiple
                        onChange={async (e) => {
                          const files = Array.from(e.target.files);
                          if (files.length === 0) return;

                          setLoading(true);
                          const uploadedFiles = [];

                          for (const file of files) {
                            try {
                              const path = `course_files/${Date.now()}_${file.name}`;
                              const storageRef = ref(storage, path);
                              const snapshot = await uploadBytes(storageRef, file);
                              const url = await getDownloadURL(snapshot.ref);

                              uploadedFiles.push({
                                name: file.name,
                                url,
                                path,
                                size: file.size,
                                type: file.type,
                              });
                            } catch (error) {
                              console.error('Error uploading file:', error);
                              Alert.alert('Error', `Failed to upload ${file.name}`);
                            }
                          }

                          const existingFiles = editingWidget.config?.files || [];
                          setEditingWidget({
                            ...editingWidget,
                            config: {
                              ...editingWidget.config,
                              files: [...existingFiles, ...uploadedFiles],
                            },
                          });
                          setLoading(false);
                          e.target.value = ''; // Reset input
                        }}
                        style={{ display: 'none' }}
                      />
                      <TouchableOpacity
                        style={[styles.addItemButton, { backgroundColor: palette.primary || '#FF0600' }]}
                        onPress={() => document.getElementById('file-widget-upload').click()}
                      >
                        <Text style={[styles.addItemButtonText, { color: '#fff' }]}>+ Upload Files</Text>
                      </TouchableOpacity>
                    </label>
                  )}
                </View>
                <Text style={[styles.helperText, { color: palette.mutedText || 'rgba(255,255,255,0.6)', fontSize: 12, marginBottom: 12 }]}>
                  Upload files that students can download (PDFs, worksheets, templates, etc.)
                </Text>

                {(editingWidget.config?.files || []).map((file, index) => (
                  <View key={index} style={[styles.columnRow, { backgroundColor: 'rgba(255,255,255,0.03)', borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                    <View style={{ flex: 1 }}>
                      <Text style={[styles.label, { color: palette.text || 'white', fontSize: 14, marginBottom: 4 }]}>
                        {file.name}
                      </Text>
                      <Text style={[styles.helperText, { color: palette.mutedText, fontSize: 12 }]}>
                        {file.size ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : 'Unknown size'}
                      </Text>
                    </View>
                    <TouchableOpacity
                      onPress={async () => {
                        try {
                          // Delete from storage
                          if (file.path) {
                            const storageRef = ref(storage, file.path);
                            await deleteObject(storageRef);
                          }
                        } catch (error) {
                          console.error('Error deleting file:', error);
                        }

                        // Remove from widget config
                        const files = [...(editingWidget.config?.files || [])];
                        files.splice(index, 1);
                        setEditingWidget({
                          ...editingWidget,
                          config: { ...editingWidget.config, files },
                        });
                      }}
                      style={[styles.deleteButton, { backgroundColor: '#EA806B', marginLeft: 12 }]}
                    >
                      <Text style={[styles.deleteButtonText, { color: '#fff' }]}>✕</Text>
                    </TouchableOpacity>
                  </View>
                ))}

                {(!editingWidget.config?.files || editingWidget.config.files.length === 0) && (
                  <View style={[styles.emptyState, { backgroundColor: 'rgba(255,255,255,0.03)', borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                    <Text style={[styles.emptyStateText, { color: palette.mutedText }]}>No files uploaded yet. Click "+ Upload Files" to add downloadable files.</Text>
                  </View>
                )}

                <View style={[styles.formGroup, { marginTop: 20 }]}>
                  <View style={styles.labelRow}>
                    <View>
                      <Text style={[styles.label, { color: palette.text || 'white' }]}>Allow Student Uploads</Text>
                      <Text style={[styles.helperText, { color: palette.mutedText || 'rgba(255,255,255,0.6)', fontSize: 12, marginTop: 2 }]}>
                        Enable students to upload their completed work (assignments, homework, etc.)
                      </Text>
                    </View>
                    <TouchableOpacity
                      onPress={() => {
                        setEditingWidget({
                          ...editingWidget,
                          config: {
                            ...editingWidget.config,
                            allowUploads: !editingWidget.config?.allowUploads,
                          },
                        });
                      }}
                      style={[
                        styles.toggleButton,
                        { backgroundColor: editingWidget.config?.allowUploads ? palette.primary : 'rgba(255,255,255,0.1)' },
                      ]}
                    >
                      <Text style={[styles.toggleButtonText, { color: editingWidget.config?.allowUploads ? '#fff' : palette.text }]}>
                        {editingWidget.config?.allowUploads ? 'Enabled' : 'Disabled'}
                      </Text>
                    </TouchableOpacity>
                  </View>
                </View>

                {/* AI Feedback Configuration */}
                {editingWidget.config?.allowUploads && (
                  <>
                    <View style={[styles.formGroup, { marginTop: 20 }]}>
                      <View style={styles.labelRow}>
                        <View>
                          <Text style={[styles.label, { color: palette.text || 'white' }]}>Enable AI Feedback</Text>
                          <Text style={[styles.helperText, { color: palette.mutedText || 'rgba(255,255,255,0.6)', fontSize: 12, marginTop: 2 }]}>
                            Automatically generate AI-powered feedback when students upload files
                          </Text>
                        </View>
                        <TouchableOpacity
                          onPress={() => {
                            setEditingWidget({
                              ...editingWidget,
                              config: {
                                ...editingWidget.config,
                                aiEnabled: !editingWidget.config?.aiEnabled,
                              },
                            });
                          }}
                          style={[
                            styles.toggleButton,
                            { backgroundColor: editingWidget.config?.aiEnabled ? palette.primary : 'rgba(255,255,255,0.1)' },
                          ]}
                        >
                          <Text style={[styles.toggleButtonText, { color: editingWidget.config?.aiEnabled ? '#fff' : palette.text }]}>
                            {editingWidget.config?.aiEnabled ? 'Enabled' : 'Disabled'}
                          </Text>
                        </TouchableOpacity>
                      </View>
                    </View>

                    {editingWidget.config?.aiEnabled && (
                      <View style={styles.formGroup}>
                        <Text style={[styles.label, { color: palette.text || 'white' }]}>AI Feedback Prompt *</Text>
                        <TextInput
                          style={[styles.textInput, styles.textArea, { borderColor: palette.border || 'rgba(255,255,255,0.2)', color: palette.text || 'white' }]}
                          value={editingWidget.config?.aiPrompt || ''}
                          onChangeText={(value) => setEditingWidget({
                            ...editingWidget,
                            config: {
                              ...editingWidget.config,
                              aiPrompt: value,
                            },
                          })}
                          placeholder="e.g., Review this business plan and provide feedback on: 1) Market analysis, 2) Financial projections, 3) Marketing strategy. Be specific and constructive."
                  placeholderTextColor={palette.mutedText || 'rgba(255,255,255,0.4)'}
                  multiline
                          numberOfLines={6}
                />
                <Text style={[styles.helperText, { color: palette.mutedText || 'rgba(255,255,255,0.6)', fontSize: 12, marginTop: 4 }]}>
                          This prompt tells the AI what to look for and how to provide feedback. Be specific about what aspects to review.
                        </Text>
                        <View style={[styles.exampleBox, { backgroundColor: 'rgba(13, 196, 74, 0.1)', borderColor: '#0DC44A', marginTop: 12 }]}>
                          <Text style={[styles.exampleLabel, { color: '#0DC44A' }]}>Example Prompts</Text>
                          <Text style={[styles.exampleText, { color: palette.mutedText, fontSize: 13 }]}>
                            • "Grade this assignment out of 100 and provide detailed feedback on strengths and areas for improvement."
                            {'\n\n'}
                            • "Review this essay for: grammar, structure, argument clarity, and evidence quality. Provide specific examples."
                            {'\n\n'}
                            • "Evaluate this financial model for accuracy, completeness, and realistic assumptions. Suggest improvements."
                </Text>
                        </View>
                      </View>
                    )}
                  </>
                )}
              </View>
            )}

            {/* Calculator Widget Configuration */}
            {editingWidget.type === 'calculator' && (
              <View style={styles.formGroup}>
                <View style={styles.labelRow}>
                  <Text style={[styles.label, { color: palette.text || 'white' }]}>Calculator Fields</Text>
                  <TouchableOpacity
                    onPress={() => {
                      const fields = editingWidget.config?.fields || [];
                      setEditingWidget({
                        ...editingWidget,
                        config: {
                          ...editingWidget.config,
                          fields: [...fields, { name: '', label: '', type: 'number', formula: '' }]
                        }
                      });
                    }}
                    style={[styles.addItemButton, { backgroundColor: palette.primary || '#FF0600' }]}
                  >
                    <Text style={[styles.addItemButtonText, { color: '#fff' }]}>+ Add Field</Text>
                  </TouchableOpacity>
                </View>
                <Text style={[styles.helperText, { color: palette.mutedText || 'rgba(255,255,255,0.6)', fontSize: 12, marginBottom: 16 }]}>
                  Create input fields and calculated results (e.g., ROI calculator, break-even analysis)
                </Text>
                
                {(editingWidget.config?.fields || []).map((field, index) => (
                  <View key={index} style={[styles.questionCard, { backgroundColor: 'rgba(255,255,255,0.03)', borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                    <View style={styles.questionHeader}>
                      <Text style={[styles.questionNumber, { color: palette.text || 'white' }]}>Field {index + 1}</Text>
                      <TouchableOpacity
                        onPress={() => {
                          const fields = [...(editingWidget.config?.fields || [])];
                          fields.splice(index, 1);
                          setEditingWidget({
                            ...editingWidget,
                            config: { ...editingWidget.config, fields }
                          });
                        }}
                        style={[styles.deleteButton, { backgroundColor: '#EA806B' }]}
                      >
                        <Text style={[styles.deleteButtonText, { color: '#fff' }]}>✕</Text>
                      </TouchableOpacity>
                    </View>
                    
                    <TextInput
                      style={[styles.textInput, { borderColor: palette.border || 'rgba(255,255,255,0.2)', color: palette.text || 'white', marginTop: 8 }]}
                      value={field.label || ''}
                      onChangeText={(value) => {
                        const fields = [...(editingWidget.config?.fields || [])];
                        fields[index] = { ...fields[index], label: value, name: value.toLowerCase().replace(/\s+/g, '_') };
                        setEditingWidget({
                          ...editingWidget,
                          config: { ...editingWidget.config, fields }
                        });
                      }}
                      placeholder="Field Label (e.g., Initial Investment)"
                      placeholderTextColor={palette.mutedText || 'rgba(255,255,255,0.4)'}
                    />
                    
                    <View style={{ flexDirection: 'row', gap: 8, marginTop: 8 }}>
                      <TouchableOpacity
                        style={[styles.selectOption, field.type === 'number' && { backgroundColor: palette.primary || '#FF0600' }]}
                        onPress={() => {
                          const fields = [...(editingWidget.config?.fields || [])];
                          fields[index] = { ...fields[index], type: 'number' };
                          setEditingWidget({
                            ...editingWidget,
                            config: { ...editingWidget.config, fields }
                          });
                        }}
                      >
                        <Text style={[styles.selectText, { color: field.type === 'number' ? '#fff' : (palette.text || 'white'), fontSize: 12 }]}>Number Input</Text>
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={[styles.selectOption, field.type === 'calculated' && { backgroundColor: palette.primary || '#FF0600' }]}
                        onPress={() => {
                          const fields = [...(editingWidget.config?.fields || [])];
                          fields[index] = { ...fields[index], type: 'calculated' };
                          setEditingWidget({
                            ...editingWidget,
                            config: { ...editingWidget.config, fields }
                          });
                        }}
                      >
                        <Text style={[styles.selectText, { color: field.type === 'calculated' ? '#fff' : (palette.text || 'white'), fontSize: 12 }]}>Calculated Result</Text>
                      </TouchableOpacity>
                    </View>
                    
                    {field.type === 'calculated' && (
                      <TextInput
                        style={[styles.textInput, { borderColor: palette.border || 'rgba(255,255,255,0.2)', color: palette.text || 'white', marginTop: 8 }]}
                        value={field.formula || ''}
                        onChangeText={(value) => {
                          const fields = [...(editingWidget.config?.fields || [])];
                          fields[index] = { ...fields[index], formula: value };
                          setEditingWidget({
                            ...editingWidget,
                            config: { ...editingWidget.config, fields }
                          });
                        }}
                        placeholder="Formula (e.g., revenue - costs)"
                        placeholderTextColor={palette.mutedText || 'rgba(255,255,255,0.4)'}
                      />
                    )}
                  </View>
                ))}
                
                {(!editingWidget.config?.fields || editingWidget.config.fields.length === 0) && (
                  <View style={[styles.emptyState, { backgroundColor: 'rgba(255,255,255,0.03)', borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                    <Text style={[styles.emptyStateText, { color: palette.mutedText }]}>No fields added yet. Click "+ Add Field" to get started.</Text>
                  </View>
                )}
              </View>
            )}

            {/* Scorecard Widget Configuration */}
            {editingWidget.type === 'scorecard' && (
              <View style={styles.formGroup}>
                <View style={styles.labelRow}>
                  <Text style={[styles.label, { color: palette.text || 'white' }]}>Assessment Criteria</Text>
                  <TouchableOpacity
                    onPress={() => {
                      const criteria = editingWidget.config?.criteria || [];
                      setEditingWidget({
                        ...editingWidget,
                        config: {
                          ...editingWidget.config,
                          criteria: [...criteria, { statement: '', minScore: 1, maxScore: 10 }]
                        }
                      });
                    }}
                    style={[styles.addItemButton, { backgroundColor: palette.primary || '#FF0600' }]}
                  >
                    <Text style={[styles.addItemButtonText, { color: '#fff' }]}>+ Add Criterion</Text>
                  </TouchableOpacity>
                </View>
                <Text style={[styles.helperText, { color: palette.mutedText || 'rgba(255,255,255,0.6)', fontSize: 12, marginBottom: 16 }]}>
                  Create self-assessment questions that students rate themselves on
                </Text>
                
                {(editingWidget.config?.criteria || []).map((criterion, index) => (
                  <View key={index} style={[styles.questionCard, { backgroundColor: 'rgba(255,255,255,0.03)', borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                    <View style={styles.questionHeader}>
                      <Text style={[styles.questionNumber, { color: palette.text || 'white' }]}>Criterion {index + 1}</Text>
                      <TouchableOpacity
                        onPress={() => {
                          const criteria = [...(editingWidget.config?.criteria || [])];
                          criteria.splice(index, 1);
                          setEditingWidget({
                            ...editingWidget,
                            config: { ...editingWidget.config, criteria }
                          });
                        }}
                        style={[styles.deleteButton, { backgroundColor: '#EA806B' }]}
                      >
                        <Text style={[styles.deleteButtonText, { color: '#fff' }]}>✕</Text>
                      </TouchableOpacity>
                    </View>
                    
                    <TextInput
                      style={[styles.textInput, { borderColor: palette.border || 'rgba(255,255,255,0.2)', color: palette.text || 'white', marginTop: 8 }]}
                      value={criterion.statement || ''}
                      onChangeText={(value) => {
                        const criteria = [...(editingWidget.config?.criteria || [])];
                        criteria[index] = { ...criteria[index], statement: value };
                        setEditingWidget({
                          ...editingWidget,
                          config: { ...editingWidget.config, criteria }
                        });
                      }}
                      placeholder="Statement to rate (e.g., I am ready to take calculated risks)"
                      placeholderTextColor={palette.mutedText || 'rgba(255,255,255,0.4)'}
                      multiline
                    />
                    
                    <View style={{ flexDirection: 'row', gap: 12, marginTop: 8 }}>
                      <View style={{ flex: 1 }}>
                        <Text style={[styles.label, { color: palette.mutedText || 'rgba(255,255,255,0.8)', fontSize: 12, marginBottom: 6 }]}>Min Score</Text>
                        <TextInput
                          style={[styles.textInput, { borderColor: palette.border || 'rgba(255,255,255,0.2)', color: palette.text || 'white' }]}
                          value={String(criterion.minScore || 1)}
                          onChangeText={(value) => {
                            const criteria = [...(editingWidget.config?.criteria || [])];
                            criteria[index] = { ...criteria[index], minScore: parseInt(value) || 1 };
                            setEditingWidget({
                              ...editingWidget,
                              config: { ...editingWidget.config, criteria }
                            });
                          }}
                          keyboardType="numeric"
                          placeholder="1"
                          placeholderTextColor={palette.mutedText || 'rgba(255,255,255,0.4)'}
                        />
                      </View>
                      
                      <View style={{ flex: 1 }}>
                        <Text style={[styles.label, { color: palette.mutedText || 'rgba(255,255,255,0.8)', fontSize: 12, marginBottom: 6 }]}>Max Score</Text>
                        <TextInput
                          style={[styles.textInput, { borderColor: palette.border || 'rgba(255,255,255,0.2)', color: palette.text || 'white' }]}
                          value={String(criterion.maxScore || 10)}
                          onChangeText={(value) => {
                            const criteria = [...(editingWidget.config?.criteria || [])];
                            criteria[index] = { ...criteria[index], maxScore: parseInt(value) || 10 };
                            setEditingWidget({
                              ...editingWidget,
                              config: { ...editingWidget.config, criteria }
                            });
                          }}
                          keyboardType="numeric"
                          placeholder="10"
                          placeholderTextColor={palette.mutedText || 'rgba(255,255,255,0.4)'}
                        />
                      </View>
                    </View>
                  </View>
                ))}
                
                {(!editingWidget.config?.criteria || editingWidget.config.criteria.length === 0) && (
                  <View style={[styles.emptyState, { backgroundColor: 'rgba(255,255,255,0.03)', borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                    <Text style={[styles.emptyStateText, { color: palette.mutedText }]}>No criteria added yet. Click "+ Add Criterion" to get started.</Text>
                  </View>
                )}
              </View>
            )}

            {/* Checklist Widget Configuration */}
            {editingWidget.type === 'checklist' && (
              <View style={styles.formGroup}>
                <View style={styles.labelRow}>
                  <Text style={[styles.label, { color: palette.text || 'white' }]}>Checklist Items</Text>
                  <TouchableOpacity
                    onPress={() => {
                      const items = editingWidget.config?.items || [];
                      setEditingWidget({
                        ...editingWidget,
                        config: {
                          ...editingWidget.config,
                          items: [...items, { text: '', description: '' }]
                        }
                      });
                    }}
                    style={[styles.addItemButton, { backgroundColor: palette.primary || '#FF0600' }]}
                  >
                    <Text style={[styles.addItemButtonText, { color: '#fff' }]}>+ Add Item</Text>
                  </TouchableOpacity>
                </View>
                <Text style={[styles.helperText, { color: palette.mutedText || 'rgba(255,255,255,0.6)', fontSize: 12, marginBottom: 16 }]}>
                  Create action items for students to complete and check off
                </Text>
                
                {(editingWidget.config?.items || []).map((item, index) => (
                  <View key={index} style={[styles.questionCard, { backgroundColor: 'rgba(255,255,255,0.03)', borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                    <View style={styles.questionHeader}>
                      <Text style={[styles.questionNumber, { color: palette.text || 'white' }]}>Item {index + 1}</Text>
                      <TouchableOpacity
                        onPress={() => {
                          const items = [...(editingWidget.config?.items || [])];
                          items.splice(index, 1);
                          setEditingWidget({
                            ...editingWidget,
                            config: { ...editingWidget.config, items }
                          });
                        }}
                        style={[styles.deleteButton, { backgroundColor: '#EA806B' }]}
                      >
                        <Text style={[styles.deleteButtonText, { color: '#fff' }]}>✕</Text>
                      </TouchableOpacity>
                    </View>
                    
                    <TextInput
                      style={[styles.textInput, { borderColor: palette.border || 'rgba(255,255,255,0.2)', color: palette.text || 'white', marginTop: 8 }]}
                      value={item.text || ''}
                      onChangeText={(value) => {
                        const items = [...(editingWidget.config?.items || [])];
                        items[index] = { ...items[index], text: value };
                        setEditingWidget({
                          ...editingWidget,
                          config: { ...editingWidget.config, items }
                        });
                      }}
                      placeholder="Action item (e.g., Define target market)"
                      placeholderTextColor={palette.mutedText || 'rgba(255,255,255,0.4)'}
                    />
                    
                    <TextInput
                      style={[styles.textInput, { borderColor: palette.border || 'rgba(255,255,255,0.2)', color: palette.text || 'white', marginTop: 8 }]}
                      value={item.description || ''}
                      onChangeText={(value) => {
                        const items = [...(editingWidget.config?.items || [])];
                        items[index] = { ...items[index], description: value };
                        setEditingWidget({
                          ...editingWidget,
                          config: { ...editingWidget.config, items }
                        });
                      }}
                      placeholder="Description (optional)"
                      placeholderTextColor={palette.mutedText || 'rgba(255,255,255,0.4)'}
                      multiline
                    />
                  </View>
                ))}
                
                {(!editingWidget.config?.items || editingWidget.config.items.length === 0) && (
                  <View style={[styles.emptyState, { backgroundColor: 'rgba(255,255,255,0.03)', borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                    <Text style={[styles.emptyStateText, { color: palette.mutedText }]}>No items added yet. Click "+ Add Item" to get started.</Text>
                  </View>
                )}
              </View>
            )}

            {/* Decision Tree Widget Configuration */}
            {editingWidget.type === 'decision_tree' && (
              <View style={styles.formGroup}>
                <View style={styles.labelRow}>
                  <Text style={[styles.label, { color: palette.text || 'white' }]}>Decision Nodes</Text>
                  <TouchableOpacity
                    onPress={() => {
                      const nodes = editingWidget.config?.nodes || [];
                      setEditingWidget({
                        ...editingWidget,
                        config: {
                          ...editingWidget.config,
                          nodes: [...nodes, { question: '', yesPath: '', noPath: '', yesLabel: 'Yes', noLabel: 'No' }]
                        }
                      });
                    }}
                    style={[styles.addItemButton, { backgroundColor: palette.primary || '#FF0600' }]}
                  >
                    <Text style={[styles.addItemButtonText, { color: '#fff' }]}>+ Add Decision</Text>
                  </TouchableOpacity>
                </View>
                <Text style={[styles.helperText, { color: palette.mutedText || 'rgba(255,255,255,0.6)', fontSize: 12, marginBottom: 16 }]}>
                  Create yes/no questions that guide students through complex decisions
                </Text>
                
                {(editingWidget.config?.nodes || []).map((node, index) => (
                  <View key={index} style={[styles.questionCard, { backgroundColor: 'rgba(255,255,255,0.03)', borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                    <View style={styles.questionHeader}>
                      <Text style={[styles.questionNumber, { color: palette.text || 'white' }]}>Decision {index + 1}</Text>
                      <TouchableOpacity
                        onPress={() => {
                          const nodes = [...(editingWidget.config?.nodes || [])];
                          nodes.splice(index, 1);
                          setEditingWidget({
                            ...editingWidget,
                            config: { ...editingWidget.config, nodes }
                          });
                        }}
                        style={[styles.deleteButton, { backgroundColor: '#EA806B' }]}
                      >
                        <Text style={[styles.deleteButtonText, { color: '#fff' }]}>✕</Text>
                      </TouchableOpacity>
                    </View>
                    
                    <TextInput
                      style={[styles.textInput, { borderColor: palette.border || 'rgba(255,255,255,0.2)', color: palette.text || 'white', marginTop: 8 }]}
                      value={node.question || ''}
                      onChangeText={(value) => {
                        const nodes = [...(editingWidget.config?.nodes || [])];
                        nodes[index] = { ...nodes[index], question: value };
                        setEditingWidget({
                          ...editingWidget,
                          config: { ...editingWidget.config, nodes }
                        });
                      }}
                      placeholder="Question (e.g., Do you have more than $50k to invest?)"
                      placeholderTextColor={palette.mutedText || 'rgba(255,255,255,0.4)'}
                      multiline
                    />
                    
                    <View style={{ flexDirection: 'row', gap: 12, marginTop: 8 }}>
                      <View style={{ flex: 1 }}>
                        <Text style={[styles.label, { color: palette.mutedText || 'rgba(255,255,255,0.8)', fontSize: 12, marginBottom: 6 }]}>If Yes</Text>
                        <TextInput
                          style={[styles.textInput, { borderColor: palette.border || 'rgba(255,255,255,0.2)', color: palette.text || 'white' }]}
                          value={node.yesPath || ''}
                          onChangeText={(value) => {
                            const nodes = [...(editingWidget.config?.nodes || [])];
                            nodes[index] = { ...nodes[index], yesPath: value };
                            setEditingWidget({
                              ...editingWidget,
                              config: { ...editingWidget.config, nodes }
                            });
                          }}
                          placeholder="Recommendation or next step"
                          placeholderTextColor={palette.mutedText || 'rgba(255,255,255,0.4)'}
                          multiline
                        />
                      </View>
                      
                      <View style={{ flex: 1 }}>
                        <Text style={[styles.label, { color: palette.mutedText || 'rgba(255,255,255,0.8)', fontSize: 12, marginBottom: 6 }]}>If No</Text>
                        <TextInput
                          style={[styles.textInput, { borderColor: palette.border || 'rgba(255,255,255,0.2)', color: palette.text || 'white' }]}
                          value={node.noPath || ''}
                          onChangeText={(value) => {
                            const nodes = [...(editingWidget.config?.nodes || [])];
                            nodes[index] = { ...nodes[index], noPath: value };
                            setEditingWidget({
                              ...editingWidget,
                              config: { ...editingWidget.config, nodes }
                            });
                          }}
                          placeholder="Recommendation or next step"
                          placeholderTextColor={palette.mutedText || 'rgba(255,255,255,0.4)'}
                          multiline
                        />
                      </View>
                    </View>
                  </View>
                ))}
                
                {(!editingWidget.config?.nodes || editingWidget.config.nodes.length === 0) && (
                  <View style={[styles.emptyState, { backgroundColor: 'rgba(255,255,255,0.03)', borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                    <Text style={[styles.emptyStateText, { color: palette.mutedText }]}>No decisions added yet. Click "+ Add Decision" to get started.</Text>
                  </View>
                )}
              </View>
            )}

            {/* Timeline Widget Configuration */}
            {editingWidget.type === 'timeline' && (
              <View style={styles.formGroup}>
                <View style={styles.labelRow}>
                  <Text style={[styles.label, { color: palette.text || 'white' }]}>Timeline Milestones</Text>
                  <TouchableOpacity
                    onPress={() => {
                      const milestones = editingWidget.config?.milestones || [];
                      setEditingWidget({
                        ...editingWidget,
                        config: {
                          ...editingWidget.config,
                          milestones: [...milestones, { month: 1, title: '', description: '', tasks: [] }]
                        }
                      });
                    }}
                    style={[styles.addItemButton, { backgroundColor: palette.primary || '#FF0600' }]}
                  >
                    <Text style={[styles.addItemButtonText, { color: '#fff' }]}>+ Add Milestone</Text>
                  </TouchableOpacity>
                </View>
                <Text style={[styles.helperText, { color: palette.mutedText || 'rgba(255,255,255,0.6)', fontSize: 12, marginBottom: 16 }]}>
                  Create a roadmap with key milestones students should hit over time
                </Text>
                
                {(editingWidget.config?.milestones || []).map((milestone, index) => (
                  <View key={index} style={[styles.questionCard, { backgroundColor: 'rgba(255,255,255,0.03)', borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                    <View style={styles.questionHeader}>
                      <Text style={[styles.questionNumber, { color: palette.text || 'white' }]}>Milestone {index + 1}</Text>
                      <TouchableOpacity
                        onPress={() => {
                          const milestones = [...(editingWidget.config?.milestones || [])];
                          milestones.splice(index, 1);
                          setEditingWidget({
                            ...editingWidget,
                            config: { ...editingWidget.config, milestones }
                          });
                        }}
                        style={[styles.deleteButton, { backgroundColor: '#EA806B' }]}
                      >
                        <Text style={[styles.deleteButtonText, { color: '#fff' }]}>✕</Text>
                      </TouchableOpacity>
                    </View>
                    
                    <View style={{ flexDirection: 'row', gap: 12, marginTop: 8 }}>
                      <View style={{ width: 100 }}>
                        <Text style={[styles.label, { color: palette.mutedText || 'rgba(255,255,255,0.8)', fontSize: 12, marginBottom: 6 }]}>Month</Text>
                        <TextInput
                          style={[styles.textInput, { borderColor: palette.border || 'rgba(255,255,255,0.2)', color: palette.text || 'white' }]}
                          value={String(milestone.month || 1)}
                          onChangeText={(value) => {
                            const milestones = [...(editingWidget.config?.milestones || [])];
                            milestones[index] = { ...milestones[index], month: parseInt(value) || 1 };
                            setEditingWidget({
                              ...editingWidget,
                              config: { ...editingWidget.config, milestones }
                            });
                          }}
                          keyboardType="numeric"
                          placeholder="1"
                          placeholderTextColor={palette.mutedText || 'rgba(255,255,255,0.4)'}
                        />
                      </View>
                      
                      <View style={{ flex: 1 }}>
                        <Text style={[styles.label, { color: palette.mutedText || 'rgba(255,255,255,0.8)', fontSize: 12, marginBottom: 6 }]}>Title</Text>
                        <TextInput
                          style={[styles.textInput, { borderColor: palette.border || 'rgba(255,255,255,0.2)', color: palette.text || 'white' }]}
                          value={milestone.title || ''}
                          onChangeText={(value) => {
                            const milestones = [...(editingWidget.config?.milestones || [])];
                            milestones[index] = { ...milestones[index], title: value };
                            setEditingWidget({
                              ...editingWidget,
                              config: { ...editingWidget.config, milestones }
                            });
                          }}
                          placeholder="Milestone title"
                          placeholderTextColor={palette.mutedText || 'rgba(255,255,255,0.4)'}
                        />
                      </View>
                    </View>
                    
                    <TextInput
                      style={[styles.textInput, { borderColor: palette.border || 'rgba(255,255,255,0.2)', color: palette.text || 'white', marginTop: 8 }]}
                      value={milestone.description || ''}
                      onChangeText={(value) => {
                        const milestones = [...(editingWidget.config?.milestones || [])];
                        milestones[index] = { ...milestones[index], description: value };
                        setEditingWidget({
                          ...editingWidget,
                          config: { ...editingWidget.config, milestones }
                        });
                      }}
                      placeholder="Description"
                      placeholderTextColor={palette.mutedText || 'rgba(255,255,255,0.4)'}
                      multiline
                    />
                  </View>
                ))}
                
                {(!editingWidget.config?.milestones || editingWidget.config.milestones.length === 0) && (
                  <View style={[styles.emptyState, { backgroundColor: 'rgba(255,255,255,0.03)', borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                    <Text style={[styles.emptyStateText, { color: palette.mutedText }]}>No milestones added yet. Click "+ Add Milestone" to get started.</Text>
                  </View>
                )}
              </View>
            )}

            {/* Comparison Widget Configuration */}
            {editingWidget.type === 'comparison' && (
              <View style={styles.formGroup}>
                <View style={styles.labelRow}>
                  <Text style={[styles.label, { color: palette.text || 'white' }]}>Comparison Options</Text>
                  <TouchableOpacity
                    onPress={() => {
                      const options = editingWidget.config?.options || [];
                      setEditingWidget({
                        ...editingWidget,
                        config: {
                          ...editingWidget.config,
                          options: [...options, { name: '', criteria: {} }]
                        }
                      });
                    }}
                    style={[styles.addItemButton, { backgroundColor: palette.primary || '#FF0600' }]}
                  >
                    <Text style={[styles.addItemButtonText, { color: '#fff' }]}>+ Add Option</Text>
                  </TouchableOpacity>
                </View>
                <Text style={[styles.helperText, { color: palette.mutedText || 'rgba(255,255,255,0.6)', fontSize: 12, marginBottom: 12 }]}>
                  Create a comparison matrix for evaluating multiple options side-by-side
                </Text>
                
                <TextInput
                  style={[styles.textInput, { borderColor: palette.border || 'rgba(255,255,255,0.2)', color: palette.text || 'white', marginBottom: 16 }]}
                  value={editingWidget.config?.criteriaList?.join(', ') || ''}
                  onChangeText={(value) => {
                    setEditingWidget({
                      ...editingWidget,
                      config: {
                        ...editingWidget.config,
                        criteriaList: value.split(',').map(c => c.trim()).filter(Boolean)
                      }
                    });
                  }}
                  placeholder="Criteria to compare (comma-separated, e.g., Cost, Speed, Quality)"
                  placeholderTextColor={palette.mutedText || 'rgba(255,255,255,0.4)'}
                />
                
                {(editingWidget.config?.options || []).map((option, index) => (
                  <View key={index} style={[styles.questionCard, { backgroundColor: 'rgba(255,255,255,0.03)', borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                    <View style={styles.questionHeader}>
                      <Text style={[styles.questionNumber, { color: palette.text || 'white' }]}>Option {index + 1}</Text>
                      <TouchableOpacity
                        onPress={() => {
                          const options = [...(editingWidget.config?.options || [])];
                          options.splice(index, 1);
                          setEditingWidget({
                            ...editingWidget,
                            config: { ...editingWidget.config, options }
                          });
                        }}
                        style={[styles.deleteButton, { backgroundColor: '#EA806B' }]}
                      >
                        <Text style={[styles.deleteButtonText, { color: '#fff' }]}>✕</Text>
                      </TouchableOpacity>
                    </View>
                    
                    <TextInput
                      style={[styles.textInput, { borderColor: palette.border || 'rgba(255,255,255,0.2)', color: palette.text || 'white', marginTop: 8 }]}
                      value={option.name || ''}
                      onChangeText={(value) => {
                        const options = [...(editingWidget.config?.options || [])];
                        options[index] = { ...options[index], name: value };
                        setEditingWidget({
                          ...editingWidget,
                          config: { ...editingWidget.config, options }
                        });
                      }}
                      placeholder="Option name (e.g., Business Model A)"
                      placeholderTextColor={palette.mutedText || 'rgba(255,255,255,0.4)'}
                    />
                  </View>
                ))}
                
                {(!editingWidget.config?.options || editingWidget.config.options.length === 0) && (
                  <View style={[styles.emptyState, { backgroundColor: 'rgba(255,255,255,0.03)', borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                    <Text style={[styles.emptyStateText, { color: palette.mutedText }]}>No options added yet. Click "+ Add Option" to get started.</Text>
                  </View>
                )}
              </View>
            )}
          </AnimatedCard>

          <View style={styles.formActions}>
            <AnimatedButton
              title={loading ? 'Saving...' : 'Save Widget'}
              onPress={handleSaveWidget}
              disabled={loading}
              loading={loading}
              style={styles.saveButton}
            />
          </View>
        </View>
      );
    }

    // Widget List View
    const widgetTypes = [
      { key: 'all', label: 'All Widgets', icon: '📦' },
      { key: 'spreadsheet', label: 'Spreadsheets', icon: '📊' },
      { key: 'calculator', label: 'Calculators', icon: '🧮' },
      { key: 'quiz', label: 'Quizzes', icon: '❓' },
      { key: 'sequential_quiz', label: 'Sequential Quiz', icon: '🎯' },
      { key: 'scorecard', label: 'Scorecards', icon: '📈' },
      { key: 'checklist', label: 'Checklists', icon: '✅' },
      { key: 'decision_tree', label: 'Decision Trees', icon: '🌳' },
      { key: 'timeline', label: 'Timelines', icon: '⏱️' },
      { key: 'comparison', label: 'Comparisons', icon: '⚖️' },
      { key: 'matching', label: 'Matching', icon: '🎮' },
      { key: 'file', label: 'File Upload', icon: '📁' },
    ];

    const filteredWidgets = selectedWidgetType === 'all' 
      ? widgets 
      : widgets.filter(w => w.type === selectedWidgetType);

    const getWidgetTypeColor = (type) => {
      const colors = {
        spreadsheet: '#2196F3',
        quiz: '#9C27B0',
        sequential_quiz: '#673AB7',
        file: '#FF9800',
        matching: '#4CAF50',
        calculator: '#00BCD4',
        scorecard: '#FFC107',
        checklist: '#4CAF50',
        decision_tree: '#8BC34A',
        timeline: '#3F51B5',
        comparison: '#E91E63',
      };
      return colors[type] || '#666';
    };

    const getWidgetTypeIcon = (type) => {
      const icons = {
        spreadsheet: '📊',
        quiz: '❓',
        sequential_quiz: '🎯',
        file: '📁',
        matching: '🎮',
        calculator: '🧮',
        scorecard: '📈',
        checklist: '✅',
        decision_tree: '🌳',
        timeline: '⏱️',
        comparison: '⚖️',
      };
      return icons[type] || '📦';
    };

    return (
      <View style={styles.tabContent}>
        <View style={styles.formHeader}>
          <View>
            <Text style={[styles.tabTitle, { color: palette.text || 'white' }]}>Widget Management</Text>
            <Text style={[styles.subtitle, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>
              {filteredWidgets.length} of {widgets.length} widgets
            </Text>
          </View>
          <AnimatedButton
            title="+ Create Widget"
            onPress={handleCreateWidget}
            variant="primary"
            style={styles.createButton}
          />
        </View>

        {/* Type Filter Chips */}
        <View style={[styles.filterChipsContainer, { borderBottomColor: palette.border || 'rgba(255,255,255,0.16)' }]}>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.filterChipsContent}>
            {widgetTypes.map((type) => {
              const count = type.key === 'all' ? widgets.length : widgets.filter(w => w.type === type.key).length;
              const isSelected = selectedWidgetType === type.key;
              
              return (
                <TouchableOpacity
                  key={type.key}
                  style={[
                    styles.filterChip,
                    { borderColor: palette.border || 'rgba(255,255,255,0.2)' },
                    isSelected && { 
                      backgroundColor: palette.primary || '#FF0600',
                      borderColor: palette.primary || '#FF0600',
                    }
                  ]}
                  onPress={() => setSelectedWidgetType(type.key)}
                >
                  <Text style={styles.filterChipIcon}>{type.icon}</Text>
                  <Text style={[
                    styles.filterChipText,
                    { color: isSelected ? '#fff' : (palette.text || 'white') }
                  ]}>
                    {type.label}
                  </Text>
                  <View style={[
                    styles.filterChipBadge,
                    { backgroundColor: isSelected ? 'rgba(255,255,255,0.2)' : (palette.surface || 'rgba(0,0,0,0.3)') }
                  ]}>
                    <Text style={[
                      styles.filterChipBadgeText,
                      { color: isSelected ? '#fff' : (palette.mutedText || 'rgba(255,255,255,0.60)') }
                    ]}>
                      {count}
                    </Text>
                  </View>
                </TouchableOpacity>
              );
            })}
          </ScrollView>
        </View>

        {widgetsLoading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={palette.primary || '#FF0600'} />
            <Text style={[styles.loadingText, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>Loading widgets...</Text>
          </View>
        ) : filteredWidgets.length === 0 ? (
          <View style={styles.emptyStateContainer}>
            <Text style={styles.emptyStateIcon}>📦</Text>
            <Text style={[styles.emptyStateTitle, { color: palette.text || 'white' }]}>
              {selectedWidgetType === 'all' ? 'No widgets yet' : `No ${selectedWidgetType} widgets`}
            </Text>
            <Text style={[styles.emptyStateDescription, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>
              {selectedWidgetType === 'all' 
                ? 'Create your first widget to get started!' 
                : 'Try selecting a different widget type or create a new widget.'}
            </Text>
            {selectedWidgetType !== 'all' && (
              <TouchableOpacity onPress={() => setSelectedWidgetType('all')} style={styles.emptyStateButton}>
                <Text style={[styles.emptyStateButtonText, { color: palette.primary || '#FF0600' }]}>View All Widgets</Text>
              </TouchableOpacity>
            )}
          </View>
        ) : (
          <ScrollView style={styles.widgetGrid} contentContainerStyle={styles.widgetGridContent}>
            {filteredWidgets.map((widget, index) => {
              const usage = getWidgetUsage(widget.id);
              const usageCount = usage.length;
              
              return (
                <AnimatedCard 
                  key={widget.id} 
                  delay={index * 50} 
                  style={[
                    styles.widgetCard, 
                    { 
                      borderColor: palette.border || 'rgba(255,255,255,0.16)', 
                      backgroundColor: palette.surface 
                    }
                  ]}
                >
                  {/* Widget Header */}
                  <View style={styles.widgetCardHeader}>
                    <View style={[styles.widgetIconContainer, { backgroundColor: getWidgetTypeColor(widget.type) + '20' }]}>
                      <Text style={styles.widgetIcon}>{getWidgetTypeIcon(widget.type)}</Text>
                    </View>
                    <View style={[styles.widgetTypeBadge, { backgroundColor: getWidgetTypeColor(widget.type) }]}>
                      <Text style={styles.widgetTypeBadgeText}>{widget.type.replace('_', ' ')}</Text>
                    </View>
                  </View>

                  {/* Widget Content */}
                  <View style={styles.widgetCardContent}>
                    <Text style={[styles.widgetCardTitle, { color: palette.text || 'white' }]} numberOfLines={2}>
                      {widget.title}
                    </Text>
                    <Text style={[styles.widgetCardDescription, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]} numberOfLines={3}>
                      {widget.description || 'No description provided'}
                    </Text>
                  </View>

                  {/* Widget Usage Info */}
                  <View style={[styles.widgetUsageSection, { borderTopColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                    <View style={styles.widgetUsageHeader}>
                      <Text style={[styles.widgetUsageTitle, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>
                        📍 Usage
                      </Text>
                      <Text style={[styles.widgetUsageCount, { color: palette.text || 'white' }]}>
                        {usageCount} {usageCount === 1 ? 'place' : 'places'}
                      </Text>
                    </View>
                    
                    {usageCount > 0 ? (
                      <View style={styles.widgetUsageList}>
                        {usage.slice(0, 2).map((use, idx) => (
                          <View key={idx} style={styles.widgetUsageItem}>
                            <Text style={[styles.widgetUsageCourse, { color: palette.text || 'white' }]} numberOfLines={1}>
                              {use.courseTitle}
                            </Text>
                            <Text style={[styles.widgetUsageDetails, { color: palette.mutedText || 'rgba(255,255,255,0.50)' }]} numberOfLines={1}>
                              {use.moduleTitle} • {use.placement}
                            </Text>
                          </View>
                        ))}
                        {usageCount > 2 && (
                          <Text style={[styles.widgetUsageMore, { color: palette.mutedText || 'rgba(255,255,255,0.50)' }]}>
                            + {usageCount - 2} more
                          </Text>
                        )}
                      </View>
                    ) : (
                      <Text style={[styles.widgetUsageEmpty, { color: palette.mutedText || 'rgba(255,255,255,0.40)' }]}>
                        Not used in any courses yet
                      </Text>
                    )}
                  </View>

                  {/* Widget Actions */}
                  <View style={styles.widgetCardActions}>
                    <TouchableOpacity
                      style={[styles.widgetActionButton, styles.widgetEditButton, { borderColor: palette.primary || '#FF0600' }]}
                      onPress={() => handleEditWidget(widget)}
                    >
                      <Text style={[styles.widgetActionButtonText, { color: palette.primary || '#FF0600' }]}>✏️ Edit</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[styles.widgetActionButton, styles.widgetDeleteButton, { borderColor: '#DC2626' }]}
                      onPress={() => handleDeleteWidget(widget.id)}
                      disabled={loading}
                    >
                      <Text style={[styles.widgetActionButtonText, { color: '#DC2626' }]}>🗑️ Delete</Text>
                    </TouchableOpacity>
                  </View>
                </AnimatedCard>
              );
            })}
          </ScrollView>
        )}
      </View>
    );
  };

  const renderCoursesTab = () => {
    if (showCourseForm && editingCourse) {
      return (
        <View style={styles.tabContent}>
          <View style={styles.formHeader}>
            <Text style={[styles.tabTitle, { color: palette.text || 'white' }]}>
              {editingCourse.id ? 'Edit Course' : 'Create New Course'}
            </Text>
            <TouchableOpacity
              onPress={() => {
                setShowCourseForm(false);
                setEditingCourse(null);
              }}
              style={[styles.closeButton, { borderColor: palette.border || 'rgba(255,255,255,0.2)' }]}
            >
              <Text style={[styles.closeButtonText, { color: palette.text || 'white' }]}>✕ Cancel</Text>
            </TouchableOpacity>
          </View>

          {/* Course Details */}
          <AnimatedCard style={[styles.section, { borderTopColor: palette.border || 'rgba(255,255,255,0.16)', backgroundColor: palette.surface }]} delay={0}>
            <Text style={[styles.sectionTitle, { color: palette.text || 'white' }]}>Course Details</Text>
            
            <View style={styles.formGroup}>
              <Text style={[styles.label, { color: palette.text || 'white' }]}>Title *</Text>
              <TextInput
                style={[styles.textInput, { borderColor: palette.border || 'rgba(255,255,255,0.2)', color: palette.text || 'white' }]}
                value={editingCourse.title}
                onChangeText={(value) => setEditingCourse({ ...editingCourse, title: value })}
                placeholder="Course Title"
                placeholderTextColor={palette.mutedText || 'rgba(255,255,255,0.4)'}
              />
            </View>

                <View style={styles.formGroup}>
                  <Text style={[styles.label, { color: palette.text || 'white' }]}>Description *</Text>
                  <TextInput
                    style={[styles.textInput, styles.textArea, { borderColor: palette.border || 'rgba(255,255,255,0.2)', color: palette.text || 'white' }]}
                    value={editingCourse.description}
                    onChangeText={(value) => setEditingCourse({ ...editingCourse, description: value })}
                    placeholder="Course Description"
                    placeholderTextColor={palette.mutedText || 'rgba(255,255,255,0.4)'}
                    multiline
                  />
                </View>

            <View style={styles.formRow}>
              <View style={styles.formGroup}>
                <Text style={[styles.label, { color: palette.text || 'white' }]}>Status</Text>
                <View style={[styles.selectContainer, { borderColor: palette.border || 'white' }]}>
                  <TouchableOpacity
                    style={[styles.selectOption, editingCourse.status === 'Available' && { backgroundColor: '#0DC44A' }]}
                    onPress={() => setEditingCourse({ ...editingCourse, status: 'Available' })}
                  >
                    <Text style={[styles.selectText, { color: editingCourse.status === 'Available' ? '#fff' : (palette.text || 'white') }]}>Available</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.selectOption, editingCourse.status === 'Coming Soon' && { backgroundColor: palette.primary || '#FF0600' }]}
                    onPress={() => setEditingCourse({ ...editingCourse, status: 'Coming Soon' })}
                  >
                    <Text style={[styles.selectText, { color: editingCourse.status === 'Coming Soon' ? '#fff' : (palette.text || 'white') }]}>Coming Soon</Text>
                  </TouchableOpacity>
                </View>
              </View>

              <View style={styles.formGroup}>
                <Text style={[styles.label, { color: palette.text || 'white' }]}>Order</Text>
                <TextInput
                  style={[styles.textInput, { borderColor: palette.border || 'rgba(255,255,255,0.2)', color: palette.text || 'white' }]}
                  value={String(editingCourse.order || 0)}
                  onChangeText={(value) => setEditingCourse({ ...editingCourse, order: parseInt(value) || 0 })}
                  placeholder="1"
                  placeholderTextColor={palette.mutedText || 'rgba(255,255,255,0.4)'}
                  keyboardType="numeric"
                />
              </View>
            </View>

            {Platform.OS === 'web' && (
              <View style={styles.formGroup}>
                <Text style={[styles.label, { color: palette.text || 'white' }]}>Thumbnail Image</Text>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e, 'thumbnail')}
                  style={{ 
                    padding: 8,
                    borderRadius: 22,
                    borderWidth: 1,
                    borderColor: palette.border || 'rgba(255,255,255,0.2)',
                    backgroundColor: 'transparent',
                    color: palette.text || 'white',
                  }}
                />
                {editingCourse.thumbnailUrl && (
                  <Text style={[styles.helperText, { color: palette.mutedText || 'rgba(255,255,255,0.6)' }]}>
                    ✓ Image uploaded
                  </Text>
                )}
              </View>
            )}
          </AnimatedCard>

          {/* Deadlines & Notifications */}
          <AnimatedCard style={[styles.section, { borderTopColor: palette.border || 'rgba(255,255,255,0.16)', backgroundColor: palette.surface }]} delay={50}>
            <Text style={[styles.sectionTitle, { color: palette.text || 'white' }]}>Deadlines & Notifications</Text>
            <Text style={[styles.helperText, { color: palette.mutedText || 'rgba(255,255,255,0.6)', fontSize: 14, marginBottom: 12 }]}>
              Set automatic deadlines for students when they enroll in this course. Notifications will be created automatically.
            </Text>

            <View style={styles.formGroup}>
              <View style={styles.labelRow}>
                <View>
                  <Text style={[styles.label, { color: palette.text || 'white' }]}>Enable Deadlines</Text>
                  <Text style={[styles.helperText, { color: palette.mutedText || 'rgba(255,255,255,0.6)', fontSize: 12, marginTop: 2 }]}>
                    Automatically create deadline notifications when students start this course
                  </Text>
                </View>
                <TouchableOpacity
                  onPress={() => {
                    setEditingCourse({
                      ...editingCourse,
                      deadlines: {
                        ...editingCourse.deadlines,
                        enabled: !editingCourse.deadlines?.enabled,
                      },
                    });
                  }}
                  style={[
                    styles.toggleButton,
                    { backgroundColor: editingCourse.deadlines?.enabled ? palette.primary : 'rgba(255,255,255,0.1)' },
                  ]}
                >
                  <Text style={[styles.toggleButtonText, { color: editingCourse.deadlines?.enabled ? '#fff' : palette.text }]}>
                    {editingCourse.deadlines?.enabled ? 'Enabled' : 'Disabled'}
                  </Text>
                </TouchableOpacity>
              </View>
            </View>

            {editingCourse.deadlines?.enabled && (
              <>
                <View style={styles.formGroup}>
                  <Text style={[styles.label, { color: palette.text || 'white' }]}>Course Completion Deadline (Days)</Text>
                  <TextInput
                    style={[styles.textInput, { borderColor: palette.border || 'rgba(255,255,255,0.2)', color: palette.text || 'white' }]}
                    value={String(editingCourse.deadlines?.courseDuration || '')}
                    onChangeText={(value) => setEditingCourse({
                      ...editingCourse,
                      deadlines: {
                        ...editingCourse.deadlines,
                        courseDuration: parseInt(value) || 0,
                      },
                    })}
                    placeholder="e.g., 30"
                    placeholderTextColor={palette.mutedText || 'rgba(255,255,255,0.4)'}
                    keyboardType="numeric"
                  />
                  <Text style={[styles.helperText, { color: palette.mutedText || 'rgba(255,255,255,0.6)', fontSize: 12, marginTop: 4 }]}>
                    Number of days students have to complete the entire course
                  </Text>
                </View>

                <View style={styles.formGroup}>
                  <Text style={[styles.label, { color: palette.text || 'white' }]}>Chapter Deadline Interval (Days)</Text>
                  <TextInput
                    style={[styles.textInput, { borderColor: palette.border || 'rgba(255,255,255,0.2)', color: palette.text || 'white' }]}
                    value={String(editingCourse.deadlines?.chapterDuration || '')}
                    onChangeText={(value) => setEditingCourse({
                      ...editingCourse,
                      deadlines: {
                        ...editingCourse.deadlines,
                        chapterDuration: parseInt(value) || 0,
                      },
                    })}
                    placeholder="e.g., 7"
                    placeholderTextColor={palette.mutedText || 'rgba(255,255,255,0.4)'}
                    keyboardType="numeric"
                  />
                  <Text style={[styles.helperText, { color: palette.mutedText || 'rgba(255,255,255,0.6)', fontSize: 12, marginTop: 4 }]}>
                    Number of days between each chapter deadline (e.g., 7 = Chapter 1 due in 7 days, Chapter 2 in 14 days, etc.)
                  </Text>
                </View>

                <View style={[styles.exampleBox, { backgroundColor: 'rgba(255,255,255,0.03)', borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                  <Text style={[styles.exampleLabel, { color: palette.primary }]}>Example Timeline</Text>
                  <Text style={[styles.exampleText, { color: palette.mutedText }]}>
                    {editingCourse.deadlines?.courseDuration 
                      ? `Students will have ${editingCourse.deadlines.courseDuration} days to complete the course.`
                      : 'Set a course duration to see the timeline.'}
                  </Text>
                  {editingCourse.deadlines?.chapterDuration && (
                    <Text style={[styles.exampleText, { color: palette.mutedText, marginTop: 8 }]}>
                      Chapters will be due every {editingCourse.deadlines.chapterDuration} days.
                    </Text>
                  )}
                </View>
              </>
            )}
          </AnimatedCard>

          {/* Chapters & Modules - App Style UI */}
          <View style={styles.adminStructureSection}>
            {/* Section Header */}
            <View style={styles.adminStructureHeader}>
              <View>
                <Text style={styles.adminStructureTitle}>Course Structure</Text>
                <Text style={styles.adminStructureSubtitle}>
                  {editingCourse.chapters?.length || 0} chapters • {editingCourse.chapters?.reduce((acc, ch) => acc + (ch.modules?.length || 0), 0) || 0} modules
              </Text>
              </View>
              <View style={styles.adminStructureActions}>
                {editingCourse.chapters?.length > 0 && (
                  <TouchableOpacity
                    style={styles.adminToggleAllBtn}
                    onPress={() => {
                      const allExpanded = editingCourse.chapters?.every(ch => expandedEditorChapters[ch.id]);
                      const newState = {};
                      editingCourse.chapters?.forEach(ch => { newState[ch.id] = !allExpanded; });
                      setExpandedEditorChapters(newState);
                    }}
                  >
                    <Text style={styles.adminToggleAllBtnText}>
                      {editingCourse.chapters?.every(ch => expandedEditorChapters[ch.id]) ? '− Collapse' : '+ Expand'}
                    </Text>
                  </TouchableOpacity>
                )}
                <TouchableOpacity style={styles.adminAddChapterBtn} onPress={handleAddChapter}>
                  <Text style={styles.adminAddChapterBtnText}>+ Add Chapter</Text>
                </TouchableOpacity>
              </View>
            </View>

            {/* Empty State */}
            {(!editingCourse.chapters || editingCourse.chapters.length === 0) && (
              <View style={styles.adminEmptyChapters}>
                <Text style={styles.adminEmptyChaptersIcon}>📖</Text>
                <Text style={styles.adminEmptyChaptersTitle}>No chapters yet</Text>
                <Text style={styles.adminEmptyChaptersDesc}>Add your first chapter to start building your course</Text>
                <TouchableOpacity style={styles.adminAddFirstBtn} onPress={handleAddChapter}>
                  <Text style={styles.adminAddFirstBtnText}>+ Create First Chapter</Text>
                  </TouchableOpacity>
                </View>
            )}

            {/* Chapter List */}
            {editingCourse.chapters?.map((chapter, chapterIndex) => {
              const isExpanded = expandedEditorChapters[chapter.id];
              const moduleCount = chapter.modules?.length || 0;
              const videosCount = chapter.modules?.filter(m => m.videoUrl)?.length || 0;
              const contentCount = chapter.modules?.filter(m => m.content || m.fullText)?.length || 0;
              
              return (
                <View key={chapter.id} style={styles.adminChapterCard}>
                  {/* Chapter Header */}
                  <View style={styles.adminChapterHeader}>
                    {/* Reorder Buttons */}
                    <View style={styles.adminReorderBtns}>
                      <TouchableOpacity 
                        style={[styles.adminReorderBtn, chapterIndex === 0 && styles.adminReorderBtnDisabled]}
                        onPress={() => handleReorderChapter(chapterIndex, 'up')}
                        disabled={chapterIndex === 0}
                      >
                        <Text style={styles.adminReorderBtnText}>▲</Text>
                      </TouchableOpacity>
                      <TouchableOpacity 
                        style={[styles.adminReorderBtn, chapterIndex === editingCourse.chapters.length - 1 && styles.adminReorderBtnDisabled]}
                        onPress={() => handleReorderChapter(chapterIndex, 'down')}
                        disabled={chapterIndex === editingCourse.chapters.length - 1}
                      >
                        <Text style={styles.adminReorderBtnText}>▼</Text>
                      </TouchableOpacity>
                    </View>
                    
                    {/* Chapter Number */}
                    <View style={styles.adminChapterNum}>
                      <Text style={styles.adminChapterNumText}>{chapterIndex + 1}</Text>
                    </View>
                    
                    {/* Chapter Info - Clickable to expand */}
                    <TouchableOpacity 
                      style={styles.adminChapterInfo}
                      onPress={() => setExpandedEditorChapters(prev => ({ ...prev, [chapter.id]: !prev[chapter.id] }))}
                    >
                      <View style={styles.adminChapterTitleRow}>
                        <Text style={styles.adminChapterTitle}>{chapter.title || 'Untitled Chapter'}</Text>
                        <View style={[styles.adminChapterStatusBadge, { backgroundColor: (chapter.status === 'Available' || !chapter.status) ? '#0DC44A' : '#FF4747' }]}>
                          <Text style={styles.adminChapterStatusText}>{(chapter.status === 'Available' || !chapter.status) ? 'LIVE' : 'SOON'}</Text>
                        </View>
                      </View>
                      <Text style={styles.adminChapterMeta}>
                        {moduleCount} module{moduleCount !== 1 ? 's' : ''} {contentCount > 0 && `• ${contentCount} with content`} {videosCount > 0 && `• ${videosCount} video${videosCount !== 1 ? 's' : ''}`}
                      </Text>
                    </TouchableOpacity>
                    
                    {/* Expand Icon */}
                    <TouchableOpacity 
                      style={styles.adminExpandBtn}
                      onPress={() => setExpandedEditorChapters(prev => ({ ...prev, [chapter.id]: !prev[chapter.id] }))}
                    >
                      <Text style={styles.adminExpandBtnText}>{isExpanded ? '−' : '+'}</Text>
                    </TouchableOpacity>
                  </View>
                  
                  {/* Chapter Content - Expanded */}
                  {isExpanded && (
                    <View style={styles.adminChapterContent}>
                      {/* Edit Chapter Fields */}
                      <View style={styles.adminChapterFields}>
                        <View style={styles.adminFieldRow}>
                          <View style={styles.adminFieldGroup}>
                            <Text style={styles.adminFieldLabel}>Title</Text>
                  <TextInput
                              style={styles.adminFieldInput}
                    value={chapter.title}
                    onChangeText={(value) => handleUpdateChapter(chapter.id, 'title', value)}
                              placeholder="Chapter title..."
                              placeholderTextColor="rgba(255,255,255,0.3)"
                  />
                </View>
                          <View style={styles.adminFieldGroupSmall}>
                            <Text style={styles.adminFieldLabel}>Status</Text>
                            <View style={styles.adminStatusBtns}>
                              <TouchableOpacity
                                style={[styles.adminStatusBtn, (chapter.status === 'Available' || !chapter.status) && styles.adminStatusBtnActive]}
                                onPress={() => handleUpdateChapter(chapter.id, 'status', 'Available')}
                              >
                                <Text style={[styles.adminStatusBtnText, (chapter.status === 'Available' || !chapter.status) && styles.adminStatusBtnTextActive]}>Live</Text>
                              </TouchableOpacity>
                              <TouchableOpacity
                                style={[styles.adminStatusBtn, chapter.status === 'Coming Soon' && styles.adminStatusBtnSoon]}
                                onPress={() => handleUpdateChapter(chapter.id, 'status', 'Coming Soon')}
                              >
                                <Text style={[styles.adminStatusBtnText, chapter.status === 'Coming Soon' && styles.adminStatusBtnTextActive]}>Soon</Text>
                              </TouchableOpacity>
                            </View>
                          </View>
                        </View>
                        <View style={styles.adminFieldGroup}>
                          <Text style={styles.adminFieldLabel}>Description</Text>
                  <TextInput
                            style={styles.adminFieldTextarea}
                    value={chapter.description}
                    onChangeText={(value) => handleUpdateChapter(chapter.id, 'description', value)}
                            placeholder="What this chapter covers..."
                            placeholderTextColor="rgba(255,255,255,0.3)"
                    multiline
                  />
                        </View>
                </View>

                      {/* Modules Section */}
                      <View style={styles.adminModulesSection}>
                        <View style={styles.adminModulesHeader}>
                          <Text style={styles.adminModulesTitle}>Modules ({chapter.modules?.length || 0})</Text>
                          <TouchableOpacity style={styles.adminAddModuleBtn} onPress={() => handleAddModule(chapter.id)}>
                            <Text style={styles.adminAddModuleBtnText}>+ Add Module</Text>
                          </TouchableOpacity>
                        </View>
                        
                        {/* Module List */}
                        {chapter.modules?.map((module, moduleIndex) => {
                          const moduleKey = `${chapter.id}-${module.id}`;
                          const isModuleExpanded = expandedModules[moduleKey];
                          const hasVideo = !!module.videoUrl;
                          const hasContent = !!(module.content || module.fullText || module.description);
                          const hasFullContent = !!(module.content || module.fullText);
                          
                          return (
                            <View key={module.id} style={[styles.adminModuleCard, isModuleExpanded && styles.adminModuleCardExpanded]}>
                              {/* Module Header */}
                              <View style={styles.adminModuleHeader}>
                                {/* Reorder */}
                                <View style={styles.adminModuleReorder}>
                    <TouchableOpacity
                                    style={[styles.adminModuleReorderBtn, moduleIndex === 0 && styles.adminReorderBtnDisabled]}
                                    onPress={() => handleReorderModule(chapter.id, moduleIndex, 'up')}
                                    disabled={moduleIndex === 0}
                    >
                                    <Text style={styles.adminModuleReorderText}>▲</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                                    style={[styles.adminModuleReorderBtn, moduleIndex === (chapter.modules?.length || 0) - 1 && styles.adminReorderBtnDisabled]}
                                    onPress={() => handleReorderModule(chapter.id, moduleIndex, 'down')}
                                    disabled={moduleIndex === (chapter.modules?.length || 0) - 1}
                    >
                                    <Text style={styles.adminModuleReorderText}>▼</Text>
                    </TouchableOpacity>
                  </View>
                                
                                {/* Module Number */}
                                <View style={styles.adminModuleNum}>
                                  <Text style={styles.adminModuleNumText}>{moduleIndex + 1}</Text>
                </View>

                                {/* Module Info */}
                    <TouchableOpacity 
                                  style={styles.adminModuleInfo}
                                  onPress={() => setExpandedModules(prev => ({ ...prev, [moduleKey]: !prev[moduleKey] }))}
                                >
                                  <Text style={styles.adminModuleTitle} numberOfLines={1}>{module.title || 'Untitled Module'}</Text>
                                  <View style={styles.adminModuleIcons}>
                                    {hasVideo && <Text style={styles.adminModuleIcon}>🎬</Text>}
                                    {hasFullContent && <Text style={styles.adminModuleIcon}>📖</Text>}
                                    {!hasFullContent && hasContent && <Text style={styles.adminModuleIcon}>📝</Text>}
                                    {module.widgets?.length > 0 && <Text style={styles.adminModuleIcon}>🧩</Text>}
                                    {module.required && <Text style={styles.adminModuleIcon}>⭐</Text>}
                                    {!hasVideo && !hasContent && !module.widgets?.length && <Text style={styles.adminModuleEmpty}>Empty</Text>}
                  </View>
                                </TouchableOpacity>
                                
                                {/* Expand */}
                                <TouchableOpacity 
                                  style={styles.adminModuleExpandBtn}
                                  onPress={() => setExpandedModules(prev => ({ ...prev, [moduleKey]: !prev[moduleKey] }))}
                                >
                                  <Text style={styles.adminModuleExpandText}>{isModuleExpanded ? '−' : '+'}</Text>
                        </TouchableOpacity>
                      </View>

                              {/* Module Content - Expanded */}
                              {isModuleExpanded && (
                                <View style={styles.adminModuleContent}>
                                  {/* Edit Section */}
                                  <View style={styles.adminEditSection}>
                                    <Text style={styles.adminEditSectionTitle}>✏️ EDIT MODULE</Text>
                                    
                                    {/* Title */}
                                    <View style={styles.adminModuleField}>
                                      <Text style={styles.adminModuleFieldLabel}>Module Title</Text>
                        <TextInput
                                        style={styles.adminModuleFieldInput}
                          value={module.title}
                          onChangeText={(value) => handleUpdateModule(chapter.id, module.id, 'title', value)}
                                        placeholder="Module title..."
                                        placeholderTextColor="rgba(255,255,255,0.3)"
                        />
                      </View>

                                    {/* Description (short summary) */}
                                    <View style={styles.adminModuleField}>
                                      <Text style={styles.adminModuleFieldLabel}>Description (short summary - {module.description?.length || 0} chars)</Text>
                        <TextInput
                                        style={styles.adminModuleFieldTextarea}
                          value={module.description}
                          onChangeText={(value) => handleUpdateModule(chapter.id, module.id, 'description', value)}
                                        placeholder="Brief description of what this module covers..."
                                        placeholderTextColor="rgba(255,255,255,0.3)"
                          multiline
                        />
                      </View>

                                    {/* Full Content (what users see) */}
                                    <View style={styles.adminModuleField}>
                                      <Text style={styles.adminModuleFieldLabel}>📝 Full Content (shown to users - {(module.content || module.fullText || '').length} chars)</Text>
                                      
                                      {/* Formatting Help Bar */}
                                      <View style={styles.adminFormattingBar}>
                                        <Text style={styles.adminFormattingTitle}>Quick Formatting:</Text>
                                        <View style={styles.adminFormattingButtons}>
                                          <TouchableOpacity 
                                            style={styles.adminFormatBtn}
                                            onPress={() => {
                                              const current = module.content || module.fullText || '';
                                              handleUpdateModule(chapter.id, module.id, 'content', current + '\n\n# Heading\n');
                                            }}
                                          >
                                            <Text style={styles.adminFormatBtnText}>H1</Text>
                                          </TouchableOpacity>
                                          <TouchableOpacity 
                                            style={styles.adminFormatBtn}
                                            onPress={() => {
                                              const current = module.content || module.fullText || '';
                                              handleUpdateModule(chapter.id, module.id, 'content', current + '\n\n## Subheading\n');
                                            }}
                                          >
                                            <Text style={styles.adminFormatBtnText}>H2</Text>
                                          </TouchableOpacity>
                                          <TouchableOpacity 
                                            style={styles.adminFormatBtn}
                                            onPress={() => {
                                              const current = module.content || module.fullText || '';
                                              handleUpdateModule(chapter.id, module.id, 'content', current + '\n• ');
                                            }}
                                          >
                                            <Text style={styles.adminFormatBtnText}>• List</Text>
                                          </TouchableOpacity>
                                          <TouchableOpacity 
                                            style={styles.adminFormatBtn}
                                            onPress={() => {
                                              const current = module.content || module.fullText || '';
                                              handleUpdateModule(chapter.id, module.id, 'content', current + '\n1. ');
                                            }}
                                          >
                                            <Text style={styles.adminFormatBtnText}>1. List</Text>
                                          </TouchableOpacity>
                                          <TouchableOpacity 
                                            style={styles.adminFormatBtn}
                                            onPress={() => {
                                              const current = module.content || module.fullText || '';
                                              handleUpdateModule(chapter.id, module.id, 'content', current + '**bold text**');
                                            }}
                                          >
                                            <Text style={[styles.adminFormatBtnText, { fontWeight: '900' }]}>B</Text>
                                          </TouchableOpacity>
                                          <TouchableOpacity 
                                            style={styles.adminFormatBtn}
                                            onPress={() => {
                                              const current = module.content || module.fullText || '';
                                              handleUpdateModule(chapter.id, module.id, 'content', current + '*italic text*');
                                            }}
                                          >
                                            <Text style={[styles.adminFormatBtnText, { fontStyle: 'italic' }]}>I</Text>
                                          </TouchableOpacity>
                                        </View>
                                      </View>
                                      
                                      {/* Content Editor - Clean & Readable */}
                                      {Platform.OS === 'web' ? (
                                        <textarea
                                          value={module.content || module.fullText || ''}
                                          onChange={(e) => handleUpdateModule(chapter.id, module.id, 'content', e.target.value)}
                                          placeholder="Write your module content here...

Example format:
# Main Title

This is a paragraph of text explaining the concept.

## Key Points

• First important point
• Second important point  
• Third important point

## What You'll Learn

1. First thing to learn
2. Second thing to learn
3. Third thing to learn

**Bold text** for emphasis and *italic text* for highlights."
                                          style={{
                                            width: '100%',
                                            minHeight: 400,
                                            padding: 20,
                                            fontSize: 16,
                                            fontFamily: 'Georgia, serif',
                                            lineHeight: 1.8,
                                            border: '2px solid rgba(0,0,0,0.15)',
                                            borderRadius: 12,
                                            backgroundColor: '#FFFEF8',
                                            color: '#333',
                                            resize: 'vertical',
                                            outline: 'none',
                                          }}
                                        />
                                      ) : (
                                        <TextInput
                                          style={[styles.adminContentEditor]}
                                          value={module.content || module.fullText || ''}
                                          onChangeText={(value) => handleUpdateModule(chapter.id, module.id, 'content', value)}
                                          placeholder="Write your module content here..."
                                          placeholderTextColor="rgba(0,0,0,0.3)"
                                          multiline
                                        />
                                      )}
                                      
                                      <View style={styles.adminContentHelpBox}>
                                        <Text style={styles.adminContentHelpTitle}>📖 Formatting Guide:</Text>
                                        <Text style={styles.adminContentHelpText}>
                                          • Use <Text style={{ fontWeight: '700' }}># Title</Text> for main headings{'\n'}
                                          • Use <Text style={{ fontWeight: '700' }}>## Subtitle</Text> for subheadings{'\n'}
                                          • Use <Text style={{ fontWeight: '700' }}>• item</Text> or <Text style={{ fontWeight: '700' }}>- item</Text> for bullet lists{'\n'}
                                          • Use <Text style={{ fontWeight: '700' }}>1. item</Text> for numbered lists{'\n'}
                                          • Use <Text style={{ fontWeight: '700' }}>**text**</Text> for bold{'\n'}
                                          • Use <Text style={{ fontWeight: '700' }}>*text*</Text> for italic{'\n'}
                                          • Leave blank lines between paragraphs
                                        </Text>
                                      </View>
                                    </View>

                                    {/* Video Upload */}
                                    <View style={styles.adminModuleField}>
                                      <Text style={styles.adminModuleFieldLabel}>Video {module.videoUrl && '✓'}</Text>
                                      <View style={styles.adminModuleVideoRow}>
                      {Platform.OS === 'web' && (
                                          <label style={{ 
                                            display: 'inline-flex',
                                            padding: '8px 12px',
                                            borderRadius: 22,
                                            backgroundColor: '#FF4747',
                                            cursor: 'pointer',
                                            fontSize: 12,
                                            fontWeight: '600',
                                            color: '#000',
                                            fontFamily: 'Archivo',
                                          }}>
                          <input
                            type="file"
                            accept="video/*"
                            onChange={(e) => handleVideoUpload(e, chapter.id, module.id)}
                            disabled={uploadingVideo === `${chapter.id}-${module.id}`}
                                              style={{ display: 'none' }}
                                            />
                                            📤 Upload
                                          </label>
                                        )}
                        <TextInput
                                          style={styles.adminModuleVideoInput}
                          value={module.videoUrl}
                          onChangeText={(value) => handleUpdateModule(chapter.id, module.id, 'videoUrl', value)}
                                          placeholder="Or paste video URL..."
                                          placeholderTextColor="rgba(255,255,255,0.3)"
                                        />
                                      </View>
                          {uploadingVideo === `${chapter.id}-${module.id}` && (
                                        <View style={styles.adminUploadProgress}>
                                          <View style={[styles.adminUploadBar, { width: `${uploadProgress[`${chapter.id}-${module.id}`] || 0}%` }]} />
                                          <Text style={styles.adminUploadText}>{uploadProgress[`${chapter.id}-${module.id}`]?.toFixed(0) || 0}%</Text>
                              </View>
                                      )}
                                      
                                      {/* Video Player Preview in Edit Section */}
                                      {module.videoUrl && Platform.OS === 'web' && (
                                        <View style={styles.adminVideoPlayerContainer}>
                                          <Text style={styles.adminVideoPlayerLabel}>🎬 Video Preview (click to play)</Text>
                                          {module.videoUrl.includes('youtube') || module.videoUrl.includes('youtu.be') ? (
                                            <iframe
                                              src={`https://www.youtube.com/embed/${module.videoUrl.includes('youtu.be') 
                                                ? module.videoUrl.split('youtu.be/')[1]?.split('?')[0] 
                                                : module.videoUrl.split('v=')[1]?.split('&')[0]}`}
                                              style={{ 
                                width: '100%', 
                                                height: 250, 
                                                borderRadius: 12, 
                                                border: 'none',
                                                backgroundColor: '#000'
                                              }}
                                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                              allowFullScreen
                                            />
                                          ) : module.videoUrl.includes('vimeo') ? (
                                            <iframe
                                              src={`https://player.vimeo.com/video/${module.videoUrl.split('vimeo.com/')[1]?.split('?')[0]}`}
                                              style={{ 
                                                width: '100%', 
                                                height: 250, 
                                                borderRadius: 12, 
                                                border: 'none',
                                                backgroundColor: '#000'
                                              }}
                                              allow="autoplay; fullscreen; picture-in-picture"
                                              allowFullScreen
                                            />
                                          ) : (
                                            <video 
                                              src={module.videoUrl} 
                                              controls
                                              style={{ 
                                                width: '100%', 
                                                height: 250, 
                                                borderRadius: 12, 
                                                backgroundColor: '#000',
                                                objectFit: 'contain'
                                              }}
                                            />
                                          )}
                        </View>
                      )}
                                    </View>
                                    
                                    {/* Settings Row */}
                                    <View style={styles.adminModuleSettingsRow}>
                                      <TouchableOpacity
                                        style={[styles.adminModuleToggle, module.required && styles.adminModuleToggleActive]}
                                        onPress={() => handleUpdateModule(chapter.id, module.id, 'required', !module.required)}
                                      >
                                        <Text style={[styles.adminModuleToggleText, module.required && styles.adminModuleToggleTextActive]}>
                                          {module.required ? '⭐ Required' : '☆ Optional'}
                        </Text>
                                      </TouchableOpacity>

                      {Platform.OS === 'web' && (
                                        <label style={{ 
                                          display: 'inline-flex',
                                          padding: '8px 12px',
                                          borderRadius: 22,
                                          border: '1px solid rgba(255,255,255,0.2)',
                                          cursor: 'pointer',
                                          fontSize: 12,
                                          fontWeight: '500',
                                          color: 'white',
                                          fontFamily: 'Archivo',
                                        }}>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleImageUpload(e, 'module', chapter.id, module.id)}
                                            style={{ display: 'none' }}
                                          />
                                          {module.imageUrl ? '🖼 Change Thumbnail' : '🖼 Add Thumbnail'}
                                        </label>
                                      )}
                                      
                                      <TouchableOpacity style={styles.adminModuleDeleteBtn} onPress={() => handleDeleteModule(chapter.id, module.id)}>
                                        <Text style={styles.adminModuleDeleteText}>🗑 Delete</Text>
                                      </TouchableOpacity>
                      </View>

                                    {/* Widget Management */}
                                    <View style={styles.adminModuleWidgets}>
                                      <View style={styles.adminModuleWidgetsHeader}>
                                        <Text style={styles.adminModuleWidgetsTitle}>Widgets ({module.widgets?.length || 0}/5)</Text>
                          {(module.widgets || []).length < 5 && widgets.length > 0 && (
                            <TouchableOpacity
                                            style={styles.adminAddWidgetBtn}
                              onPress={() => {
                                const currentWidgets = module.widgets || [];
                                handleUpdateModule(chapter.id, module.id, 'widgets', [
                                  ...currentWidgets,
                                  { widgetId: '', placement: 'below' }
                                ]);
                              }}
                            >
                                            <Text style={styles.adminAddWidgetText}>+ Add</Text>
                            </TouchableOpacity>
                          )}
                        </View>
                        {(module.widgets || []).map((widgetConfig, widgetIndex) => (
                                        <View key={widgetIndex} style={styles.adminWidgetItem}>
                                <TouchableOpacity
                                            style={styles.adminWidgetSelector}
                                  onPress={() => {
                                    const currentIndex = widgets.findIndex(w => w.id === widgetConfig.widgetId);
                                    const nextWidget = widgets[(currentIndex + 1) % widgets.length];
                                    const updatedWidgets = [...(module.widgets || [])];
                                    updatedWidgets[widgetIndex] = { ...updatedWidgets[widgetIndex], widgetId: nextWidget.id };
                                    handleUpdateModule(chapter.id, module.id, 'widgets', updatedWidgets);
                                  }}
                                >
                                            <Text style={styles.adminWidgetSelectorText}>
                                              {widgetConfig.widgetId ? widgets.find(w => w.id === widgetConfig.widgetId)?.title || 'Select' : 'Select widget...'}
                                  </Text>
                                </TouchableOpacity>
                                <TouchableOpacity
                                            style={styles.adminWidgetRemove}
                                  onPress={() => {
                                    const updatedWidgets = [...(module.widgets || [])];
                                              updatedWidgets.splice(widgetIndex, 1);
                                    handleUpdateModule(chapter.id, module.id, 'widgets', updatedWidgets);
                                  }}
                                >
                                            <Text style={styles.adminWidgetRemoveText}>×</Text>
                                </TouchableOpacity>
                                        </View>
                                      ))}
                              </View>
                            </View>

                                  {/* PREVIEW SECTION - Shows what user will see */}
                                  <View style={styles.adminPreviewSection}>
                                    <Text style={styles.adminPreviewSectionTitle}>👁 USER PREVIEW</Text>
                                    <Text style={styles.adminPreviewSubtitle}>This is how users will see this module</Text>
                                    
                                    {/* Video Preview */}
                                    {module.videoUrl && (
                                      <View style={styles.adminVideoPreviewContainer}>
                                        <Text style={styles.adminPreviewLabel}>🎬 Video</Text>
                                        {Platform.OS === 'web' && (
                                          module.videoUrl.includes('youtube') || module.videoUrl.includes('youtu.be') ? (
                                            <iframe
                                              src={`https://www.youtube.com/embed/${module.videoUrl.includes('youtu.be') 
                                                ? module.videoUrl.split('youtu.be/')[1]?.split('?')[0] 
                                                : module.videoUrl.split('v=')[1]?.split('&')[0]}`}
                                              style={{ width: '100%', height: 300, borderRadius: 12, border: 'none' }}
                                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                              allowFullScreen
                                            />
                                          ) : module.videoUrl.includes('vimeo') ? (
                                            <iframe
                                              src={`https://player.vimeo.com/video/${module.videoUrl.split('vimeo.com/')[1]?.split('?')[0]}`}
                                              style={{ width: '100%', height: 300, borderRadius: 12, border: 'none' }}
                                              allow="autoplay; fullscreen; picture-in-picture"
                                              allowFullScreen
                                            />
                                          ) : (
                                            <video 
                                              src={module.videoUrl} 
                                              controls
                                              style={{ width: '100%', height: 300, borderRadius: 12, backgroundColor: '#000' }}
                                            />
                                          )
                                        )}
                          </View>
                                    )}
                                    
                                    {/* Thumbnail Preview */}
                                    {module.imageUrl && (
                                      <View style={styles.adminThumbnailPreview}>
                                        <Text style={styles.adminPreviewLabel}>🖼 Thumbnail</Text>
                                        {Platform.OS === 'web' && (
                                          <img 
                                            src={module.imageUrl} 
                                            alt="Module thumbnail"
                                            style={{ width: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: 12 }}
                                          />
                                        )}
                                      </View>
                                    )}
                                    
                                    {/* Full Content Preview - same as what users see */}
                                    {(module.content || module.fullText) && (
                                      <View style={styles.adminContentPreview}>
                                        <Text style={styles.adminPreviewLabel}>📝 Full Content (what users see)</Text>
                                        <View style={styles.adminContentPreviewBox}>
                                          <MarkdownRenderer 
                                            content={module.content || module.fullText}
                                            customColors={{
                                              text: '#333',
                                              mutedText: '#666',
                                              primary: '#FF4747'
                                            }}
                                          />
                          </View>
                                      </View>
                                    )}
                                    
                                    {/* Description fallback if no full content */}
                                    {!(module.content || module.fullText) && module.description && (
                                      <View style={styles.adminContentPreview}>
                                        <Text style={styles.adminPreviewLabel}>📝 Description (no full content set)</Text>
                                        <View style={styles.adminContentPreviewBox}>
                                          <Text style={styles.adminContentPreviewText}>{module.description}</Text>
                                        </View>
                                      </View>
                                    )}
                                    
                                    {/* Widget Previews */}
                                    {module.widgets && module.widgets.length > 0 && (
                                      <View style={styles.adminWidgetPreviewSection}>
                                        <Text style={styles.adminPreviewLabel}>🧩 Widgets</Text>
                                        {module.widgets.map((widgetConfig, widgetIndex) => {
                                          const widgetData = widgets.find(w => w.id === widgetConfig.widgetId);
                                          if (!widgetData) return null;
                                          return (
                                            <View key={widgetIndex} style={styles.adminWidgetPreviewCard}>
                                              <View style={styles.adminWidgetPreviewHeader}>
                                                <Text style={styles.adminWidgetPreviewTitle}>{widgetData.title}</Text>
                                                <Text style={styles.adminWidgetPreviewType}>{widgetData.type}</Text>
                                              </View>
                                              {widgetData.description && (
                                                <Text style={styles.adminWidgetPreviewDesc}>{widgetData.description}</Text>
                                              )}
                                              <View style={styles.adminWidgetPreviewPlacement}>
                                                <Text style={styles.adminWidgetPreviewPlacementText}>
                                                  Placement: {widgetConfig.placement === 'above' ? '↑ Above content' : widgetConfig.placement === 'below' ? '↓ Below content' : '→ Sidebar'}
                            </Text>
                                              </View>
                                              {/* Render actual widget preview */}
                                              <View style={styles.adminWidgetPreviewRenderer}>
                                                <WidgetRenderer widget={widgetData} compact={true} />
                                              </View>
                                            </View>
                                          );
                                        })}
                          </View>
                        )}
                                    
                                    {/* Empty State */}
                                    {!module.videoUrl && !module.description && (!module.widgets || module.widgets.length === 0) && (
                                      <View style={styles.adminEmptyPreview}>
                                        <Text style={styles.adminEmptyPreviewIcon}>📭</Text>
                                        <Text style={styles.adminEmptyPreviewText}>This module is empty. Add content, video, or widgets above.</Text>
                      </View>
                                    )}
                    </View>
                                </View>
                              )}
                      </View>
                          );
                        })}
                  
                        {/* Empty modules */}
                  {(!chapter.modules || chapter.modules.length === 0) && (
                          <View style={styles.adminEmptyModules}>
                            <Text style={styles.adminEmptyModulesText}>No modules yet - click "+ Add Module" above</Text>
                          </View>
                  )}
                </View>
                      
                      {/* Delete Chapter */}
                      <TouchableOpacity style={styles.adminDeleteChapterBtn} onPress={() => handleDeleteChapter(chapter.id)}>
                        <Text style={styles.adminDeleteChapterText}>🗑 Delete Chapter</Text>
                      </TouchableOpacity>
                    </View>
                  )}
                </View>
              );
            })}
          </View>

          {/* Save Button */}
          <View style={styles.formActions}>
            <AnimatedButton
              title={loading ? 'Saving...' : 'Save Course'}
              onPress={handleSaveCourse}
              disabled={loading}
              loading={loading}
              style={styles.saveButton}
            />
          </View>
        </View>
      );
    }

    // Course List View - App Style with Hard Shadows
    return (
      <View style={styles.tabContent}>
        {/* Header */}
        <View style={styles.formHeader}>
          <Text style={[styles.tabTitle, { color: palette.text || 'white' }]}>Course Management</Text>
          <AnimatedButton
            title="+ Create Course"
            onPress={handleCreateCourse}
            variant="primary"
            style={styles.createButton}
          />
        </View>

        {/* Quick Stats Cards */}
        {!coursesLoading && courses.length > 0 && (
          <View style={styles.adminStatsRow}>
            <View style={styles.adminStatCard}>
              <Text style={styles.adminStatNumber}>{courses.length}</Text>
              <Text style={styles.adminStatLabel}>Courses</Text>
            </View>
            <View style={styles.adminStatCard}>
              <Text style={styles.adminStatNumber}>
                {courses.reduce((acc, c) => acc + (c.chapters?.length || 0), 0)}
            </Text>
              <Text style={styles.adminStatLabel}>Chapters</Text>
            </View>
            <View style={styles.adminStatCard}>
              <Text style={styles.adminStatNumber}>
                {courses.reduce((acc, c) => acc + (c.chapters?.reduce((a, ch) => a + (ch.modules?.length || 0), 0) || 0), 0)}
                      </Text>
              <Text style={styles.adminStatLabel}>Modules</Text>
                        </View>
            <View style={styles.adminStatCard}>
              <Text style={styles.adminStatNumber}>
                {courses.reduce((acc, c) => acc + (c.chapters?.reduce((a, ch) => a + (ch.modules?.filter(m => m.videoUrl)?.length || 0), 0) || 0), 0)}
                        </Text>
              <Text style={styles.adminStatLabel}>Videos</Text>
                      </View>
                    </View>
        )}

        {/* Course List */}
        {coursesLoading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={palette.primary || '#FF4747'} />
            <Text style={[styles.loadingText, { color: palette.mutedText }]}>Loading courses...</Text>
                  </View>
        ) : courses.length === 0 ? (
          <View style={styles.adminEmptyState}>
            <Text style={styles.adminEmptyIcon}>📚</Text>
            <Text style={styles.adminEmptyTitle}>No courses yet</Text>
            <Text style={styles.adminEmptyDesc}>Create your first course to get started.</Text>
                    <AnimatedButton
              title="+ Create Course"
              onPress={handleCreateCourse}
              variant="primary"
              style={{ marginTop: 16 }}
            />
          </View>
        ) : (
          <View style={styles.adminCourseList}>
            {courses.map((course, index) => {
              const chaptersCount = course.chapters?.length || 0;
              const modulesCount = course.chapters?.reduce((a, ch) => a + (ch.modules?.length || 0), 0) || 0;
              const videosCount = course.chapters?.reduce((a, ch) => a + (ch.modules?.filter(m => m.videoUrl)?.length || 0), 0) || 0;
              
              return (
                <View key={course.id} style={styles.adminCourseCard}>
                  <View style={styles.adminCourseCardInner}>
                    {/* Status & Order */}
                    <View style={styles.adminCourseTop}>
                      <View style={[styles.adminStatusBadge, { backgroundColor: course.status === 'Available' ? '#0DC44A' : '#FF4747' }]}>
                        <Text style={styles.adminStatusText}>{course.status === 'Available' ? '● LIVE' : '◐ SOON'}</Text>
                      </View>
                      <Text style={styles.adminCourseOrder}>#{course.order || index + 1}</Text>
                    </View>
                    
                    {/* Title & Description */}
                    <Text style={styles.adminCourseTitle}>{course.title || 'Untitled'}</Text>
                    <Text style={styles.adminCourseDesc} numberOfLines={2}>{course.description || 'No description'}</Text>
                    
                    {/* Stats */}
                    <View style={styles.adminCourseStats}>
                      <View style={styles.adminCourseStat}>
                        <Text style={styles.adminCourseStatNum}>{chaptersCount}</Text>
                        <Text style={styles.adminCourseStatLbl}>chapters</Text>
                      </View>
                      <View style={styles.adminCourseStat}>
                        <Text style={styles.adminCourseStatNum}>{modulesCount}</Text>
                        <Text style={styles.adminCourseStatLbl}>modules</Text>
                      </View>
                      <View style={styles.adminCourseStat}>
                        <Text style={styles.adminCourseStatNum}>{videosCount}</Text>
                        <Text style={styles.adminCourseStatLbl}>videos</Text>
                      </View>
                    </View>
                    
                    {/* Actions */}
                    <View style={styles.adminCourseActions}>
                      <TouchableOpacity style={styles.adminEditBtn} onPress={() => handleEditCourse(course)}>
                        <Text style={styles.adminEditBtnText}>✏️ Edit Course</Text>
                      </TouchableOpacity>
                      <TouchableOpacity style={styles.adminDeleteBtnSmall} onPress={() => handleDeleteCourse(course.id)}>
                        <Text style={styles.adminDeleteBtnSmallText}>🗑</Text>
                    </TouchableOpacity>
                  </View>
            </View>
        </View>
              );
            })}
          </View>
        )}
      </View>
    );
  };

  const renderPersistentWidgetsTab = () => {
    // Define the three matrix widget types
    const matrixTypes = [
      { type: 'strategic_decision', title: 'Strategic Decision', description: 'Evaluate decisions based on criteria and importance', component: StrategicDecisionMatrix },
      { type: 'strategic_action', title: 'Strategic Action', description: 'Prioritize actions based on impact and effort', component: StrategicActionMatrix },
      { type: 'tiger_team_breakouts', title: 'Tiger Team Breakouts', description: 'Track and manage high-priority breakouts', component: TigerTeamBreakoutsTracker },
    ];

    // Get or initialize widget for each type
    const getWidgetForType = (type) => {
      return persistentWidgets.find(w => w.type === type) || {
        type,
        title: matrixTypes.find(m => m.type === type)?.title || '',
        description: matrixTypes.find(m => m.type === type)?.description || '',
        status: 'Inactive',
        schedule: { type: 'weekly', dayOfWeek: 1, dayOfMonth: 1 },
      };
    };

    const handleUpdateWidget = async (type, updates) => {
      console.log('handleUpdateWidget', type, updates);
      const existingWidget = persistentWidgets.find(w => w.type === type);
      setLoading(true);
      
      let result;
      if (existingWidget) {
        const { id, ...widgetUpdates } = { ...existingWidget, ...updates };
        result = await persistentWidgetService.updatePersistentWidget(id, widgetUpdates);
      } else {
        const widgetData = {
          type,
          title: matrixTypes.find(m => m.type === type)?.title || '',
          description: matrixTypes.find(m => m.type === type)?.description || '',
          status: updates.status || 'Inactive',
          schedule: { type: 'weekly', dayOfWeek: 1, dayOfMonth: 1 },
          ...updates,
        };
        result = await persistentWidgetService.createPersistentWidget(widgetData);
      }

      setLoading(false);
      if (result.success) {
        await loadPersistentWidgets();
      } else {
        Alert.alert('Error', result.error || 'Failed to update widget');
      }
    };

    // Format schedule display text
    const formatScheduleText = (schedule) => {
      if (!schedule || !schedule.type) return 'Not configured';
      
      const scheduleType = schedule.type;
      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      
      switch (scheduleType) {
        case 'daily':
          return 'Daily';
        case 'weekly':
          const dayOfWeek = schedule.dayOfWeek !== undefined ? schedule.dayOfWeek : 1;
          return `Weekly - ${dayNames[dayOfWeek]}`;
        case 'monthly':
          const dayOfMonth = schedule.dayOfMonth !== undefined ? schedule.dayOfMonth : 1;
          return `Monthly - Day ${dayOfMonth}`;
        default:
          return scheduleType.charAt(0).toUpperCase() + scheduleType.slice(1);
      }
    };

    // Open modal for editing widget
    const handleOpenEditModal = (type) => {
      const widget = getWidgetForType(type);
      setEditingWidgetType(type);
      setTempSchedule(widget.schedule ? { ...widget.schedule } : { type: 'weekly', dayOfWeek: 1, dayOfMonth: 1 });
      setTempStatus(widget.status || 'Inactive');
    };

    // Close modal and discard changes
    const handleCloseEditModal = () => {
      setEditingWidgetType(null);
      setTempSchedule(null);
      setTempStatus(null);
    };

    // Save schedule and status changes from modal
    const handleSaveSchedule = async () => {
      if (!editingWidgetType || !tempSchedule || !tempStatus) return;
      
      await handleUpdateWidget(editingWidgetType, { 
        schedule: tempSchedule,
        status: tempStatus
      });
      handleCloseEditModal();
    };


    // Get widget being edited in modal
    const editingWidget = editingWidgetType ? getWidgetForType(editingWidgetType) : null;
    const editingMatrixType = editingWidgetType ? matrixTypes.find(m => m.type === editingWidgetType) : null;
    const editingScheduleType = tempSchedule?.type || editingWidget?.schedule?.type || 'weekly';
    const editingDayOfWeek = tempSchedule?.dayOfWeek !== undefined ? tempSchedule.dayOfWeek : (editingWidget?.schedule?.dayOfWeek !== undefined ? editingWidget.schedule.dayOfWeek : 1);
    const editingDayOfMonth = tempSchedule?.dayOfMonth !== undefined ? tempSchedule.dayOfMonth : (editingWidget?.schedule?.dayOfMonth !== undefined ? editingWidget.schedule.dayOfMonth : 1);
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    return (
      <View style={styles.tabContent}>
        <View style={styles.formHeader}>
          <Text style={[styles.tabTitle, { color: palette.text || 'white' }]}>Persistent Widgets</Text>
        </View>

        <View style={[styles.section, { borderTopColor: palette.border || 'rgba(255,255,255,0.16)' }]}>
          <Text style={[styles.sectionTitle, { color: palette.text || 'white' }]}>
            Persistent Widgets Configuration
          </Text>
          <Text style={[styles.helperText, { color: palette.mutedText || 'rgba(255,255,255,0.60)', marginBottom: 24 }]}>
            Configure the three matrix widget types. Activate or deactivate each widget and set their completion frequency.
          </Text>
          
          {persistentWidgetsLoading ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color={palette.primary || '#FF0600'} />
              <Text style={[styles.loadingText, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>Loading widgets...</Text>
            </View>
          ) : (
            <View style={styles.widgetsList}>
              {matrixTypes.map((matrixType, index) => {
                const widget = getWidgetForType(matrixType.type);
                const isActive = widget.status === 'Active';
                const PreviewComponent = matrixType.component;
                const previewWidget = {
                  ...widget,
                  id: widget.id || 'preview',
                };
                
                return (
                  <AnimatedCard key={matrixType.type} delay={index * 100} style={[styles.widgetCard, { width: '100%',borderColor: palette.border || 'rgba(255,255,255,0.16)', backgroundColor: palette.surface }]}>
                    {/* Widget Info and Status Section */}
                    <View style={[styles.widgetSettingsSection, { borderBottomColor: palette.border || 'rgba(255,255,255,0.16)' }]}>
                      <View style={styles.widgetCardHeader}>
                        <View style={styles.widgetCardInfo}>
                          <View style={styles.labelRow}>
                            <Text style={[styles.widgetCardTitle, { color: palette.text || 'white' }]}>
                              {matrixType.title}
                            </Text>
                            <Text style={[styles.label, { color: palette.mutedText || 'rgba(255,255,255,0.60)', fontSize: 13 }]}>
                              {isActive ? 'Active' : 'Inactive'}
                            </Text>
                          </View>
                          <Text style={[styles.widgetCardDescription, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>
                            {matrixType.description}
                          </Text>
                        </View>
                      </View>
                      
                      {/* Schedule Display */}
                      <View style={[styles.formGroup, { padding: 20, marginTop: 16 }]}>
                        <Text style={[styles.label, { color: palette.text || 'white', marginBottom: 8 }]}>Schedule</Text>
                        <Text style={[styles.scheduleDisplayText, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>
                          {formatScheduleText(widget.schedule)}
                        </Text>
                      </View>

                      {/* Manage Widget Button */}
                      <TouchableOpacity
                        style={[styles.manageWidgetButton, { 
                          marginHorizontal: 20,
                          backgroundColor: palette.primary || '#FF0600',
                          marginTop: 16
                        }]}
                        onPress={() => handleOpenEditModal(matrixType.type)}
                      >
                        <Text style={[styles.manageWidgetButtonText, { color: '#fff' }]}>
                          Manage Widget
                        </Text>
                      </TouchableOpacity>
                    </View>

                    {/* Preview Section */}
                    <View style={styles.widgetPreviewSection}>
                      <Text style={[styles.previewSectionTitle, { color: palette.text || 'white', marginBottom: 16 }]}>
                        Preview
                      </Text>
                      {PreviewComponent && (
                        <View style={styles.previewContent}>
                          <PreviewComponent 
                            widget={previewWidget} 
                            palette={palette} 
                            compact={false}
                            onComplete={() => {}}
                          />
                        </View>
                      )}
                    </View>
                  </AnimatedCard>
                );
              })}
            </View>
          )}
        </View>

        {/* Edit Schedule Modal */}
        <Modal
          visible={editingWidgetType !== null}
          transparent={true}
          animationType="fade"
          onRequestClose={handleCloseEditModal}
        >
          <View style={[styles.modalOverlay, { backgroundColor: 'rgba(0,0,0,0.75)' }]}>
            <View style={[styles.modalContainer, { 
              backgroundColor: palette.surface,
              borderColor: palette.border || 'rgba(255,255,255,0.16)'
            }]}>
              {/* Modal Header */}
              <View style={[styles.modalHeader, { borderBottomColor: palette.border || 'rgba(255,255,255,0.16)' }]}>
                <View>
                  <Text style={[styles.modalTitle, { color: palette.text || 'white' }]}>
                    {editingMatrixType?.title || 'Manage Widget'}
                  </Text>
                  <Text style={[styles.modalSubtitle, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>
                    Configure schedule settings
                  </Text>
                </View>
                <TouchableOpacity
                  style={[styles.modalCloseButton, { 
                    borderColor: palette.border || 'rgba(255,255,255,0.16)',
                    backgroundColor: palette.surface
                  }]}
                  onPress={handleCloseEditModal}
                >
                  <Text style={[styles.modalCloseButtonText, { color: palette.text || 'white' }]}>×</Text>
                </TouchableOpacity>
              </View>

              {/* Modal Content */}
              <ScrollView style={styles.modalContent}>
                {/* Status Toggle Section */}
                <View style={styles.modalSection}>
                  <Text style={[styles.modalSectionTitle, { color: palette.text || 'white' }]}>
                    Widget Status
                  </Text>
                  <Text style={[styles.modalSectionDescription, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>
                    Activate or deactivate this widget
                  </Text>
                  <View style={[styles.labelRow, { marginTop: 12, justifyContent: 'space-between', alignItems: 'center' }]}>
                    <Text style={[styles.label, { color: palette.text || 'white', fontSize: 14 }]}>
                      {tempStatus === 'Active' ? 'Active' : 'Inactive'}
                    </Text>
                    <ToggleSwitch
                      isOn={tempStatus === 'Active'}
                      onToggle={() => setTempStatus(tempStatus === 'Active' ? 'Inactive' : 'Active' )}
                    />
                  </View>
                </View>

                <View style={styles.modalSection}>
                  <Text style={[styles.modalSectionTitle, { color: palette.text || 'white' }]}>
                    Completion Frequency
                  </Text>
                  <Text style={[styles.modalSectionDescription, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>
                    Select how often this widget should be completed
                  </Text>
                  <View style={styles.selectContainer}>
                    {['daily', 'weekly', 'monthly'].map((freq) => (
                      <TouchableOpacity
                        key={freq}
                        style={[
                          styles.selectOption, 
                          editingScheduleType === freq && { backgroundColor: palette.primary || '#FF0600' },
                        ]}
                        onPress={() => {
                          const newSchedule = { 
                            ...tempSchedule, 
                            type: freq,
                            dayOfWeek: freq === 'weekly' ? (tempSchedule?.dayOfWeek !== undefined ? tempSchedule.dayOfWeek : 1) : 0,
                            dayOfMonth: freq === 'monthly' ? (tempSchedule?.dayOfMonth !== undefined ? tempSchedule.dayOfMonth : 1) : 0,
                          };
                          setTempSchedule(newSchedule);
                        }}
                      >
                        <Text style={[styles.selectText, { 
                          color: editingScheduleType === freq ? '#fff' : (palette.text || 'white'),
                          fontSize: 12 
                        }]}>
                          {freq.charAt(0).toUpperCase() + freq.slice(1).replace('-', ' ')}
                        </Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                </View>

                {/* Day of Week selector for weekly */}
                {editingScheduleType === 'weekly' && (
                  <View style={styles.modalSection}>
                    <Text style={[styles.modalSectionTitle, { color: palette.text || 'white' }]}>Day of Week</Text>
                    <Text style={[styles.modalSectionDescription, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>
                      Select which day of the week this widget should be completed
                    </Text>
                    <View style={styles.selectContainer}>
                      {dayNames.map((dayName, dayIndex) => (
                        <TouchableOpacity
                          key={dayIndex}
                          style={[
                            styles.selectOption,
                            editingDayOfWeek === dayIndex && { backgroundColor: palette.primary || '#FF0600' },
                          ]}
                          onPress={() => {
                            setTempSchedule({ ...tempSchedule, dayOfWeek: dayIndex });
                          }}
                        >
                          <Text style={[styles.selectText, {
                            color: editingDayOfWeek === dayIndex ? '#fff' : (palette.text || 'white'),
                            fontSize: 11
                          }]}>
                            {dayName.substring(0, 3)}
                          </Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                  </View>
                )}

                {/* Day of Month selector for monthly */}
                {editingScheduleType === 'monthly' && (
                  <View style={styles.modalSection}>
                    <Text style={[styles.modalSectionTitle, { color: palette.text || 'white' }]}>Day of Month (1-31)</Text>
                    <Text style={[styles.modalSectionDescription, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>
                      Select which day of the month this widget should be completed
                    </Text>
                    <View style={styles.selectContainer}>
                      {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                        <TouchableOpacity
                          key={day}
                          style={[
                            styles.selectOption,
                            editingDayOfMonth === day && { backgroundColor: palette.primary || '#FF0600' },
                          ]}
                          onPress={() => {
                            setTempSchedule({ ...tempSchedule, dayOfMonth: day });
                          }}
                        >
                          <Text style={[styles.selectText, {
                            color: editingDayOfMonth === day ? '#fff' : (palette.text || 'white'),
                            fontSize: 11
                          }]}>
                            {day}
                          </Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                  </View>
                )}
              </ScrollView>

              {/* Modal Footer */}
              <View style={[styles.modalFooter, { borderTopColor: palette.border || 'rgba(255,255,255,0.16)' }]}>
                <TouchableOpacity
                  style={[styles.modalCancelButton, { 
                    borderColor: palette.border || 'rgba(255,255,255,0.16)',
                    backgroundColor: 'transparent'
                  }]}
                  onPress={handleCloseEditModal}
                >
                  <Text style={[styles.modalCancelButtonText, { color: palette.text || 'white' }]}>Cancel</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.modalSaveButton, { backgroundColor: palette.primary || '#FF0600' }]}
                  onPress={handleSaveSchedule}
                  disabled={loading}
                >
                  <Text style={[styles.addButtonText, { color: '#fff' }]}>
                    {loading ? 'Saving...' : 'Save'}
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </Modal>
      </View>
    );
  };

  return (
    <View style={styles.container}>
      <View style={styles.layoutRow}>
        {/* Sidebar */}
        <AdminSidebar activeTab={activeTab} setActiveTab={setActiveTab} />

        {/* Main Content */}
        <ScrollView style={styles.mainCol} contentContainerStyle={styles.mainContent}>
          {activeTab === 'users' && renderUsersTab()}
          {activeTab === 'v12' && renderV12Tab()}
          {activeTab === 'courses' && renderCoursesTab()}
          {activeTab === 'pacing' && renderPacingTab()}
          {activeTab === 'widgets' && renderWidgetsTab()}
          {activeTab === 'persistent_widgets' && renderPersistentWidgetsTab()}
          {activeTab === 'glossary' && renderGlossaryTab()}
          {activeTab === 'metrics' && renderMetricsTab()}
          {activeTab === 'site_management' && renderSiteManagementTab()}
          {/* Legacy tabs for backwards compatibility */}
          {activeTab === 'maintenance' && renderMaintenanceTab()}
          {activeTab === 'development' && renderDevelopmentTab()}
        </ScrollView>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingLeft: 48,
    paddingRight: 48,
    paddingTop: 160,
    paddingBottom: 40,
    position: 'relative',
    ...Platform.select({
      web: {
        overflow: 'visible',
      },
    }),
  },
  layoutRow: {
    alignSelf: 'stretch',
    flexDirection: 'row',
    gap: 48,
    ...Platform.select({
      web: {
        overflow: 'visible',
      },
    }),
  },
  mainCol: {
    flex: 1,
    ...Platform.select({
      web: {
        overflow: 'visible',
      },
    }),
  },
  mainContent: {
    paddingBottom: 60,
    paddingRight: 12,
    ...Platform.select({
      web: {
        overflow: 'visible',
      },
    }),
  },
  tabContent: {
    paddingHorizontal: 24,
    gap: 24,
  },
  tabTitle: {
    fontSize: 36,
    fontFamily: 'Chivo',
    fontWeight: '900',
    lineHeight: 42,
    color: '#FFFFFF',
    textShadowColor: 'rgba(0, 0, 0, 0.4)',
    textShadowOffset: { width: 3, height: 3 },
    textShadowRadius: 0,
  },
  section: {
    width: '100%',
    paddingTop: 16,
    paddingBottom: 16,
    borderTopWidth: 1,
    gap: 16,
    paddingHorizontal: 24,
    borderRadius: 16,
  },
  sectionTitle: {
    fontSize: 24,
    fontFamily: 'Chivo',
    fontWeight: '900',
    lineHeight: 28,
    color: '#000000',
    textShadowColor: 'rgba(255, 255, 255, 0.3)',
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 0,
  },
  addUserForm: {
    gap: 16,
  },
  formRow: {
    flexDirection: 'row',
    gap: 16,
    flexWrap: 'wrap',
  },
  formCol: {
    flex: 1,
    minWidth: 200,
    gap: 4,
  },
  label: {
    fontSize: 13,
    fontFamily: 'Archivo',
    fontWeight: '600',
    lineHeight: 16,
  },
  input: {
    borderWidth: 1,
    borderRadius: 22,
    justifyContent: 'center',
  },
  selectContainer: {
    flexDirection: 'row',
    borderWidth: 1,
    borderRadius: 22,
    overflow: 'hidden',
  },
  selectOption: {
    flex: 1,
    paddingVertical: 8,
    paddingHorizontal: 12,
    alignItems: 'center',
  },
  selectText: {
    fontSize: 15,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },
  addButton: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 22,
    alignItems: 'center',
    alignSelf: 'flex-start',
    minWidth: 120,
  },
  addButtonText: {
    fontSize: 15,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },
  loadingContainer: {
    padding: 40,
    alignItems: 'center',
    gap: 12,
  },
  loadingText: {
    fontSize: 15,
    fontFamily: 'Archivo',
  },
  emptyText: {
    fontSize: 15,
    fontFamily: 'Archivo',
    textAlign: 'center',
    padding: 40,
  },
  usersList: {
    maxHeight: 600,
  },
  userCard: {
    borderWidth: 1,
    borderRadius: 16,
    padding: 16,
    marginBottom: 12,
    gap: 12,
    backgroundColor: 'transparent',
  },
  userInfo: {
    gap: 4,
  },
  userName: {
    fontSize: 17,
    fontFamily: 'Archivo',
    fontWeight: '800',
    lineHeight: 24,
  },
  userEmail: {
    fontSize: 15,
    fontFamily: 'Archivo',
    lineHeight: 20,
  },
  userMeta: {
    marginTop: 4,
  },
  userMetaText: {
    fontSize: 13,
    fontFamily: 'Archivo',
    lineHeight: 18,
  },
  userExtendedMeta: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 10,
  },
  commitmentBadge: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 6,
    borderWidth: 1,
  },
  commitmentText: {
    fontSize: 12,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },
  v12StatusBadge: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 6,
    borderWidth: 1,
  },
  v12StatusText: {
    fontSize: 12,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },
  // V12 Admin Tab Styles
  v12TabHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    marginBottom: 8,
  },
  v12HeaderBadge: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 6,
  },
  v12HeaderBadgeText: {
    fontSize: 16,
    fontFamily: 'Archivo',
    fontWeight: '900',
    letterSpacing: 2,
  },
  v12StatsGrid: {
    flexDirection: 'row',
    gap: 16,
    flexWrap: 'wrap',
  },
  v12StatCard: {
    flex: 1,
    minWidth: 140,
    padding: 20,
    borderRadius: 12,
    borderWidth: 2,
    alignItems: 'center',
  },
  v12StatNumber: {
    fontSize: 36,
    fontFamily: 'Archivo',
    fontWeight: '900',
  },
  v12StatLabel: {
    fontSize: 13,
    fontFamily: 'Archivo',
    fontWeight: '600',
    textAlign: 'center',
    marginTop: 4,
  },
  v12UserList: {
    maxHeight: 600,
  },
  v12UserCard: {
    flexDirection: 'row',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    marginBottom: 12,
    gap: 16,
  },
  v12UserInfo: {
    flex: 1,
    gap: 4,
  },
  v12UserName: {
    fontSize: 16,
    fontFamily: 'Archivo',
    fontWeight: '700',
  },
  v12UserEmail: {
    fontSize: 13,
    fontFamily: 'Archivo',
  },
  v12UserMeta: {
    marginTop: 6,
  },
  v12MetaLabel: {
    fontSize: 12,
    fontFamily: 'Archivo',
  },
  v12UserProgress: {
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 80,
  },
  v12CylinderCircle: {
    width: 56,
    height: 56,
    borderRadius: 28,
    borderWidth: 3,
    alignItems: 'center',
    justifyContent: 'center',
  },
  v12CylinderCount: {
    fontSize: 18,
    fontFamily: 'Archivo',
    fontWeight: '900',
  },
  v12CylinderLabel: {
    fontSize: 10,
    fontFamily: 'Archivo',
  },
  v12PhaseText: {
    fontSize: 12,
    fontFamily: 'Archivo',
    fontWeight: '600',
    marginTop: 6,
  },
  v12DiagnosticsCount: {
    fontSize: 10,
    fontFamily: 'Archivo',
  },
  v12NotStarted: {
    fontSize: 13,
    fontFamily: 'Archivo',
    fontStyle: 'italic',
  },
  v12UserDetails: {
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    marginTop: -8,
    marginBottom: 12,
    gap: 16,
  },
  v12ActionButtons: {
    flexDirection: 'row',
    gap: 12,
    flexWrap: 'wrap',
  },
  v12ActionBtn: {
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
  },
  v12ActionBtnText: {
    fontSize: 13,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },
  v12CylinderGrid: {
    gap: 8,
  },
  v12DetailsLabel: {
    fontSize: 13,
    fontFamily: 'Archivo',
    fontWeight: '600',
    marginBottom: 8,
  },
  v12CylinderMiniGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  v12CylinderMini: {
    width: 44,
    height: 32,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
  v12CylinderMiniText: {
    fontSize: 11,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },
  v12HistorySection: {
    gap: 8,
  },
  v12HistoryItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1,
  },
  v12HistoryLeft: {
    gap: 2,
  },
  v12HistoryDate: {
    fontSize: 13,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },
  v12HistoryPhase: {
    fontSize: 11,
    fontFamily: 'Archivo',
  },
  v12HistoryRight: {
    alignItems: 'flex-end',
    gap: 2,
  },
  v12HistoryScore: {
    fontSize: 13,
    fontFamily: 'Archivo',
    fontWeight: '700',
  },
  v12HistoryItems: {
    fontSize: 11,
    fontFamily: 'Archivo',
  },
  v12NoHistory: {
    fontSize: 13,
    fontFamily: 'Archivo',
    fontStyle: 'italic',
    textAlign: 'center',
    paddingVertical: 16,
  },
  userActions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  actionButton: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 22,
    borderWidth: 1,
    backgroundColor: 'transparent',
  },
  actionButtonText: {
    fontSize: 15,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },
  grantButton: {
    borderColor: '#4CAF50',
  },
  suspendButton: {
    borderColor: '#FF9800',
  },
  adminButton: {
    borderColor: '#9C27B0',
  },
  userButton: {
    borderColor: '#607D8B',
  },
  resetButton: {
    borderColor: '#2196F3',
  },
  deleteButton: {
    borderColor: '#EA806B',
  },
  deleteButtonText: {
    color: '#EA806B',
  },
  metricsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 24,
    marginTop: 16,
  },
  metricCard: {
    flex: 1,
    minWidth: 200,
    borderWidth: 1,
    borderRadius: 16,
    padding: 24,
    alignItems: 'center',
    gap: 8,
    backgroundColor: 'transparent',
  },
  metricNumber: {
    fontSize: 36,
    fontFamily: 'Chivo',
    fontWeight: '900',
  },
  metricLabel: {
    fontSize: 15,
    fontFamily: 'Archivo',
    textAlign: 'center',
  },
  placeholderText: {
    fontSize: 15,
    fontFamily: 'Archivo',
    lineHeight: 22,
  },
  maintenanceRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    justifyContent: 'space-between',
    paddingVertical: 12,
  },
  maintenanceInfo: {
    flex: 1,
    gap: 4,
  },
  maintenanceTitle: {
    fontSize: 17,
    fontFamily: 'Archivo',
    fontWeight: '800',
    lineHeight: 24,
  },
  maintenanceDesc: {
    fontSize: 15,
    fontFamily: 'Archivo',
    lineHeight: 20,
  },
  bannerStatus: {
    fontSize: 13,
    fontFamily: 'Archivo',
    marginTop: 4,
    fontStyle: 'italic',
  },
  // Site Management sub-tabs
  subTabNav: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 24,
    flexWrap: 'wrap',
  },
  subTabButton: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    borderWidth: 2,
    minWidth: 140,
    alignItems: 'center',
    ...Platform.select({
      web: {
        cursor: 'pointer',
        transition: 'all 0.2s ease',
      },
    }),
  },
  subTabButtonActive: {
    borderWidth: 2,
  },
  subTabButtonText: {
    fontSize: 15,
    fontFamily: 'Archivo',
    fontWeight: '700',
  },
  // Status grid for maintenance overview
  statusGrid: {
    flexDirection: 'row',
    gap: 16,
    flexWrap: 'wrap',
  },
  statusCard: {
    flex: 1,
    minWidth: 150,
    padding: 16,
    borderRadius: 12,
    borderWidth: 2,
    alignItems: 'center',
    gap: 8,
  },
  statusCardEmoji: {
    fontSize: 32,
  },
  statusCardTitle: {
    fontSize: 16,
    fontFamily: 'Archivo',
    fontWeight: '700',
  },
  statusCardStatus: {
    fontSize: 14,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },
  // Course styles
  formHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 24,
    gap: 16,
  },
  closeButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 22,
    borderWidth: 1,
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  closeButtonText: {
    fontSize: 14,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },
  formGroup: {
    marginBottom: 16,
    gap: 6,
  },
  textInput: {
    padding: 12,
    borderRadius: 22,
    borderWidth: 1,
    fontSize: 15,
    fontFamily: 'Archivo',
    ...Platform.select({
      web: {
        outlineStyle: 'none',
      },
    }),
  },
  textArea: {
    minHeight: 100,
    maxHeight: 400,
    paddingTop: 12,
    textAlignVertical: 'top',
    ...Platform.select({
      web: {
        resize: 'vertical',
      },
    }),
  },
  helperText: {
    fontSize: 13,
    fontFamily: 'Archivo',
    marginTop: 4,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  addChapterButton: {
    alignSelf: 'flex-start',
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  chapterCard: {
    borderWidth: 1,
    borderRadius: 16,
    padding: 16,
    marginBottom: 16,
  },
  chapterHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  chapterNumber: {
    fontSize: 16,
    fontFamily: 'Archivo',
    fontWeight: '700',
  },
  deleteText: {
    fontSize: 14,
    fontFamily: 'Archivo',
    fontWeight: '600',
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  formActions: {
    marginTop: 24,
    marginBottom: 40,
  },
  saveButton: {
    maxWidth: 200,
  },
  createButton: {
    alignSelf: 'flex-start',
  },
  coursesList: {
    marginTop: 16,
    gap: 16,
  },
  courseListCard: {
    borderWidth: 1,
    borderRadius: 16,
    padding: 20,
    gap: 16,
  },
  courseListHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 16,
  },
  courseListInfo: {
    flex: 1,
    gap: 8,
  },
  courseListTitle: {
    fontSize: 20,
    fontFamily: 'Archivo',
    fontWeight: '700',
    lineHeight: 26,
  },
  courseListDescription: {
    fontSize: 15,
    fontFamily: 'Archivo',
    lineHeight: 22,
  },
  courseListMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginTop: 8,
  },
  courseListMetaText: {
    fontSize: 14,
    fontFamily: 'Archivo',
  },
  courseListActions: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'center',
  },
  courseActionButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  courseStatus: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 16,
  },
  statusAvailable: {
    backgroundColor: '#0DC44A',
  },
  statusComingSoon: {
    backgroundColor: '#FF0600',
  },
  statusText: {
    fontSize: 12,
    fontFamily: 'Archivo',
    fontWeight: '700',
    color: '#fff',
  },
  // Module styles
  modulesSection: {
    marginTop: 20,
    paddingTop: 20,
    borderTopWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.1)',
  },
  modulesSectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  modulesTitle: {
    fontSize: 15,
    fontFamily: 'Archivo',
    fontWeight: '700',
  },
  addModuleButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 22,
    borderWidth: 1,
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  addModuleButtonText: {
    fontSize: 13,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },
  moduleCard: {
    borderWidth: 1,
    borderRadius: 12,
    padding: 12,
    marginBottom: 12,
  },
  moduleHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  moduleNumber: {
    fontSize: 14,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },
  emptyModulesText: {
    fontSize: 14,
    fontFamily: 'Archivo',
    fontStyle: 'italic',
    textAlign: 'center',
    paddingVertical: 20,
  },
  // ========== IMPROVED CHAPTERS & MODULES UI STYLES ==========
  chaptersHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 20,
    flexWrap: 'wrap',
    gap: 16,
  },
  chaptersHeaderLeft: {
    flex: 1,
    minWidth: 200,
  },
  chaptersSummary: {
    fontSize: 14,
    fontFamily: 'Archivo',
    marginTop: 4,
  },
  chaptersHeaderActions: {
    flexDirection: 'row',
    gap: 12,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  expandCollapseBtn: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    ...Platform.select({
      web: {
        cursor: 'pointer',
        transition: 'all 0.2s ease',
      },
    }),
  },
  expandCollapseBtnText: {
    fontSize: 13,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },
  addChapterBtnNew: {
    paddingHorizontal: 16,
    paddingVertical: 10,
  },
  emptyChaptersState: {
    padding: 40,
    borderRadius: 16,
    borderWidth: 1,
    borderStyle: 'dashed',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyChaptersIcon: {
    fontSize: 48,
    marginBottom: 16,
  },
  emptyChaptersTitle: {
    fontSize: 18,
    fontFamily: 'Archivo',
    fontWeight: '700',
    marginBottom: 8,
  },
  emptyChaptersDesc: {
    fontSize: 14,
    fontFamily: 'Archivo',
    textAlign: 'center',
    lineHeight: 22,
    maxWidth: 400,
  },
  // Chapter Accordion Styles
  chapterAccordion: {
    borderWidth: 2,
    borderRadius: 16,
    marginBottom: 16,
    overflow: 'hidden',
    ...Platform.select({
      web: {
        transition: 'border-color 0.2s ease',
      },
    }),
  },
  chapterAccordionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    gap: 12,
    ...Platform.select({
      web: {
        cursor: 'pointer',
        transition: 'background-color 0.2s ease',
      },
    }),
  },
  chapterDragHandle: {
    width: 20,
    alignItems: 'center',
    justifyContent: 'center',
    ...Platform.select({
      web: {
        cursor: 'grab',
      },
    }),
  },
  dragHandleIcon: {
    fontSize: 16,
    fontWeight: '900',
    letterSpacing: -2,
  },
  chapterNumberBadge: {
    width: 36,
    height: 36,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  chapterNumberBadgeText: {
    fontSize: 16,
    fontFamily: 'Archivo',
    fontWeight: '900',
    color: '#fff',
  },
  chapterAccordionInfo: {
    flex: 1,
    gap: 4,
  },
  chapterTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
  },
  chapterAccordionTitle: {
    fontSize: 17,
    fontFamily: 'Archivo',
    fontWeight: '700',
    flex: 1,
  },
  chapterStatusPill: {
    paddingHorizontal: 10,
    paddingVertical: 3,
    borderRadius: 12,
  },
  chapterStatusPillText: {
    fontSize: 11,
    fontFamily: 'Archivo',
    fontWeight: '700',
    color: '#fff',
  },
  chapterMetaRow: {
    flexDirection: 'row',
    gap: 16,
    flexWrap: 'wrap',
  },
  chapterMetaItem: {
    fontSize: 13,
    fontFamily: 'Archivo',
  },
  chapterExpandIcon: {
    fontSize: 14,
    fontWeight: '700',
    width: 24,
    textAlign: 'center',
  },
  chapterAccordionContent: {
    padding: 20,
    borderTopWidth: 1,
  },
  chapterQuickEdit: {
    marginBottom: 24,
  },
  chapterQuickEditRow: {
    flexDirection: 'row',
    gap: 16,
    marginBottom: 16,
    flexWrap: 'wrap',
  },
  chapterQuickEditField: {
    flex: 1,
    minWidth: 200,
  },
  chapterQuickEditStatus: {
    minWidth: 200,
  },
  quickEditLabel: {
    fontSize: 12,
    fontFamily: 'Archivo',
    fontWeight: '600',
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  quickEditInput: {
    padding: 12,
    borderRadius: 10,
    borderWidth: 1,
    fontSize: 15,
    fontFamily: 'Archivo',
    ...Platform.select({
      web: {
        outlineStyle: 'none',
      },
    }),
  },
  quickEditTextarea: {
    padding: 12,
    borderRadius: 10,
    borderWidth: 1,
    fontSize: 14,
    fontFamily: 'Archivo',
    minHeight: 80,
    textAlignVertical: 'top',
    ...Platform.select({
      web: {
        outlineStyle: 'none',
        resize: 'vertical',
      },
    }),
  },
  statusToggle: {
    flexDirection: 'row',
    borderRadius: 10,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.2)',
  },
  statusToggleBtn: {
    flex: 1,
    paddingVertical: 10,
    paddingHorizontal: 16,
    alignItems: 'center',
  },
  statusToggleBtnActive: {},
  statusToggleBtnText: {
    fontSize: 13,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },
  chapterDescField: {
    width: '100%',
  },
  // Modules Container (inside expanded chapter)
  modulesContainer: {
    marginBottom: 20,
  },
  modulesHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.08)',
  },
  modulesHeaderTitle: {
    fontSize: 15,
    fontFamily: 'Archivo',
    fontWeight: '700',
  },
  addModuleBtnNew: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 8,
  },
  addModuleBtnNewText: {
    fontSize: 13,
    fontFamily: 'Archivo',
    fontWeight: '600',
    color: '#fff',
  },
  // Module Accordion Styles
  moduleAccordion: {
    borderWidth: 1,
    borderRadius: 12,
    marginBottom: 10,
    overflow: 'hidden',
    ...Platform.select({
      web: {
        transition: 'border-color 0.2s ease',
      },
    }),
  },
  moduleAccordionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    gap: 10,
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  moduleDragHandle: {
    width: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  moduleNumberBadge: {
    width: 28,
    height: 28,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  moduleNumberBadgeText: {
    fontSize: 13,
    fontFamily: 'Archivo',
    fontWeight: '700',
  },
  moduleAccordionInfo: {
    flex: 1,
    gap: 4,
  },
  moduleAccordionTitle: {
    fontSize: 14,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },
  moduleStatusIcons: {
    flexDirection: 'row',
    gap: 6,
  },
  moduleStatusIcon: {
    fontSize: 12,
  },
  moduleStatusEmpty: {
    fontSize: 12,
    fontFamily: 'Archivo',
    fontStyle: 'italic',
  },
  moduleExpandIcon: {
    fontSize: 18,
    fontWeight: '600',
    width: 24,
    textAlign: 'center',
  },
  moduleAccordionContent: {
    padding: 16,
    borderTopWidth: 1,
  },
  moduleFormGroup: {
    marginBottom: 16,
  },
  moduleFormLabel: {
    fontSize: 13,
    fontFamily: 'Archivo',
    fontWeight: '600',
    marginBottom: 6,
  },
  moduleFormLabelSmall: {
    fontSize: 12,
    fontFamily: 'Archivo',
    fontWeight: '500',
    marginBottom: 6,
  },
  moduleFormInput: {
    padding: 12,
    borderRadius: 10,
    borderWidth: 1,
    fontSize: 14,
    fontFamily: 'Archivo',
    ...Platform.select({
      web: {
        outlineStyle: 'none',
      },
    }),
  },
  moduleFormTextarea: {
    padding: 12,
    borderRadius: 10,
    borderWidth: 1,
    fontSize: 14,
    fontFamily: 'Archivo',
    minHeight: 120,
    textAlignVertical: 'top',
    ...Platform.select({
      web: {
        outlineStyle: 'none',
        resize: 'vertical',
      },
    }),
  },
  moduleFormSection: {
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    marginBottom: 16,
  },
  moduleFormSectionTitle: {
    fontSize: 14,
    fontFamily: 'Archivo',
    fontWeight: '700',
    marginBottom: 12,
  },
  videoStatus: {
    padding: 10,
    borderRadius: 8,
    marginTop: 8,
  },
  moduleSettingsRow: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 16,
    flexWrap: 'wrap',
  },
  moduleSettingToggle: {
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 10,
    borderWidth: 1,
    ...Platform.select({
      web: {
        cursor: 'pointer',
        transition: 'all 0.2s ease',
      },
    }),
  },
  moduleSettingToggleActive: {},
  moduleSettingToggleText: {
    fontSize: 13,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },
  // Widgets Section Inside Module
  widgetsSection: {
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    marginBottom: 16,
    backgroundColor: 'rgba(255,255,255,0.01)',
  },
  widgetsSectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  widgetsSectionTitle: {
    fontSize: 14,
    fontFamily: 'Archivo',
    fontWeight: '700',
  },
  addWidgetBtn: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1,
  },
  addWidgetBtnText: {
    fontSize: 12,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },
  widgetsList: {
    gap: 8,
  },
  widgetItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 10,
    borderRadius: 8,
    borderWidth: 1,
    gap: 8,
  },
  widgetItemMain: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  widgetSelector: {
    flex: 1,
    minWidth: 150,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    borderWidth: 1,
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  widgetSelectorText: {
    fontSize: 13,
    fontFamily: 'Archivo',
    flex: 1,
  },
  widgetPlacementBtns: {
    flexDirection: 'row',
    gap: 4,
  },
  widgetPlacementBtn: {
    width: 28,
    height: 28,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.05)',
  },
  widgetPlacementBtnText: {
    fontSize: 14,
    fontWeight: '600',
  },
  removeWidgetBtn: {
    width: 28,
    height: 28,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(234, 128, 107, 0.1)',
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  noWidgetsText: {
    fontSize: 13,
    fontFamily: 'Archivo',
    fontStyle: 'italic',
    textAlign: 'center',
    paddingVertical: 12,
  },
  deleteModuleBtn: {
    alignSelf: 'flex-start',
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    ...Platform.select({
      web: {
        cursor: 'pointer',
        transition: 'all 0.2s ease',
      },
    }),
  },
  // Empty Modules State (inside chapter)
  emptyModulesState: {
    padding: 24,
    borderRadius: 12,
    borderWidth: 1,
    borderStyle: 'dashed',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyModulesIcon: {
    fontSize: 32,
    marginBottom: 8,
  },
  emptyModulesTitle: {
    fontSize: 15,
    fontFamily: 'Archivo',
    fontWeight: '600',
    marginBottom: 4,
  },
  emptyModulesDesc: {
    fontSize: 13,
    fontFamily: 'Archivo',
    textAlign: 'center',
    lineHeight: 20,
  },
  // Chapter Actions Bar
  chapterActionsBar: {
    paddingTop: 16,
    borderTopWidth: 1,
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  chapterDeleteBtn: {
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 8,
    ...Platform.select({
      web: {
        cursor: 'pointer',
        transition: 'all 0.2s ease',
      },
    }),
  },
  // ========== END IMPROVED CHAPTERS & MODULES UI STYLES ==========
  
  // ========== COURSE LIST VIEW STYLES ==========
  courseStatsRow: {
    flexDirection: 'row',
    gap: 12,
    flexWrap: 'wrap',
  },
  courseStatCard: {
    flex: 1,
    minWidth: 100,
    padding: 16,
    borderRadius: 16,
    borderWidth: 1,
    alignItems: 'center',
    gap: 4,
  },
  courseStatNumber: {
    fontSize: 28,
    fontFamily: 'Chivo',
    fontWeight: '900',
  },
  courseStatLabel: {
    fontSize: 12,
    fontFamily: 'Archivo',
    fontWeight: '500',
  },
  emptyCoursesState: {
    padding: 48,
    borderRadius: 16,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyCoursesIcon: {
    fontSize: 48,
    marginBottom: 16,
  },
  emptyCoursesTitle: {
    fontSize: 20,
    fontFamily: 'Archivo',
    fontWeight: '700',
    marginBottom: 8,
  },
  emptyCoursesDesc: {
    fontSize: 14,
    fontFamily: 'Archivo',
    textAlign: 'center',
    lineHeight: 22,
    maxWidth: 360,
  },
  coursesGridNew: {
    gap: 16,
  },
  courseCardNew: {
    borderWidth: 1,
    borderRadius: 16,
    overflow: 'hidden',
  },
  courseCardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    paddingBottom: 0,
  },
  courseCardHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  courseStatusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 20,
  },
  courseStatusDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  courseStatusText: {
    fontSize: 12,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },
  courseOrderBadge: {
    fontSize: 13,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },
  courseCardBody: {
    padding: 16,
    gap: 8,
  },
  courseCardTitle: {
    fontSize: 18,
    fontFamily: 'Archivo',
    fontWeight: '700',
    lineHeight: 24,
  },
  courseCardDesc: {
    fontSize: 14,
    fontFamily: 'Archivo',
    lineHeight: 20,
  },
  courseCardStatsRow: {
    flexDirection: 'row',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderTopWidth: 1,
  },
  courseCardStatItem: {
    flex: 1,
    alignItems: 'center',
    gap: 2,
  },
  courseCardStatValue: {
    fontSize: 18,
    fontFamily: 'Archivo',
    fontWeight: '700',
  },
  courseCardStatLabel: {
    fontSize: 11,
    fontFamily: 'Archivo',
    fontWeight: '500',
  },
  courseCardStatDivider: {
    width: 1,
    height: '100%',
  },
  courseCardActions: {
    flexDirection: 'row',
    padding: 12,
    gap: 10,
    borderTopWidth: 1,
  },
  courseEditBtn: {
    flex: 1,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 22,
    alignItems: 'center',
    justifyContent: 'center',
  },
  courseEditBtnText: {
    color: '#fff',
    fontSize: 14,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },
  courseDeleteBtn: {
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 22,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  courseDeleteBtnText: {
    fontSize: 14,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },
  // ========== END COURSE LIST VIEW STYLES ==========
  
  // ========== MODULE EDITOR STYLES ==========
  moduleEditorLayout: {
    flexDirection: 'row',
    gap: 20,
    ...Platform.select({
      web: {
        flexWrap: 'wrap',
      },
    }),
  },
  moduleEditorMain: {
    flex: 2,
    minWidth: 280,
    gap: 16,
  },
  moduleEditorSidebar: {
    flex: 1,
    minWidth: 220,
    gap: 16,
  },
  moduleInlineEdit: {
    marginBottom: 8,
  },
  moduleTitleInline: {
    fontSize: 18,
    fontFamily: 'Archivo',
    fontWeight: '700',
    padding: 8,
    paddingLeft: 0,
    ...Platform.select({
      web: {
        outlineStyle: 'none',
      },
    }),
  },
  moduleTitleUnderline: {
    height: 2,
    backgroundColor: 'rgba(255, 71, 71, 0.4)',
    marginTop: -4,
  },
  contentEditorCard: {
    borderRadius: 16,
    borderWidth: 1,
    overflow: 'hidden',
  },
  contentEditorHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 14,
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.08)',
  },
  contentEditorTitle: {
    fontSize: 14,
    fontFamily: 'Archivo',
    fontWeight: '700',
  },
  contentEditorHint: {
    fontSize: 11,
    fontFamily: 'Archivo',
  },
  contentEditorTextarea: {
    padding: 14,
    fontSize: 14,
    fontFamily: 'Archivo',
    lineHeight: 22,
    minHeight: 180,
    textAlignVertical: 'top',
    ...Platform.select({
      web: {
        outlineStyle: 'none',
        resize: 'vertical',
      },
    }),
  },
  contentPreviewBox: {
    padding: 14,
    borderTopWidth: 1,
    backgroundColor: 'rgba(255,255,255,0.02)',
  },
  contentPreviewLabel: {
    fontSize: 10,
    fontFamily: 'Archivo',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 6,
  },
  contentPreviewText: {
    fontSize: 13,
    fontFamily: 'Archivo',
    lineHeight: 20,
  },
  mediaSection: {
    flexDirection: 'row',
    gap: 12,
    flexWrap: 'wrap',
  },
  mediaCard: {
    flex: 1,
    minWidth: 180,
    borderRadius: 16,
    borderWidth: 1,
    padding: 14,
    gap: 10,
  },
  mediaCardSmall: {
    flex: 0,
    minWidth: 140,
    maxWidth: 180,
  },
  mediaCardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  mediaCardTitle: {
    fontSize: 13,
    fontFamily: 'Archivo',
    fontWeight: '700',
  },
  mediaStatusBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  videoPreviewContainer: {
    borderRadius: 8,
    overflow: 'hidden',
  },
  videoPreviewEmbed: {
    height: 80,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  videoPreviewIcon: {
    fontSize: 28,
  },
  videoPreviewUrl: {
    fontSize: 10,
    fontFamily: 'Archivo',
    paddingHorizontal: 10,
  },
  mediaUploadRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  mediaOrText: {
    fontSize: 11,
    fontFamily: 'Archivo',
  },
  uploadProgressBar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  uploadProgressTrack: {
    flex: 1,
    height: 3,
    backgroundColor: 'rgba(255,255,255,0.1)',
    borderRadius: 2,
    overflow: 'hidden',
  },
  uploadProgressFill: {
    height: '100%',
  },
  uploadProgressText: {
    fontSize: 11,
    fontFamily: 'Archivo',
    fontWeight: '600',
    minWidth: 36,
  },
  mediaUrlInput: {
    padding: 10,
    borderRadius: 22,
    borderWidth: 1,
    fontSize: 13,
    fontFamily: 'Archivo',
    ...Platform.select({
      web: {
        outlineStyle: 'none',
      },
    }),
  },
  imagePreviewContainer: {
    borderRadius: 8,
    overflow: 'hidden',
  },
  imagePlaceholder: {
    height: 50,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  imagePlaceholderIcon: {
    fontSize: 20,
    opacity: 0.3,
  },
  settingsCard: {
    borderRadius: 16,
    borderWidth: 1,
    padding: 14,
    gap: 10,
  },
  settingsCardTitle: {
    fontSize: 13,
    fontFamily: 'Archivo',
    fontWeight: '700',
    marginBottom: 4,
  },
  settingToggleNew: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 12,
    borderRadius: 22,
    borderWidth: 1,
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  settingToggleLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  settingToggleIcon: {
    fontSize: 18,
  },
  settingToggleLabel: {
    fontSize: 13,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },
  settingToggleHint: {
    fontSize: 10,
    fontFamily: 'Archivo',
    marginTop: 2,
  },
  settingToggleSwitch: {
    width: 36,
    height: 20,
    borderRadius: 10,
    padding: 2,
  },
  settingToggleSwitchKnob: {
    width: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: '#fff',
  },
  widgetsCard: {
    borderRadius: 16,
    borderWidth: 1,
    padding: 14,
    gap: 10,
  },
  widgetsCardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  widgetsCardTitle: {
    fontSize: 13,
    fontFamily: 'Archivo',
    fontWeight: '700',
  },
  widgetsCardCount: {
    fontSize: 11,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },
  widgetsListNew: {
    gap: 6,
  },
  widgetItemNew: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 22,
    borderWidth: 1,
    overflow: 'hidden',
  },
  widgetItemContent: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 8,
    paddingHorizontal: 12,
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  widgetItemName: {
    fontSize: 12,
    fontFamily: 'Archivo',
    fontWeight: '500',
    flex: 1,
  },
  widgetItemMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  widgetItemPlacement: {
    fontSize: 12,
    fontWeight: '700',
  },
  widgetItemRemove: {
    width: 28,
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(234, 128, 107, 0.1)',
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  noWidgetsTextNew: {
    fontSize: 12,
    fontFamily: 'Archivo',
    fontStyle: 'italic',
    textAlign: 'center',
    paddingVertical: 6,
  },
  addWidgetBtnNew: {
    padding: 10,
    borderRadius: 22,
    borderWidth: 1,
    borderStyle: 'dashed',
    alignItems: 'center',
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  addWidgetBtnNewText: {
    fontSize: 12,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },
  deleteModuleBtnNew: {
    padding: 10,
    borderRadius: 22,
    borderWidth: 1,
    alignItems: 'center',
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  // ========== END MODULE EDITOR STYLES ==========
  
  // Widget builder styles
  labelRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  addItemButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
  },
  addItemButtonText: {
    fontSize: 13,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },
  columnRow: {
    flexDirection: 'row',
    gap: 12,
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    marginBottom: 12,
    alignItems: 'flex-start',
  },
  emptyState: {
    padding: 24,
    borderRadius: 8,
    borderWidth: 1,
    borderStyle: 'dashed',
    alignItems: 'center',
  },
  emptyStateText: {
    fontSize: 14,
    fontFamily: 'Archivo',
    textAlign: 'center',
  },
  questionCard: {
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    marginBottom: 16,
  },
  questionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  questionNumber: {
    fontSize: 16,
    fontFamily: 'Archivo',
    fontWeight: '700',
  },
  answersLabel: {
    fontSize: 13,
    fontFamily: 'Archivo',
    fontWeight: '600',
    marginBottom: 8,
  },
  answerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 8,
  },
  radioButton: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioCheck: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '700',
  },
  addAnswerButton: {
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderStyle: 'dashed',
    alignItems: 'center',
    marginTop: 8,
  },
  addAnswerButtonText: {
    fontSize: 13,
    fontFamily: 'Archivo',
    fontWeight: '500',
  },
  // Glossary styles
  glossaryList: {
    gap: 16,
  },
  glossaryCard: {
    borderWidth: 1,
    borderRadius: 12,
    padding: 20,
  },
  glossaryCardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
    gap: 16,
  },
  glossaryCardInfo: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap',
  },
  glossaryTerm: {
    fontSize: 20,
    fontFamily: 'Archivo',
    fontWeight: '700',
  },
  categoryBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    borderWidth: 1,
  },
  categoryBadgeText: {
    fontSize: 11,
    fontFamily: 'Archivo',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  glossaryCardActions: {
    flexDirection: 'row',
    gap: 8,
  },
  iconButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  iconButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  glossaryDefinition: {
    fontSize: 15,
    fontFamily: 'Archivo',
    lineHeight: 22,
  },
  exampleBox: {
    marginTop: 12,
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
  },
  exampleLabel: {
    fontSize: 12,
    fontFamily: 'Archivo',
    fontWeight: '600',
    marginBottom: 4,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  exampleText: {
    fontSize: 14,
    fontFamily: 'Archivo',
    fontStyle: 'italic',
    lineHeight: 20,
  },
  toggleButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    minWidth: 80,
    alignItems: 'center',
  },
  toggleButtonText: {
    fontSize: 13,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },
  // New Widget Management Styles
  subtitle: {
    fontSize: 14,
    fontFamily: 'Archivo',
    marginTop: 4,
  },
  filterChipsContainer: {
    paddingVertical: 20,
    paddingHorizontal: 24,
    borderBottomWidth: 1,
    marginBottom: 24,
  },
  filterChipsContent: {
    gap: 12,
    paddingRight: 24,
  },
  filterChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 24,
    borderWidth: 2,
  },
  filterChipIcon: {
    fontSize: 18,
  },
  filterChipText: {
    fontSize: 14,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },
  filterChipBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 12,
    minWidth: 24,
    alignItems: 'center',
  },
  filterChipBadgeText: {
    fontSize: 12,
    fontFamily: 'Archivo',
    fontWeight: '700',
  },
  emptyStateContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 80,
    paddingHorizontal: 40,
  },
  emptyStateIcon: {
    fontSize: 64,
    marginBottom: 16,
  },
  emptyStateTitle: {
    fontSize: 20,
    fontFamily: 'Archivo',
    fontWeight: '700',
    marginBottom: 8,
    textAlign: 'center',
  },
  emptyStateDescription: {
    fontSize: 15,
    fontFamily: 'Archivo',
    textAlign: 'center',
    marginBottom: 24,
    lineHeight: 22,
  },
  emptyStateButton: {
    paddingHorizontal: 24,
    paddingVertical: 12,
  },
  emptyStateButtonText: {
    fontSize: 15,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },
  widgetGrid: {
    flex: 1,
    paddingHorizontal: 24,
  },
  widgetGridContent: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 24,
    paddingBottom: 40,
  },
  widgetCard: {
    width: Platform.OS === 'web' ? 'calc(33.333% - 16px)' : '100%',
    minWidth: 320,
    borderRadius: 16,
    borderWidth: 2,
    overflow: 'hidden',
  },
  widgetCardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    paddingBottom: 16,
  },
  widgetIconContainer: {
    width: 56,
    height: 56,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },
  widgetIcon: {
    fontSize: 28,
  },
  widgetTypeBadge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
  },
  widgetTypeBadgeText: {
    fontSize: 12,
    fontFamily: 'Archivo',
    fontWeight: '700',
    color: '#fff',
    textTransform: 'capitalize',
  },
  widgetCardContent: {
    paddingHorizontal: 20,
    paddingBottom: 16,
    minHeight: 100,
  },
  widgetCardTitle: {
    fontSize: 18,
    fontFamily: 'Archivo',
    fontWeight: '700',
    marginBottom: 8,
    lineHeight: 24,
  },
  widgetCardDescription: {
    fontSize: 14,
    fontFamily: 'Archivo',
    lineHeight: 20,
  },
  widgetUsageSection: {
    padding: 20,
    borderTopWidth: 1,
    minHeight: 100,
  },
  widgetUsageHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  widgetUsageTitle: {
    fontSize: 13,
    fontFamily: 'Archivo',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  widgetUsageCount: {
    fontSize: 14,
    fontFamily: 'Archivo',
    fontWeight: '700',
  },
  widgetUsageList: {
    gap: 8,
  },
  widgetUsageItem: {
    marginBottom: 6,
  },
  widgetUsageCourse: {
    fontSize: 14,
    fontFamily: 'Archivo',
    fontWeight: '600',
    marginBottom: 2,
  },
  widgetUsageDetails: {
    fontSize: 12,
    fontFamily: 'Archivo',
  },
  widgetUsageMore: {
    fontSize: 12,
    fontFamily: 'Archivo',
    fontStyle: 'italic',
    marginTop: 4,
  },
  widgetUsageEmpty: {
    fontSize: 13,
    fontFamily: 'Archivo',
    fontStyle: 'italic',
  },
  widgetCardActions: {
    flexDirection: 'row',
    gap: 12,
    padding: 20,
    paddingTop: 16,
  },
  widgetActionButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 10,
    borderWidth: 2,
    alignItems: 'center',
  },
  widgetEditButton: {
    // borderColor set inline
  },
  widgetDeleteButton: {
    // borderColor set inline
  },
  widgetActionButtonText: {
    fontSize: 14,
    fontFamily: 'Archivo',
    fontWeight: '700',
  },
  // Widget Type Grid Styles
  helperText: {
    fontSize: 13,
    fontFamily: 'Archivo',
    marginTop: 4,
    marginBottom: 16,
    lineHeight: 18,
  },
  widgetTypeGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
    marginTop: 8,
  },
  widgetTypeCard: {
    width: Platform.OS === 'web' ? 'calc(20% - 13px)' : '18%',
    minWidth: 140,
    minHeight: 180,
    borderRadius: 16,
    borderWidth: 2,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
    transition: 'all 0.2s ease',
  },
  widgetTypeEmoji: {
    fontSize: 40,
    marginBottom: 12,
  },
  widgetTypeName: {
    fontSize: 14,
    fontFamily: 'Archivo',
    fontWeight: '700',
    textAlign: 'center',
    lineHeight: 18,
    marginBottom: 6,
  },
  widgetTypeDescription: {
    fontSize: 11,
    fontFamily: 'Archivo',
    textAlign: 'center',
    lineHeight: 14,
    paddingHorizontal: 4,
  },
  // Course Pacing Styles
  courseSelectionGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 20,
    marginTop: 16,
  },
  courseSelectionCard: {
    width: Platform.OS === 'web' ? 'calc(33.333% - 14px)' : '100%',
    minWidth: 280,
    padding: 20,
    borderRadius: 16,
    borderWidth: 2,
    transition: 'all 0.2s ease',
  },
  courseSelectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  courseSelectionTitle: {
    fontSize: 18,
    fontFamily: 'Archivo',
    fontWeight: '700',
    flex: 1,
    marginRight: 12,
    lineHeight: 24,
  },
  selectedBadge: {
    width: 28,
    height: 28,
    borderRadius: 14,
    justifyContent: 'center',
    alignItems: 'center',
  },
  selectedBadgeText: {
    fontSize: 16,
    color: '#fff',
    fontWeight: '700',
  },
  courseSelectionMeta: {
    flexDirection: 'row',
    gap: 16,
  },
  courseSelectionMetaText: {
    fontSize: 13,
    fontFamily: 'Archivo',
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 24,
  },
  previewButton: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 10,
    borderWidth: 2,
  },
  previewButtonText: {
    fontSize: 14,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },
  timelineContainer: {
    padding: 32,
    borderRadius: 20,
    borderWidth: 2,
    marginTop: 8,
  },
  timelineHeader: {
    marginBottom: 32,
  },
  timelineHeaderText: {
    fontSize: 20,
    fontFamily: 'Archivo',
    fontWeight: '700',
    marginBottom: 6,
  },
  timelineSubtext: {
    fontSize: 14,
    fontFamily: 'Archivo',
    lineHeight: 20,
  },
  timelineLine: {
    position: 'relative',
    paddingLeft: 40,
  },
  timelineMarker: {
    position: 'relative',
    marginBottom: 32,
  },
  timelineDot: {
    position: 'absolute',
    left: -40,
    top: 8,
    width: 16,
    height: 16,
    borderRadius: 8,
    zIndex: 2,
  },
  timelineCard: {
    padding: 24,
    borderRadius: 16,
    borderWidth: 2,
    borderLeftWidth: 4,
  },
  timelineCardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  timelineCardDay: {
    fontSize: 16,
    fontFamily: 'Archivo',
    fontWeight: '700',
    letterSpacing: 0.5,
  },
  timelineCardBadge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 12,
  },
  timelineCardBadgeText: {
    fontSize: 11,
    fontFamily: 'Archivo',
    fontWeight: '700',
    letterSpacing: 0.5,
  },
  editRulesButton: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
  },
  editRulesButtonText: {
    fontSize: 12,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },
  timelineCardTitle: {
    fontSize: 20,
    fontFamily: 'Archivo',
    fontWeight: '700',
    marginBottom: 16,
    lineHeight: 26,
  },
  timelineCardDescription: {
    fontSize: 14,
    fontFamily: 'Archivo',
    lineHeight: 20,
    marginBottom: 16,
  },
  timelineRules: {
    gap: 16,
    marginBottom: 16,
  },
  timelineRule: {
    flexDirection: 'row',
    gap: 12,
    alignItems: 'flex-start',
  },
  timelineRuleIcon: {
    fontSize: 20,
    marginTop: 2,
  },
  timelineRuleLabel: {
    fontSize: 12,
    fontFamily: 'Archivo',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 4,
  },
  timelineRuleValue: {
    fontSize: 15,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },
  modulesList: {
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.1)',
  },
  modulesLabel: {
    fontSize: 13,
    fontFamily: 'Archivo',
  },
  infoBox: {
    flexDirection: 'row',
    gap: 16,
    padding: 20,
    borderRadius: 12,
    borderWidth: 2,
    marginTop: 24,
  },
  infoBoxIcon: {
    fontSize: 28,
  },
  infoBoxContent: {
    flex: 1,
  },
  infoBoxTitle: {
    fontSize: 16,
    fontFamily: 'Archivo',
    fontWeight: '700',
    marginBottom: 6,
  },
  infoBoxText: {
    fontSize: 14,
    fontFamily: 'Archivo',
    lineHeight: 20,
  },
  // Inline Edit Styles
  inlineEditContainer: {
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.1)',
    marginTop: 16,
  },
  inlineEditSection: {
    marginBottom: 20,
  },
  inlineEditLabel: {
    fontSize: 15,
    fontFamily: 'Archivo',
    fontWeight: '700',
    marginBottom: 12,
  },
  inlineRuleOptions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  inlineRuleOption: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 2,
  },
  inlineRuleOptionText: {
    fontSize: 13,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },
  inlineDaysInput: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginTop: 12,
  },
  // Modal Styles (kept for other modals)
  modalOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.75)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modalContainer: {
    width: Platform.OS === 'web' ? 700 : '90%',
    maxHeight: '90%',
    borderRadius: 20,
    borderWidth: 2,
    overflow: 'hidden',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: 24,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.1)',
  },
  modalTitle: {
    fontSize: 24,
    fontFamily: 'Archivo',
    fontWeight: '700',
    marginBottom: 4,
  },
  modalSubtitle: {
    fontSize: 15,
    fontFamily: 'Archivo',
  },
  modalCloseButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    borderWidth: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalCloseButtonText: {
    fontSize: 20,
    fontWeight: '600',
  },
  modalContent: {
    maxHeight: 500,
    padding: 24,
  },
  modalSection: {
    marginBottom: 32,
  },
  modalSectionTitle: {
    fontSize: 18,
    fontFamily: 'Archivo',
    fontWeight: '700',
    marginBottom: 6,
  },
  modalSectionDescription: {
    fontSize: 14,
    fontFamily: 'Archivo',
    marginBottom: 16,
    lineHeight: 20,
  },
  ruleOptions: {
    gap: 12,
  },
  ruleOption: {
    padding: 20,
    borderRadius: 12,
    borderWidth: 1,
  },
  ruleOptionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 8,
  },
  ruleOptionIcon: {
    fontSize: 24,
  },
  ruleOptionTitle: {
    fontSize: 16,
    fontFamily: 'Archivo',
    fontWeight: '700',
  },
  ruleOptionDescription: {
    fontSize: 14,
    fontFamily: 'Archivo',
    lineHeight: 20,
  },
  ruleOptionInput: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginTop: 16,
  },
  daysInput: {
    width: 80,
    height: 44,
    borderRadius: 8,
    borderWidth: 2,
    paddingHorizontal: 16,
    fontSize: 16,
    fontFamily: 'Archivo',
    fontWeight: '600',
    textAlign: 'center',
  },
  daysInputLabel: {
    fontSize: 14,
    fontFamily: 'Archivo',
  },
  modalFooter: {
    flexDirection: 'row',
    gap: 12,
    padding: 24,
    borderTopWidth: 1,
  },
  modalCancelButton: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 10,
    borderWidth: 2,
    alignItems: 'center',
  },
  modalCancelButtonText: {
    fontSize: 15,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },
  modalSaveButton: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 10,
    borderWidth: 2,
    alignItems: 'center',
  },
  statusText: {
    fontSize: 14,
    fontFamily: 'Archivo',
    fontWeight: '600',
    marginTop: 8,
  },
  emulatorInstructions: {
    fontSize: 12,
    fontFamily: 'Archivo',
    marginTop: 4,
    fontStyle: 'italic',
  },
  endpointsList: {
    marginTop: 12,
  },
  endpointItem: {
    fontSize: 13,
    fontFamily: 'Archivo',
    marginBottom: 4,
  },
  emulatorButton: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    alignItems: 'center',
    marginTop: 16,
  },
  emulatorButtonText: {
    fontSize: 14,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },
  instructionText: {
    fontSize: 14,
    fontFamily: 'Archivo',
    marginBottom: 12,
  },
  instructionStep: {
    fontSize: 13,
    fontFamily: 'Archivo',
    marginBottom: 6,
    paddingLeft: 8,
  },
  testEmailForm: {
    gap: 16,
    marginTop: 16,
  },
  testEmailButtons: {
    flexDirection: 'row',
    gap: 12,
    flexWrap: 'wrap',
  },
  testEmailButton: {
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 22,
    alignItems: 'center',
    minWidth: 160,
    height: 44,
    justifyContent: 'center',
  },
  testEmailButtonText: {
    fontSize: 14,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },
  testEmailNote: {
    fontSize: 12,
    fontFamily: 'Archivo',
    marginTop: 12,
    fontStyle: 'italic',
    lineHeight: 16,
  },
  connectionStatus: {
    fontSize: 14,
    fontFamily: 'Archivo',
    fontWeight: '600',
    marginBottom: 8,
  },
  emailTestSection: {
    marginBottom: 24,
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
  },
  emailTestTitle: {
    fontSize: 16,
    fontFamily: 'Chivo',
    fontWeight: '700',
    marginBottom: 8,
  },
  emailTestSubtitle: {
    fontSize: 13,
    fontFamily: 'Archivo',
    marginBottom: 12,
  },
  frequencySelector: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 16,
    flexWrap: 'wrap',
  },
  frequencyButton: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 20,
    borderWidth: 1,
    alignItems: 'center',
    minWidth: 80,
  },
  frequencyButtonText: {
    fontSize: 13,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },
  emergencyButton: {
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 8,
    alignItems: 'center',
    marginTop: 16,
    marginBottom: 12,
    borderWidth: 2,
  },
  emergencyButtonText: {
    fontSize: 16,
    fontFamily: 'Chivo',
    fontWeight: '900',
  },
  emergencyNote: {
    fontSize: 11,
    fontFamily: 'Archivo',
    fontStyle: 'italic',
    lineHeight: 14,
  },
  // Persistent Widgets styles
  widgetsList: {
    gap: 24,
  },
  widgetCardInfo: {
    flex: 1,
    gap: 8,
  },
  widgetCardMeta: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 8,
  },
  widgetTypeText: {
    fontSize: 12,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },
  widgetSettingsSection: {
    paddingBottom: 24,
    borderBottomWidth: 1,
    marginBottom: 24,
  },
  widgetPreviewSection: {
    padding: 24,
  },
  previewSectionTitle: {
    fontSize: 18,
    fontFamily: 'Archivo',
    fontWeight: '700',
  },
  previewContent: {
    padding: 0,
  },
  scheduleDisplayText: {
    fontSize: 15,
    fontFamily: 'Archivo',
    fontWeight: '600',
    marginTop: 4,
  },
  manageWidgetButton: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  manageWidgetButtonText: {
    fontSize: 15,
    fontFamily: 'Archivo',
    fontWeight: '700',
  },
  
  // ========== APP-STYLE ADMIN UI ==========
  adminStatsRow: {
    flexDirection: 'row',
    gap: 16,
    marginBottom: 32,
    flexWrap: 'wrap',
  },
  adminStatCard: {
    backgroundColor: '#FF4747',
    borderRadius: 20,
    padding: 20,
    minWidth: 140,
    flex: 1,
    ...Platform.select({
      web: {
        boxShadow: '4px 6px 0px rgba(0,0,0,1)',
      },
    }),
  },
  adminStatNumber: {
    fontSize: 36,
    fontFamily: 'Chivo',
    fontWeight: '900',
    color: '#000',
    ...Platform.select({
      web: {
        textShadow: '2px 2px 0px rgba(255,255,255,0.3)',
      },
    }),
  },
  adminStatLabel: {
    fontSize: 14,
    fontFamily: 'Archivo',
    fontWeight: '700',
    color: '#000',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  adminEmptyState: {
    backgroundColor: '#FF4747',
    borderRadius: 20,
    padding: 48,
    alignItems: 'center',
    ...Platform.select({
      web: {
        boxShadow: '5px 7px 0px rgba(0,0,0,1)',
      },
    }),
  },
  adminEmptyIcon: {
    fontSize: 48,
    marginBottom: 12,
  },
  adminEmptyTitle: {
    fontSize: 24,
    fontFamily: 'Chivo',
    fontWeight: '900',
    color: '#000',
    ...Platform.select({
      web: {
        textShadow: '2px 2px 0px rgba(255,255,255,0.3)',
      },
    }),
  },
  adminEmptyDesc: {
    fontSize: 14,
    fontFamily: 'Archivo',
    color: 'rgba(0,0,0,0.7)',
    textAlign: 'center',
  },
  adminCourseList: {
    gap: 20,
  },
  adminCourseCard: {
    backgroundColor: '#FFF',
    borderRadius: 20,
    ...Platform.select({
      web: {
        boxShadow: '5px 7px 0px rgba(0,0,0,1)',
      },
    }),
  },
  adminCourseCardInner: {
    padding: 24,
  },
  adminCourseTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  adminStatusBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 20,
  },
  adminStatusText: {
    fontSize: 11,
    fontFamily: 'Archivo',
    fontWeight: '800',
    color: '#FFF',
    letterSpacing: 0.5,
  },
  adminCourseOrder: {
    fontSize: 14,
    fontFamily: 'Archivo',
    fontWeight: '700',
    color: 'rgba(0,0,0,0.3)',
  },
  adminCourseTitle: {
    fontSize: 24,
    fontFamily: 'Chivo',
    fontWeight: '900',
    color: '#000',
    marginBottom: 8,
  },
  adminCourseDesc: {
    fontSize: 14,
    fontFamily: 'Archivo',
    color: 'rgba(0,0,0,0.6)',
    marginBottom: 16,
    lineHeight: 20,
  },
  adminCourseStats: {
    flexDirection: 'row',
    gap: 24,
    marginBottom: 20,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: 'rgba(0,0,0,0.1)',
  },
  adminCourseStat: {
    alignItems: 'center',
  },
  adminCourseStatNum: {
    fontSize: 24,
    fontFamily: 'Chivo',
    fontWeight: '900',
    color: '#FF4747',
  },
  adminCourseStatLbl: {
    fontSize: 11,
    fontFamily: 'Archivo',
    fontWeight: '600',
    color: 'rgba(0,0,0,0.5)',
    textTransform: 'uppercase',
  },
  adminCourseActions: {
    flexDirection: 'row',
    gap: 12,
  },
  adminEditBtn: {
    flex: 1,
    backgroundColor: '#FF4747',
    borderRadius: 22,
    paddingVertical: 12,
    alignItems: 'center',
    ...Platform.select({
      web: {
        boxShadow: '3px 4px 0px rgba(0,0,0,1)',
        cursor: 'pointer',
        transition: 'all 0.15s ease',
      },
    }),
  },
  adminEditBtnText: {
    fontSize: 14,
    fontFamily: 'Archivo',
    fontWeight: '700',
    color: '#000',
  },
  adminDeleteBtnSmall: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: '#FFF',
    borderWidth: 2,
    borderColor: '#EA806B',
    alignItems: 'center',
    justifyContent: 'center',
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  adminDeleteBtnSmallText: {
    fontSize: 18,
  },
  
  // Structure Section (Chapters & Modules)
  adminStructureSection: {
    backgroundColor: '#FF4747',
    borderRadius: 20,
    padding: 24,
    ...Platform.select({
      web: {
        boxShadow: '5px 7px 0px rgba(0,0,0,1)',
      },
    }),
  },
  adminStructureHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 24,
    flexWrap: 'wrap',
    gap: 16,
  },
  adminStructureTitle: {
    fontSize: 24,
    fontFamily: 'Chivo',
    fontWeight: '900',
    color: '#000',
    ...Platform.select({
      web: {
        textShadow: '2px 2px 0px rgba(255,255,255,0.3)',
      },
    }),
  },
  adminStructureSubtitle: {
    fontSize: 14,
    fontFamily: 'Archivo',
    color: 'rgba(0,0,0,0.6)',
    marginTop: 4,
  },
  adminStructureActions: {
    flexDirection: 'row',
    gap: 12,
  },
  adminToggleAllBtn: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 22,
    backgroundColor: 'rgba(0,0,0,0.1)',
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  adminToggleAllBtnText: {
    fontSize: 13,
    fontFamily: 'Archivo',
    fontWeight: '600',
    color: '#000',
  },
  adminAddChapterBtn: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 22,
    backgroundColor: '#000',
    ...Platform.select({
      web: {
        boxShadow: '3px 4px 0px rgba(255,255,255,0.3)',
        cursor: 'pointer',
      },
    }),
  },
  adminAddChapterBtnText: {
    fontSize: 13,
    fontFamily: 'Archivo',
    fontWeight: '700',
    color: '#FFF',
  },
  adminEmptyChapters: {
    backgroundColor: 'rgba(255,255,255,0.95)',
    borderRadius: 16,
    padding: 32,
    alignItems: 'center',
    ...Platform.select({
      web: {
        boxShadow: '3px 4px 0px rgba(0,0,0,0.5)',
      },
    }),
  },
  adminEmptyChaptersIcon: {
    fontSize: 40,
    marginBottom: 8,
  },
  adminEmptyChaptersTitle: {
    fontSize: 18,
    fontFamily: 'Chivo',
    fontWeight: '900',
    color: '#000',
  },
  adminEmptyChaptersDesc: {
    fontSize: 13,
    fontFamily: 'Archivo',
    color: 'rgba(0,0,0,0.5)',
    textAlign: 'center',
    marginBottom: 16,
  },
  adminAddFirstBtn: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 22,
    backgroundColor: '#FF4747',
    ...Platform.select({
      web: {
        boxShadow: '2px 3px 0px rgba(0,0,0,1)',
        cursor: 'pointer',
      },
    }),
  },
  adminAddFirstBtnText: {
    fontSize: 13,
    fontFamily: 'Archivo',
    fontWeight: '700',
    color: '#000',
  },
  
  // Chapter Cards
  adminChapterCard: {
    backgroundColor: 'rgba(255,255,255,0.95)',
    borderRadius: 16,
    marginBottom: 16,
    overflow: 'hidden',
    ...Platform.select({
      web: {
        boxShadow: '3px 4px 0px rgba(0,0,0,0.5)',
      },
    }),
  },
  adminChapterHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    gap: 12,
    backgroundColor: 'rgba(0,0,0,0.02)',
  },
  adminReorderBtns: {
    flexDirection: 'column',
    gap: 2,
  },
  adminReorderBtn: {
    width: 24,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FF4747',
    borderRadius: 4,
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  adminReorderBtnDisabled: {
    opacity: 0.3,
    ...Platform.select({
      web: {
        cursor: 'not-allowed',
      },
    }),
  },
  adminReorderBtnText: {
    fontSize: 10,
    color: '#000',
    fontWeight: '900',
  },
  adminChapterNum: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#FF4747',
    alignItems: 'center',
    justifyContent: 'center',
    ...Platform.select({
      web: {
        boxShadow: '2px 2px 0px rgba(0,0,0,0.5)',
      },
    }),
  },
  adminChapterNumText: {
    fontSize: 16,
    fontFamily: 'Chivo',
    fontWeight: '900',
    color: '#000',
  },
  adminChapterInfo: {
    flex: 1,
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  adminChapterTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  adminChapterTitle: {
    fontSize: 16,
    fontFamily: 'Chivo',
    fontWeight: '900',
    color: '#000',
    flex: 1,
  },
  adminChapterStatusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 10,
  },
  adminChapterStatusText: {
    fontSize: 10,
    fontFamily: 'Archivo',
    fontWeight: '800',
    color: '#FFF',
  },
  adminChapterMeta: {
    fontSize: 12,
    fontFamily: 'Archivo',
    color: 'rgba(0,0,0,0.5)',
    marginTop: 2,
  },
  adminExpandBtn: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#FF4747',
    alignItems: 'center',
    justifyContent: 'center',
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  adminExpandBtnText: {
    fontSize: 20,
    fontWeight: '900',
    color: '#000',
  },
  adminChapterContent: {
    padding: 20,
    borderTopWidth: 2,
    borderTopColor: 'rgba(0,0,0,0.1)',
  },
  adminChapterFields: {
    marginBottom: 20,
  },
  adminFieldRow: {
    flexDirection: 'row',
    gap: 16,
    marginBottom: 16,
  },
  adminFieldGroup: {
    flex: 1,
  },
  adminFieldGroupSmall: {
    width: 180,
  },
  adminFieldLabel: {
    fontSize: 12,
    fontFamily: 'Archivo',
    fontWeight: '700',
    color: 'rgba(0,0,0,0.5)',
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  adminFieldInput: {
    borderWidth: 2,
    borderColor: 'rgba(0,0,0,0.15)',
    borderRadius: 12,
    padding: 12,
    fontSize: 14,
    fontFamily: 'Archivo',
    backgroundColor: '#FFF',
    color: '#000',
    ...Platform.select({
      web: {
        outlineStyle: 'none',
      },
    }),
  },
  adminFieldTextarea: {
    borderWidth: 2,
    borderColor: 'rgba(0,0,0,0.15)',
    borderRadius: 12,
    padding: 12,
    fontSize: 14,
    fontFamily: 'Archivo',
    backgroundColor: '#FFF',
    color: '#000',
    minHeight: 80,
    textAlignVertical: 'top',
    ...Platform.select({
      web: {
        outlineStyle: 'none',
      },
    }),
  },
  adminStatusBtns: {
    flexDirection: 'row',
    gap: 8,
  },
  adminStatusBtn: {
    flex: 1,
    paddingVertical: 10,
    borderRadius: 22,
    backgroundColor: 'rgba(0,0,0,0.05)',
    alignItems: 'center',
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  adminStatusBtnActive: {
    backgroundColor: '#0DC44A',
    ...Platform.select({
      web: {
        boxShadow: '2px 2px 0px rgba(0,0,0,0.3)',
      },
    }),
  },
  adminStatusBtnSoon: {
    backgroundColor: '#FF4747',
    ...Platform.select({
      web: {
        boxShadow: '2px 2px 0px rgba(0,0,0,0.3)',
      },
    }),
  },
  adminStatusBtnText: {
    fontSize: 13,
    fontFamily: 'Archivo',
    fontWeight: '700',
    color: 'rgba(0,0,0,0.4)',
  },
  adminStatusBtnTextActive: {
    color: '#FFF',
  },
  
  // Modules Section
  adminModulesSection: {
    backgroundColor: 'rgba(0,0,0,0.03)',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
  },
  adminModulesHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  adminModulesTitle: {
    fontSize: 14,
    fontFamily: 'Chivo',
    fontWeight: '900',
    color: '#000',
  },
  adminAddModuleBtn: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 22,
    backgroundColor: '#FF4747',
    ...Platform.select({
      web: {
        boxShadow: '2px 2px 0px rgba(0,0,0,0.3)',
        cursor: 'pointer',
      },
    }),
  },
  adminAddModuleBtnText: {
    fontSize: 12,
    fontFamily: 'Archivo',
    fontWeight: '700',
    color: '#000',
  },
  adminModuleCard: {
    backgroundColor: '#FFF',
    borderRadius: 12,
    marginBottom: 10,
    borderWidth: 2,
    borderColor: 'rgba(0,0,0,0.1)',
    overflow: 'hidden',
  },
  adminModuleCardExpanded: {
    borderColor: '#FF4747',
    ...Platform.select({
      web: {
        boxShadow: '2px 3px 0px rgba(255,71,71,0.3)',
      },
    }),
  },
  adminModuleHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    gap: 10,
  },
  adminModuleReorder: {
    flexDirection: 'column',
    gap: 2,
  },
  adminModuleReorderBtn: {
    width: 20,
    height: 16,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,71,71,0.8)',
    borderRadius: 3,
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  adminModuleReorderText: {
    fontSize: 8,
    color: '#000',
    fontWeight: '900',
  },
  adminModuleNum: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: 'rgba(255,71,71,0.15)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  adminModuleNumText: {
    fontSize: 13,
    fontFamily: 'Chivo',
    fontWeight: '900',
    color: '#FF4747',
  },
  adminModuleInfo: {
    flex: 1,
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  adminModuleTitle: {
    fontSize: 14,
    fontFamily: 'Archivo',
    fontWeight: '700',
    color: '#000',
  },
  adminModuleIcons: {
    flexDirection: 'row',
    gap: 6,
    marginTop: 2,
  },
  adminModuleIcon: {
    fontSize: 12,
  },
  adminModuleEmpty: {
    fontSize: 11,
    fontFamily: 'Archivo',
    color: 'rgba(0,0,0,0.3)',
    fontStyle: 'italic',
  },
  adminModuleExpandBtn: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: '#FF4747',
    alignItems: 'center',
    justifyContent: 'center',
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  adminModuleExpandText: {
    fontSize: 18,
    fontWeight: '900',
    color: '#000',
  },
  adminModuleContent: {
    padding: 16,
    borderTopWidth: 2,
    borderTopColor: 'rgba(0,0,0,0.05)',
    backgroundColor: 'rgba(0,0,0,0.02)',
  },
  adminModuleField: {
    marginBottom: 16,
  },
  adminModuleFieldLabel: {
    fontSize: 11,
    fontFamily: 'Archivo',
    fontWeight: '700',
    color: 'rgba(0,0,0,0.5)',
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  adminModuleFieldInput: {
    borderWidth: 2,
    borderColor: 'rgba(0,0,0,0.15)',
    borderRadius: 10,
    padding: 10,
    fontSize: 14,
    fontFamily: 'Archivo',
    backgroundColor: '#FFF',
    color: '#000',
    ...Platform.select({
      web: {
        outlineStyle: 'none',
      },
    }),
  },
  adminModuleFieldTextarea: {
    borderWidth: 2,
    borderColor: 'rgba(0,0,0,0.15)',
    borderRadius: 10,
    padding: 12,
    fontSize: 14,
    fontFamily: 'Archivo',
    backgroundColor: '#FFF',
    color: '#000',
    minHeight: 120,
    textAlignVertical: 'top',
    ...Platform.select({
      web: {
        outlineStyle: 'none',
      },
    }),
  },
  adminModulePreview: {
    marginTop: 10,
    padding: 12,
    backgroundColor: 'rgba(0,0,0,0.05)',
    borderRadius: 8,
  },
  adminModulePreviewLabel: {
    fontSize: 10,
    fontFamily: 'Archivo',
    fontWeight: '700',
    color: 'rgba(0,0,0,0.4)',
    marginBottom: 4,
    textTransform: 'uppercase',
  },
  adminModulePreviewText: {
    fontSize: 13,
    fontFamily: 'Archivo',
    color: 'rgba(0,0,0,0.7)',
    lineHeight: 18,
  },
  adminModuleVideoRow: {
    flexDirection: 'row',
    gap: 10,
    alignItems: 'center',
  },
  adminModuleVideoInput: {
    flex: 1,
    borderWidth: 2,
    borderColor: 'rgba(0,0,0,0.15)',
    borderRadius: 10,
    padding: 10,
    fontSize: 13,
    fontFamily: 'Archivo',
    backgroundColor: '#FFF',
    color: '#000',
    ...Platform.select({
      web: {
        outlineStyle: 'none',
      },
    }),
  },
  adminUploadProgress: {
    marginTop: 8,
    height: 20,
    backgroundColor: 'rgba(0,0,0,0.1)',
    borderRadius: 10,
    overflow: 'hidden',
    flexDirection: 'row',
    alignItems: 'center',
  },
  adminUploadBar: {
    height: '100%',
    backgroundColor: '#FF4747',
  },
  adminUploadText: {
    position: 'absolute',
    right: 8,
    fontSize: 11,
    fontFamily: 'Archivo',
    fontWeight: '700',
    color: '#000',
  },
  adminModuleSettingsRow: {
    flexDirection: 'row',
    gap: 10,
    flexWrap: 'wrap',
    marginBottom: 16,
  },
  adminModuleToggle: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 22,
    backgroundColor: 'rgba(0,0,0,0.05)',
    borderWidth: 2,
    borderColor: 'rgba(0,0,0,0.1)',
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  adminModuleToggleActive: {
    backgroundColor: '#0DC44A',
    borderColor: '#0DC44A',
    ...Platform.select({
      web: {
        boxShadow: '2px 2px 0px rgba(0,0,0,0.3)',
      },
    }),
  },
  adminModuleToggleText: {
    fontSize: 12,
    fontFamily: 'Archivo',
    fontWeight: '700',
    color: 'rgba(0,0,0,0.5)',
  },
  adminModuleToggleTextActive: {
    color: '#FFF',
  },
  adminModuleDeleteBtn: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 22,
    backgroundColor: 'rgba(234,128,107,0.1)',
    borderWidth: 2,
    borderColor: '#EA806B',
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  adminModuleDeleteText: {
    fontSize: 12,
    fontFamily: 'Archivo',
    fontWeight: '700',
    color: '#EA806B',
  },
  adminModuleWidgets: {
    backgroundColor: 'rgba(0,0,0,0.03)',
    borderRadius: 10,
    padding: 12,
  },
  adminModuleWidgetsHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  adminModuleWidgetsTitle: {
    fontSize: 12,
    fontFamily: 'Archivo',
    fontWeight: '700',
    color: 'rgba(0,0,0,0.6)',
  },
  adminAddWidgetBtn: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    backgroundColor: '#FF4747',
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  adminAddWidgetText: {
    fontSize: 11,
    fontFamily: 'Archivo',
    fontWeight: '700',
    color: '#000',
  },
  adminWidgetItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 6,
  },
  adminWidgetSelector: {
    flex: 1,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: '#FFF',
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.1)',
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  adminWidgetSelectorText: {
    fontSize: 12,
    fontFamily: 'Archivo',
    color: '#000',
  },
  adminWidgetRemove: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: 'rgba(234,128,107,0.2)',
    alignItems: 'center',
    justifyContent: 'center',
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  adminWidgetRemoveText: {
    fontSize: 14,
    fontWeight: '900',
    color: '#EA806B',
  },
  adminEmptyModules: {
    padding: 20,
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.03)',
    borderRadius: 8,
  },
  adminEmptyModulesText: {
    fontSize: 13,
    fontFamily: 'Archivo',
    color: 'rgba(0,0,0,0.4)',
    fontStyle: 'italic',
  },
  adminDeleteChapterBtn: {
    paddingVertical: 12,
    borderRadius: 22,
    backgroundColor: 'rgba(234,128,107,0.1)',
    borderWidth: 2,
    borderColor: '#EA806B',
    alignItems: 'center',
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  adminDeleteChapterText: {
    fontSize: 14,
    fontFamily: 'Archivo',
    fontWeight: '700',
    color: '#EA806B',
  },
  
  // Edit Section
  adminEditSection: {
    marginBottom: 24,
    paddingBottom: 24,
    borderBottomWidth: 2,
    borderBottomColor: 'rgba(0,0,0,0.1)',
  },
  adminEditSectionTitle: {
    fontSize: 14,
    fontFamily: 'Archivo',
    fontWeight: '800',
    color: '#FF4747',
    marginBottom: 16,
    letterSpacing: 1,
  },
  
  // Preview Section
  adminPreviewSection: {
    backgroundColor: 'rgba(255,71,71,0.05)',
    borderRadius: 16,
    padding: 20,
    borderWidth: 2,
    borderColor: 'rgba(255,71,71,0.2)',
  },
  adminPreviewSectionTitle: {
    fontSize: 14,
    fontFamily: 'Archivo',
    fontWeight: '800',
    color: '#FF4747',
    marginBottom: 4,
    letterSpacing: 1,
  },
  adminPreviewSubtitle: {
    fontSize: 12,
    fontFamily: 'Archivo',
    color: 'rgba(0,0,0,0.5)',
    marginBottom: 20,
  },
  adminPreviewLabel: {
    fontSize: 12,
    fontFamily: 'Archivo',
    fontWeight: '700',
    color: 'rgba(0,0,0,0.6)',
    marginBottom: 10,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  adminVideoPreviewContainer: {
    marginBottom: 20,
  },
  adminThumbnailPreview: {
    marginBottom: 20,
  },
  adminContentPreview: {
    marginBottom: 20,
  },
  adminContentPreviewBox: {
    backgroundColor: '#FFF',
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.1)',
    maxHeight: 400,
    overflow: 'scroll',
  },
  adminContentPreviewText: {
    fontSize: 14,
    fontFamily: 'Archivo',
    color: '#333',
    lineHeight: 22,
  },
  adminWidgetPreviewSection: {
    marginTop: 10,
  },
  adminWidgetPreviewCard: {
    backgroundColor: '#FFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    borderWidth: 2,
    borderColor: 'rgba(0,0,0,0.08)',
    ...Platform.select({
      web: {
        boxShadow: '2px 3px 0px rgba(0,0,0,0.1)',
      },
    }),
  },
  adminWidgetPreviewHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  adminWidgetPreviewTitle: {
    fontSize: 16,
    fontFamily: 'Chivo',
    fontWeight: '900',
    color: '#000',
  },
  adminWidgetPreviewType: {
    fontSize: 11,
    fontFamily: 'Archivo',
    fontWeight: '700',
    color: '#FF4747',
    backgroundColor: 'rgba(255,71,71,0.1)',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 10,
    textTransform: 'uppercase',
  },
  adminWidgetPreviewDesc: {
    fontSize: 13,
    fontFamily: 'Archivo',
    color: 'rgba(0,0,0,0.6)',
    marginBottom: 10,
  },
  adminWidgetPreviewPlacement: {
    marginBottom: 12,
  },
  adminWidgetPreviewPlacementText: {
    fontSize: 11,
    fontFamily: 'Archivo',
    fontWeight: '600',
    color: 'rgba(0,0,0,0.4)',
  },
  adminWidgetPreviewRenderer: {
    backgroundColor: 'rgba(0,0,0,0.03)',
    borderRadius: 10,
    padding: 12,
    marginTop: 8,
  },
  adminEmptyPreview: {
    alignItems: 'center',
    padding: 32,
  },
  adminEmptyPreviewIcon: {
    fontSize: 40,
    marginBottom: 12,
  },
  adminEmptyPreviewText: {
    fontSize: 14,
    fontFamily: 'Archivo',
    color: 'rgba(0,0,0,0.4)',
    textAlign: 'center',
  },
  adminContentHint: {
    fontSize: 12,
    fontFamily: 'Archivo',
    color: 'rgba(0,0,0,0.4)',
    marginTop: 8,
    fontStyle: 'italic',
  },
  adminVideoPlayerContainer: {
    marginTop: 16,
    backgroundColor: 'rgba(0,0,0,0.05)',
    borderRadius: 16,
    padding: 12,
    borderWidth: 2,
    borderColor: 'rgba(0,0,0,0.1)',
  },
  adminVideoPlayerLabel: {
    fontSize: 12,
    fontFamily: 'Archivo',
    fontWeight: '700',
    color: 'rgba(0,0,0,0.6)',
    marginBottom: 10,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  
  // Formatting Bar
  adminFormattingBar: {
    backgroundColor: 'rgba(0,0,0,0.05)',
    borderRadius: 10,
    padding: 12,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.08)',
  },
  adminFormattingTitle: {
    fontSize: 11,
    fontFamily: 'Archivo',
    fontWeight: '700',
    color: 'rgba(0,0,0,0.5)',
    marginBottom: 8,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  adminFormattingButtons: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  adminFormatBtn: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    backgroundColor: '#FFF',
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.15)',
    ...Platform.select({
      web: {
        cursor: 'pointer',
        transition: 'all 0.15s ease',
      },
    }),
  },
  adminFormatBtnText: {
    fontSize: 12,
    fontFamily: 'Archivo',
    fontWeight: '600',
    color: '#333',
  },
  adminContentEditor: {
    minHeight: 400,
    padding: 20,
    fontSize: 16,
    fontFamily: 'Georgia',
    lineHeight: 28,
    borderWidth: 2,
    borderColor: 'rgba(0,0,0,0.15)',
    borderRadius: 12,
    backgroundColor: '#FFFEF8',
    color: '#333',
    textAlignVertical: 'top',
  },
  adminContentHelpBox: {
    marginTop: 12,
    padding: 16,
    backgroundColor: 'rgba(255,71,71,0.05)',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'rgba(255,71,71,0.15)',
  },
  adminContentHelpTitle: {
    fontSize: 13,
    fontFamily: 'Archivo',
    fontWeight: '700',
    color: '#FF4747',
    marginBottom: 8,
  },
  adminContentHelpText: {
    fontSize: 12,
    fontFamily: 'Archivo',
    color: 'rgba(0,0,0,0.6)',
    lineHeight: 20,
  },
});

export default AdminScreen;
